import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from "react";

import { formatMetres, formatMm } from "@/lib/format";
import { collides, labelBox, type LabelBox } from "@/lib/labelBoxes";
import {
  isFase,
  isFaseCorner,
  shortenedWallLabel,
  type DemoOutline,
  type LengthSource,
} from "@/lib/outline";
import {
  FEATURE_DEFAULTS,
  GRID_MM,
  HINGE_WORDS,
  SWING_TYPES,
  hingeOf,
  bandWidthMm,
  carrierAt,
  centredFeatureOffsetMm,
  cutoutAlongMm,
  cutoutExtentMm,
  effectiveWidthMm,
  featureOverlaps,
  freeCutoutAt,
  freeStretches,
  settleFeature,
  nextCutoutId,
  nextFeatureId,
  nextLabel,
  outwardSign,
  snapCutout,
  snapOffset,
  wallAcceptsGrating,
  wallLengthMm,
  type Cutout,
  type FacadeFeature,
  type FeatureType,
  type FreeStretch,
  type GratingSpec,
  type GratingType,
  type CornerMark,
  type Placement,
  type PlacementKind,
  type SnapTarget,
} from "@/lib/placement";

/**
 * How one end of one element is cut, in the answer's own words.
 *
 * Two mitre values: same 45 degrees, opposite lean. At an outer corner the
 * band's outer edge runs *past* the wall corner (it closes the square that
 * would otherwise stay open); at an inner one it stops short of it (the
 * square is claimed twice and gets divided).
 *
 * `schraeg` is an end at a chamfer's corner (design 2026-10-18): cut oblique
 * on the bisector of the two walls, whatever angle the chamfer has, with no
 * growth — the two bands simply meet. Which way it leans is the corner's own
 * geometry, read off the drawn walls (`bevelAt`), not a second word.
 */
export type CutWord = "gerade" | "gehrung_aussen" | "gehrung_innen" | "schraeg";

/**
 * Where the one right-click menu on a wall was asked for, and about what.
 *
 * Grew out of a menu about one corner (E-47, 27.08.2026) into a menu about
 * the whole wall (task 14, 30.08.2026): a second menu confined to the *bare*
 * wall line would be unreachable the moment the wall is fully covered, which
 * is the normal case, so there is one menu, fired from wherever the click
 * lands, and its content depends on where that was.
 */
export interface CornerMenuRequest {
  /** The element under the click, if any — a bare stretch of wall has none. */
  placementId: string | null;
  /** The hole under the click, or `null` — „Ausschnitt entfernen" acts on it. */
  cutoutId: string | null;
  /** The facade object under the click, or `null` — „Anschlag wechseln"
   * acts on it where it is a door (24.09.2026). */
  featureId: string | null;
  /**
   * "The wall whose end the corner is" — the key `corners[]` and
   * `cornerFacts` use, unchanged since before this menu covered the rest of
   * the wall too. Only meaningful together with `nearCorner`.
   */
  wallIndex: number;
  /** Whether the nearer end is this wall's start. Only meaningful together
   * with `nearCorner`. */
  atStart: boolean;
  /** The wall physically under the pointer — a bare stretch, an
   * interruption or a grating band all report their own wall here. Where
   * „Objekt einsetzen" and „Wand neu aufteilen" act. */
  objectWallIndex: number;
  /** The click position along `objectWallIndex`, in millimetres, clamped to
   * the wall — the offset a new object is centred on. */
  offsetMm: number;
  /** Whether the click landed within `CORNER_CATCH_MM` of either end of
   * `objectWallIndex` — the gate for the corner-treatment section of the
   * menu (§ design, task 14). */
  nearCorner: boolean;
  /**
   * Whether the **click itself** landed within the catching radius of the
   * named wall end — geometry alone, with none of the reach a clicked band
   * lends {@link nearCorner}.
   *
   * The two differ on purpose and the difference is the point: a right-click
   * anywhere on a band that stands on a wall offers the corner treatments
   * (task 14, review round 1, important 2), and that is a deliberate,
   * separately argued reach. But the menu's *heading* is a statement of fact,
   * and „Wand 1 · W1-03 · Ecke nach Wand 1" eight metres from any corner is
   * simply untrue (round 2 of the walk, 30.08.2026, finding 5). The heading
   * reads this; the entries go on reading `nearCorner`.
   */
  cornerInReach: boolean;
  x: number;
  y: number;
  /**
   * This board's own measured width in CSS pixels, at the moment the menu was
   * asked for (fix round 1, task 8, 2026-09-10).
   *
   * Not `BOARD_FALLBACK_WIDTH_PX` — the board is the only thing that knows
   * its real, measured width (`boardWidth` state, filled by its own
   * `ResizeObserver`), and „Diesen Rost groß" needs that figure at the exact
   * moment it needs it: when a person opens this menu on an element. Riding
   * along on the request that already carries `x`/`y` is one field more on a
   * channel that already exists, not a second one built for it.
   */
  boardWidth: number;
  /** The board's drawn height at the same moment — 520 in place, the
   * container's own height once maximized (A3, 23.09.2026). Same channel,
   * same reason as {@link boardWidth}. */
  boardHeight: number;
}

/** How big the corner handle is, in pixels. Large enough to hit on a laptop
 * trackpad without covering the corner it sits on. */
const MITER_HANDLE_R = 7;

/** How far off the corner the handle sits, in pixels. Clear of the shared
 * corner bracket, close enough to read as belonging to that corner. */
const MITER_HANDLE_OFFSET = 26;

/** A vector of length one, or the zero vector unchanged. */
function normalised(vector: Vector): Vector {
  const length = Math.hypot(vector.x, vector.y);
  return length === 0 ? vector : { x: vector.x / length, y: vector.y / length };
}
import {
  dimensionLine,
  gratingHatch,
  projectRun,
  railPair,
  scaleBarMm,
  type DimensionLine,
  type MmPoint,
  type ScreenPoint,
} from "@/lib/projection";
import { shortcutWords } from "@/lib/shortcuts";
import { FIT_VIEW, zoomBy, type Viewport } from "@/lib/viewport";

import { MaximizeButton } from "./MaximizeButton";
import styles from "./PlanningBoard.module.css";

/**
 * Which tool the toolbar is holding — `select` plus one per kind of placement,
 * minus `gap` plus `feature`.
 *
 * `gap` stays a valid {@link PlacementKind} — an old draft can still carry one
 * and the board still draws it — but nothing can create one any more (E-47
 * follow-up, 30.08.2026): a door, a sill or a downpipe is now a
 * {@link FacadeFeature}, a different kind of thing standing on the wall rather
 * than a stretch of it. The store declares the same union for its own state;
 * the two meet in `PlanningScreen`, where TypeScript checks them against each
 * other.
 *
 * `cutout` joined on 12.09.2026 (design 2026-09-10 §8): a tool that is up
 * like the others — every grip beneath it goes transparent — but lays no
 * placement. Which tool lays which is {@link PLACEMENT_OF_TOOL}'s answer.
 */
export type Tool = "select" | Exclude<PlacementKind, "gap"> | "feature" | "cutout";

/**
 * Which placement a tool lays — `null` for the tools that lay none.
 *
 * A `Record` and not a `switch`, so that adding a `Tool` without deciding
 * this fails to compile. `place(tool as PlacementKind, …)` was the cast that
 * let a `cutout` tool lay a placement of kind „cutout" (design §8): a cast is
 * a claim TypeScript does not check.
 */
const PLACEMENT_OF_TOOL: Record<Tool, PlacementKind | null> = {
  select: null,
  feature: null,
  cutout: null,
  grating: "grating",
  bracket: "bracket",
  area: "area",
};

/**
 * A console the answer worked out, at the point it sits on.
 *
 * In millimetres, because everything that crosses this boundary is: the board
 * projects, it does not compute where a console goes. **Which** consoles there
 * are is a domain statement and is made in `planning` (§4.1) — the joint that
 * is shared at a corner, the joint a run walks over, the plain one at every
 * element seam, and the bracket under the middle of any element over
 * 1.000 mm.
 *
 * There is deliberately no `extra` kind. An additional console is a `bracket`
 * in the document, put there by a person, and it is drawn from there — one
 * square per bracket, and never a second one for the same console because the
 * answer echoed it back.
 */
/**
 * How one element's two ends are cut, and what it is cut *to*.
 *
 * Named rather than written inline where `cuts` is declared, because the
 * legend reads the same map to say whether an empty stretch is on the board
 * (30.08.2026) — one shape, one place, no chance of the two drifting.
 */
export interface BandCut {
  start: CutWord;
  end: CutWord;
  growsStartPx?: number;
  growsEndPx?: number;
  /** What the element is cut to. Printed beside the wall stretch where the
   * two differ — the one figure that says which grating covers a corner. */
  blankMm?: number;
}

export interface ConsoleMark {
  xMm: number;
  yMm: number;
  /**
   * `"middle"` is the only kind that shares nothing: it carries one element
   * at its own middle, not two elements at their shared seam the way
   * `joint`, `corner` and `bridged` do.
   */
  kind: "joint" | "corner" | "bridged" | "middle";
  /**
   * How wide the interruption is that a `bridged` joint reaches across, in mm.
   *
   * The arc used to span a fixed ±6 px, which happens to fit an `RR 120` at
   * A-10's scale and turns into a 12-pixel bump at the left edge of a 90-pixel
   * gap anywhere else. What it has to show is that *this* opening is walked
   * over, so it is drawn at the opening's own width (found in review,
   * 16.08.2026).
   */
  spanMm?: number;
  /**
   * This bracket's address — `DemoConsoleMark.key` verbatim, computed the
   * same way the backend does (`<placementId>:<spot>`) so a right-click here
   * and an override sent back later name the same bracket. `undefined`
   * where nothing computed one — `PlanningBoard.test.tsx`'s own literal
   * marks draw the four kinds without needing one, and the board asks
   * nothing further of a mark that carries none.
   */
  key?: string;
  /** What actually applies here — the chosen article where a person set one
   * by hand, the proposal otherwise. `DemoConsoleMark.articleNo` verbatim,
   * `undefined` on the same terms as `key`. */
  articleNo?: string | null;
  /**
   * What the catalogue proposes for this width now, regardless of any
   * override. Compared against `articleNo` to draw the mark as a contour
   * rather than a solid fill and to say when a hand-set article has drifted
   * from today's proposal (task 11) — `undefined` on the same terms as
   * `key`.
   */
  proposedArticleNo?: string | null;
}

/**
 * Somebody asked what article one bracket should build — a right-click on
 * its mark, or Shift+F10 once it holds focus, the same two triggers the
 * wall's own menu answers (`CornerMenuRequest`).
 */
export interface ConsoleMenuRequest {
  /** The mark's own address, or `null` where it carries none — the screen
   * decides what a menu without a key can still offer. */
  key: string | null;
  /** Position in `consoles` — how the screen finds this exact mark again
   * without a second lookup. */
  index: number;
  x: number;
  y: number;
}

/**
 * Why a wall did not take the element that was offered to it.
 *
 * A key, not a sentence: the wording lives on the screen (§5.5.4), where it
 * can be read next to the other sentences and where the figures it needs —
 * how much is free, how small the smallest element is — are already to hand.
 * A board that writes its own status line writes a second one.
 */
export type WallRefusal = "wall_without_measure" | "wall_has_no_room";

/**
 * Why a gesture about a facade object did not happen.
 *
 * A key, not a sentence, for the reason {@link WallRefusal} gives: the
 * wording lives on the screen, where the other sentences are.
 *
 * `feature_overlaps` is the rule `_set_feature` refuses outright on the
 * server (`document.py`, design §2.2). The planning path is stateless and
 * never calls it, so until 30.08.2026 the screen could build a document the
 * endpoint would reject — two sills fully inside one another, with no
 * message of any kind (walk finding 2).
 *
 * `fase_wall`: an object or a hole on a chamfer's slant — the server refuses
 * both (design 2026-10-18 §6.3), so the board does before it gets that far.
 */
export type FeatureRefusal = "feature_overlaps" | "fase_wall";

/** The board's own height in CSS pixels; the viewBox is written in the board's
 * measured pixels, so one viewBox unit *is* one pixel (`OutlineDraft`, same
 * reasoning: it makes the pointer arithmetic exact instead of a ratio).
 * Exported for `PlanningScreen.zoomToPlacement` (design 2026-09-10, „Diesen
 * Rost groß"), which builds a view for this same board from outside it. */
export const BOARD_HEIGHT_PX = 520;
/** The width this board renders at before its own `ResizeObserver` has
 * measured anything — jsdom never fires one, so every test runs at this
 * figure too (see the effect below). Nothing outside this component reads
 * it: `zoomToPlacement` (`PlanningScreen`, design 2026-09-10, „Diesen Rost
 * groß") takes the board's real, measured width off `CornerMenuRequest.
 * boardWidth` instead — a channel opened in task 8's own fix round 1 for
 * exactly this figure, once a fallback constant there turned out to
 * quietly under-fill a real screen (review finding 4, schlussreview
 * 10.09.2026: this docstring used to argue against opening that channel,
 * which by then had existed for a whole task). */
export const BOARD_FALLBACK_WIDTH_PX = 560;
/**
 * Room around the shape for the bands, the readings and the scale bar.
 *
 * Wider than the drawing editor's 44, and the difference is not taste: the
 * furthest thing from a wall here is the wall summary at {@link SUMMARY_OFFSET}
 * plus its own line height, and at 44 the summary was clipped by the top edge
 * of the board — half a row of figures, cut off (measured in the browser,
 * 16.08.2026). Anything drawn outward has to fit inside this number.
 *
 * Exported for the same reason as {@link BOARD_HEIGHT_PX}: „Diesen Rost groß"
 * has to know what a plain fit of the *whole* house would have scaled to, to
 * clamp against — the same lower bound this component computes for itself.
 */
export const VIEW_PADDING = 68;

/**
 * The board as a printed sheet (`/vorgang/:id/unterlagen`, WP-E2-4) — the same
 * drawing, the same geometry, not a second one (design 2026-10-12 §2).
 *
 * A sheet is not a screen: it has a fixed size (the paper decides, not a
 * `ResizeObserver`), nobody points at it, and it has to say on its own what a
 * screen says on hover. So the sheet mode changes **what is written**, never
 * where anything lies:
 *
 * - `schema` — the shape, its measures and its division: one summary per
 *   wall („9×1.000 · 500"), no element figures, no consoles, no objects. The
 *   page a customer compares with their own sketch.
 * - `plan` — everything the board carries, and every element with its number
 *   in the band and its length beside it, however many stand on a wall (the
 *   screen's six-element summary is a screen rule — on paper, nobody can zoom).
 *
 * Interactive furniture (zoom bar, overview, untreated corner handles) is
 * left out; the drawing itself is `inert` — a sheet is read, not operated.
 */
export interface BoardSheet {
  /** The drawing's size in board units — one unit prints as one CSS pixel at
   * the sheet's natural size, so the board's 10–11 px figures stay 7–8 pt. */
  width: number;
  height: number;
  detail: "schema" | "plan";
  /**
   * Mark every wall whose measure a person set rather than read — the schema
   * sheet of a Vorgang that has a reading (design §3: „Gesetzte Werte und
   * gelesene unterscheidbar, aber ruhig"). Off on a drawn Vorgang, where every
   * figure is set and a mark on all of them would say nothing.
   */
  markSetMeasures?: boolean;
}

/** Room around the shape on a sheet: up to three rows of element figures
 * stand outside the band, where the screen has one. */
const SHEET_PADDING = 76;
/** The element number inside its band, and the figures outside it, in board
 * units — sized for print, not for a pointer. */
const SHEET_NUMBER_FONT = 8;
const SHEET_READING_FONT = 8.5;
/** Roboto Mono's advance is 0,6 em — enough to tell whether a figure fits. */
const MONO_ADVANCE = 0.6;
/** One row of figures outside the band, and the gap to the band. */
const SHEET_ROW_PX = 11;
const SHEET_READING_GAP = 7;
/** The least room between two figures on one row, in board units — less, and
 * „1.000 W6-02" reads as one statement about one element. */
const SHEET_FIGURE_GAP = 10;
/**
 * How far a figure may step out past its own wall's packing to clear a
 * **neighbouring** wall's figures, in rows (P-7, Generalprobe 15.10.2026).
 * At a Rücksprung under 2 m the figures of two walls meet in one corner;
 * one or two rows clear them, and the cap keeps a figure inside the sheet's
 * padding where nothing clears it.
 */
const SHEET_MAX_EXTRA_ROWS = 4;
/** The least room between two figures of different walls, in board units. */
const SHEET_CROSS_GAP = 2;
/** Half the width of the invisible band a wall is grabbed by. */
const GRAB_HALF_WIDTH = 7;
/**
 * How near the pointer has to be for something to catch it, in screen pixels.
 *
 * Pixels and not millimetres: snapping is a pointing-device aid, and at the
 * scales a house produces on a 520 px board a radius in millimetres is
 * unfeelable (§5.3). The cap that keeps this from eating a real interruption
 * is applied where it is converted — see {@link PlanningBoard}.
 *
 * Reused as the wall menu's own corner-catch radius (`wallMenuRequestAt`,
 * task 14): a fixed millimetre figure was tried there first and was the
 * mistake this constant exists to avoid — on A-10's 17,4 m facade a fixed
 * 40 mm catch worked out to about one pixel at either end, unreachable by
 * mouse on the one real case the project has at that scale (review round 1).
 */
const CATCH_RADIUS_PX = 12;
/** One arrow key. The same step `OutlineReview` and `OutlineDraft` move by,
 * and the same grid `placement.ts` rounds to — taken from there so a dragged
 * element and a nudged one cannot end up on different numbers. */
const STEP_MM = GRID_MM;
/**
 * One arrow key with Shift.
 *
 * Not a comfort: a 10 m wall in 10 mm steps is a thousand keystrokes, which is
 * not a keyboard path but a punishment (§5.3).
 */
const COARSE_STEP_MM = GRID_MM * 10;
/** One press of `+`/`-` (with or without Strg/⌘) or one zoom button, as a
 * factor on the scale. The wheel no longer zooms (Entscheid Denis,
 * 23.09.2026): it scrolls the page, as everywhere else. */
const ZOOM_STEP = 1.25;
/** How far one arrow-key pan moves the view, as a share of the board. */
const PAN_STEP = 0.2;
/** Spacing of the cross bars inside a band, in pixels. */
const HATCH_SPACING_PX = 5;
/** How far a reading sits off its wall, outwards. */
const READING_OFFSET = 20;
/** How far a wall's own summary sits off it — clear of the element readings. */
const SUMMARY_OFFSET = 48;
/**
 * How far the wall's own measure sits off the wall — **inwards**.
 *
 * Its own figure and not `READING_OFFSET`, because it is measured into the
 * other half-plane: outside the wall carries the band, its readings and the
 * wall summary, and all of those are pushed away by `bandHalfWidth` plus a
 * reading offset.
 *
 * Inside is not empty either, and that is what sets this number: an object's
 * name sits there at {@link FEATURE_LABEL_OFFSET} (13 px, ~10 px tall). A
 * sill in the middle of a wall would otherwise print its name straight
 * through that wall's measure. 30 px clears the name's line and leaves the
 * two readable as two.
 */
const WALL_DIMENSION_OFFSET = 30;
/** How long the little ticks at either end of that measure are, in pixels. */
const WALL_DIMENSION_TICK = 4;
/** From this many elements on, one reading per wall says more than one per
 * element: at six 20 px bands the figures no longer fit (§5.5.1). */
const SUMMARY_FROM_ELEMENTS = 6;
/** A band narrower than this cannot carry its own figure legibly. */
const MIN_PX_FOR_READING = 44;
/** An element's reading and its blank, in board units (`.reading`). */
const READING_FONT = 11;
/** A finding's „!" (`.issueMark`). */
const ISSUE_FONT = 13;
/**
 * The least room between two figures on one lane of the screen, in board
 * units: two blanks of the mono face. „1.000 mm" holds one, so the room
 * between two figures can never be taken for the room inside one — at ten
 * pixels „1.000 mm 1.000 mm 1.000 mm" read as one line (UX-Gutachten 2, B-2).
 */
const READING_GAP = 2 * READING_FONT * MONO_ADVANCE;
/** One lane further out: the figure's height and 4 px (UX-Gutachten 2, B-2). */
const READING_LANE = READING_FONT + 4;
/** The lanes a wall's figures may take outside its bands — a fourth would
 * leave the board's padding. */
const READING_MAX_LANES = 3;
/**
 * The band's **working width**, in pixels: `clamp(true width, 14, 32)` while
 * the board is fitted, `max(true width, 14)` once it is zoomed in.
 *
 * The floor replaces the old 6 px one, and it is the user's own words —
 * „deutlich breiter, damit man dort auch besser Zeichnungen vornehmen kann":
 * at 17,4 m of facade a 300-mm band is 7,7 px true scale, which cannot be
 * worked on. The ceiling is new outright; without one, a band below ~2,4 m of
 * span overflowed the padding and ran out of the board **at the fitted
 * scale** — the only scale at which that overflow was ever measured. **The
 * length stays exactly true to scale** — that is the axis the domain
 * statement lives on; the width is the grating's cross dimension and, while
 * fitted, decides nothing in planning. At the fitted scale the special width
 * is carried by the ∗ mark alone, which is why that mark exists.
 *
 * `zoomed` is the whole of D-7 (design 2026-09-10). Once somebody has
 * magnified the drawing past its fitting scale, the width is exactly what
 * they came for — a hole has to be placed across it to the centimetre, and a
 * band frozen at 32 px would leave 9 mm to the pixel however far they
 * zoomed. The ceiling falls away there and the band is drawn true; the floor
 * stays in both cases, because 14 px is the least anything can be worked on
 * regardless of scale.
 */
const BAND_MIN_PX = 14;
const BAND_MAX_PX = 32;

function bandHalfWidth(widthPx: number, zoomed: boolean): number {
  const lower = Math.max(widthPx, BAND_MIN_PX);
  return (zoomed ? lower : Math.min(lower, BAND_MAX_PX)) / 2;
}

/**
 * Where the band lies relative to the wall line, in pixels — and how many
 * pixels one millimetre across it is.
 *
 * One function for the band and for the hole in it. `PlacedThing` used to
 * hold this arithmetic inline; `CutoutMark` needs the same three numbers to
 * put a hole where the band actually is, and two copies of „where is the
 * inner rail" would be the first thing to drift apart.
 */
interface BandGeometry {
  /** Distance from the wall line to the band's near edge (the inner rail). */
  innerPx: number;
  /** The drawn width of the band. */
  drawnWidth: number;
  /** The band's real width, so `drawnWidth / widthMm` is its own px per mm. */
  widthMm: number;
}

/**
 * The drawn half widths of one element's band and of its type's band.
 *
 * Ordinarily each is clamped on its own (`bandHalfWidth`), and the band hangs
 * off the type's outer line — a width zone moves the inner edge. Widening
 * outward (`Placement.widensOutward`, 31.10.2026) is the other way round: the
 * inner edge stays on the wall, so the type's half width *is* the band's, and
 * the band is the type's drawn width plus the departure **true to scale**.
 * Clamped as a whole, a 475 mm band on a recessed wall would be drawn exactly
 * as wide as its 300 mm neighbours at the fitted scale and stop short of
 * their outer line by the very 175 mm it was widened for — while the wall it
 * stands on is drawn true. Added at scale, it closes that recess at any zoom.
 */
function bandHalves(
  widthPx: number,
  typeWidthPx: number,
  zoomed: boolean,
  widensOutward: boolean,
): { halfWidth: number; typeHalfWidth: number; growthPx: number } {
  const typeHalfWidth = bandHalfWidth(typeWidthPx, zoomed);
  if (!widensOutward) return { halfWidth: bandHalfWidth(widthPx, zoomed), typeHalfWidth, growthPx: 0 };
  const halfWidth = Math.max(typeHalfWidth + (widthPx - typeWidthPx) / 2, BAND_MIN_PX / 2);
  // How far its outer edge lies past the type band's. Its figures and its
  // wall's summary move out by this much: they stand off a fixed distance
  // from the wall, and the fitted band they were spaced for is at most
  // `BAND_MAX_PX` wide — a widened one would carry them on its outer rail
  // where its neighbours' stand clear (review 31.10.2026).
  return { halfWidth, typeHalfWidth: halfWidth, growthPx: 2 * (halfWidth - typeHalfWidth) };
}

function bandGeometry(
  widthMm: number,
  typeWidthMm: number,
  scale: number,
  zoomed: boolean,
  widensOutward = false,
): BandGeometry {
  const { halfWidth, typeHalfWidth } = bandHalves(widthMm * scale, typeWidthMm * scale, zoomed, widensOutward);
  return {
    innerPx: (typeHalfWidth - halfWidth) * 2,
    drawnWidth: halfWidth * 2,
    widthMm: Math.max(widthMm, 1),
  };
}

/**
 * The smallest half-extent a drilled hole is ever drawn at, in pixels.
 *
 * True to scale a 120 mm hole in a 300 mm band is a fifth of the band — and
 * the band itself is clamped to 14–32 px (§3.1), so at A-10's ≈0,032 px/mm
 * that fifth is under three pixels of a fourteen-pixel band. **A mark that
 * vanishes at real scale is the mistake §3.1 already documents**, so the hole
 * keeps a floor, exactly as the band does. Its *position* across the band
 * stays proportional to the real one, so moving the figure still visibly
 * moves the hole. Zoomed in (D-7) the band's own scale is the true one and
 * the floor is never reached.
 */
const CUTOUT_MIN_R = 3;

/**
 * The shortest a band is ever drawn, in pixels.
 *
 * A filling at an inner corner takes a grating width off the drawn band, and
 * the drawn width is clamped **up** to 14 px at facade scale (§3.1). On a
 * 20 m house a 400 mm element — the shortest the trade allows — is 12 px
 * long, so the shrink took all of it: no band, no reading (the label needs
 * 44 px), no ✕, and a part that stands in the parts list and counts for
 * brackets had no picture at all (found in review, 30.08.2026).
 *
 * Six pixels, the width of a console dot: the smallest mark this board
 * already draws, and small enough that the remainder is only ever overstated
 * at scales where §3.1 says the band width is overstated anyway. Where the
 * blank is truly nothing, no band is drawn at all — that state has its own
 * form and is not this one.
 */
const BAND_MIN_LENGTH_PX = 6;

const CONSOLE_RADIUS = 3;
const CORNER_CONSOLE_RADIUS = 4.5;
/** How far a console mark's invisible hit target reaches past the drawn dot
 * or bar (task 11) — small marks need the margin, `.wallGrip`'s own reason. */
const CONSOLE_GRIP_PAD = 5;
/**
 * Half the diagonal of the extra console's diamond.
 *
 * A **diamond**, not a square, and that is measured rather than decorative: a
 * filled 8×8 square and the shared-corner console's ⌀ 9 circle are the same
 * area to within half a percent, so at eight pixels with antialiasing the only
 * thing telling them apart was tone — and §5.5.1's own headline says form has
 * to carry alone. A diamond reads against a circle at this size, and it is the
 * house's own mesh motif (found in review, 16.08.2026).
 */
const BRACKET_HALF = 6;
/**
 * Half the length and half the width of the middle bracket's bar, in board
 * pixels.
 *
 * A bar, not a dot: `"middle"` has to be told from `"joint"` without colour
 * (K60-CI Prüfliste 4), so the shape itself carries the difference, drawn
 * along the wall the element it belongs to lies on.
 */
const MIDDLE_MARK_HALF_LENGTH = 6;
const MIDDLE_MARK_HALF_WIDTH = 1.5;
/** How far the mesh over a refusing wall reaches out from it. */
const REFUSAL_DEPTH_PX = 12;
/** How big an object's end handle is, in pixels — the same reasoning as
 * {@link MITER_HANDLE_R}: hittable on a laptop trackpad without covering the
 * end it sits on. */
const FEATURE_HANDLE_R = 5;
/**
 * How far an object's name sits off its wall, *inwards*.
 *
 * Inwards, into the house, because everything else on this board is outside
 * it: the band, the readings, the wall summary. The name used to sit on the
 * middle of the band, fighting the hatching it was drawn over, and lying
 * across a vertical wall horizontally (measured in the browser, 30.08.2026 —
 * walk finding 9).
 */
const FEATURE_LABEL_OFFSET = 13;
/**
 * Roughly how wide one character of the name is at its font size.
 *
 * The rule this feeds is the point: **an object prints its name only where
 * the name fits inside the object.** Three objects side by side printed three
 * full names over one another and produced „HHaustuHaustür" — each label was
 * 38 px wide and none of the objects was. The elements next to them have had
 * this rule since 16.08. ({@link MIN_PX_FOR_READING}); the object label was
 * the one figure on this board without a floor.
 */
const FEATURE_LABEL_PX_PER_CHAR = 5.6;

/**
 * Below this many pixels of door width the swing is left out and the bar
 * alone stands for the door. A quarter circle a few pixels across is a smudge
 * on the wall line, not a door — and the name beside it has gone by then too
 * ({@link FEATURE_LABEL_PX_PER_CHAR}), so the bar is what is left to read.
 */
const DOOR_SWING_MIN_PX = 10;

/**
 * Where along a door its name sits, from the hinge (0) to the latch (1).
 * Off the middle towards the latch: inside the swing the name has the most
 * room where the leaf is not (24.09.2026).
 */
const DOOR_LABEL_ALONG = 0.6;

/**
 * Size of the overview frame, and its clearance from the board's own edges,
 * in pixels (task 9, design §7.3).
 *
 * Small enough to stay clear of the zoom buttons (`.zoomBar`, top right) and
 * the scale bar (bottom left) — the two corners of the board already taken —
 * large enough that the shape inside it still reads as a house.
 */
const OVERVIEW_WIDTH_PX = 132;
const OVERVIEW_HEIGHT_PX = 74;
const OVERVIEW_PADDING_PX = 6;
const OVERVIEW_MARGIN_PX = 8;

interface Vector {
  x: number;
  y: number;
}

/** One wall, as this board has to know it: where it is drawn, how long it is
 * claimed to be, which way is outside, and what is still free on it. */
interface WallView {
  index: number;
  from: ScreenPoint;
  to: ScreenPoint;
  /** Whether a rost may lie here at all — `placement.ts`'s answer, not a
   * second reading of `lengthSource` (§3.4, A-014). */
  accepts: boolean;
  /** The asserted measure. `null` on a wall that takes no placement (§3.4). */
  lengthMm: number | null;
  /** Where that measure came from — read off a drawing, or set by a person.
   * Only the print sheet asks (it marks a set figure beside read ones). */
  source: LengthSource | undefined;
  /** A chamfer's slant (design 2026-10-18): it takes grating, but no object
   * and no hole, and its two corners are only ever butted. */
  fase: boolean;
  /** „2.500 mm belegbar (3.000 bis zum Eckpunkt)“ on a wall a chamfer
   * shortened — `lengthMm` is then the usable stretch — `null` elsewhere. */
  shortened: string | null;
  /** The stated figure the sketch contradicts (E-64), shown as a question
   * beside the dashed wall — never a length to draw or lay against. `null`
   * on every wall that is not disputed. */
  disputedMm: number | null;
  /** Unit vector along the wall, in screen pixels. */
  along: Vector;
  /** Unit vector pointing out of the house — the winding's answer (§3.3). */
  outward: Vector;
  free: FreeStretch[];
  freeMm: number;
}

/** What the ghost is showing: a placement that has not happened yet (§5.4). */
interface Ghost {
  wallIndex: number;
  offsetMm: number;
  lengthMm: number;
  snappedTo: SnapTarget;
  /** The kind the gesture began with — see {@link PlanningBoard}'s `place`. */
  kind: PlacementKind;
}

/**
 * What a gesture in flight is holding on to.
 *
 * Every frame is computed from `pointerMm`/`baseOffsetMm` as they were when
 * the pointer went down, never from the frame before — the same rule
 * `OutlineDraft` states for its own drags: a gesture stays one gesture,
 * however long it lasts.
 */
interface Drag {
  kind:
    | "place"
    | "move"
    | "feature"
    /** An object already on the wall, carried along it by its grip. */
    | "feature-move"
    /** One end of an object, dragged while the other end stands still. */
    | "feature-start"
    | "feature-end"
    /** A hole, carried on both axes of its band (design 2026-09-10 §7.1). */
    | "cutout-move";
  wallIndex: number;
  /**
   * On `move`, where the pointer sat when the gesture began and where the
   * element sat. On `feature`, the one figure it needs: where the pointer
   * came down, so the release can measure how far it travelled — an object is
   * sized by the drag itself, not by `newLengthMm` (§Task 13, 30.08.2026).
   *
   * A placing gesture keeps nothing of the sort — every frame asks where the
   * pointer is now, because the element is not being carried from anywhere.
   */
  pointerMm?: number;
  id?: string;
  baseOffsetMm?: number;
  /** How long the object was when the gesture began — a resize keeps the far
   * end where it was, and that end is `baseOffsetMm + baseLengthMm`. */
  baseLengthMm?: number;
  /** On `cutout-move`, the second axis: where the pointer took hold of the
   * hole across the band, and where the hole's middle sat — both in
   * millimetres from the inner rail. */
  pointerAcrossMm?: number;
  baseAcrossMm?: number;
}

/** The German name of a grating type. Unknown types keep their own word rather
 * than being renamed into something the catalogue never said. */
export const GRATING_TYPE_NAMES: Readonly<Record<string, string>> = {
  pressrost: "Pressrost",
  barfuss: "Barfuß",
  laengsstab: "Längsstab",
};

/** What each kind is called out loud — the tool names of §5.2, so the board
 * and the toolbar say the same word for the same thing. */
export const KIND_NAMES: Readonly<Record<PlacementKind, string>> = {
  grating: "Rostelement",
  gap: "Unterbrechung",
  bracket: "Konsole extra",
  area: "Außenfläche",
};

/**
 * A figure the way a drawing prints it — grouped, without its unit.
 *
 * Derived from `formatMm` rather than built with a second number format, so
 * the German grouping is stated in one place. The unit is dropped only where
 * the drawing itself carries it: a scale bar underneath and a dimension
 * convention around it (§5.5.1, `12×1.000 · 480`).
 */
function plainMm(valueMm: number): string {
  return formatMm(valueMm).replace(/\s*mm$/, "");
}

let placementCounter = 0;

/** An id for something that has just been drawn. Unique per browser session;
 * the document itself lives in the store, and nothing here reads it back. */
function newPlacementId(): string {
  placementCounter += 1;
  const unique =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().slice(0, 8)
      : `${Date.now()}`;
  return `pl-${placementCounter}-${unique}`;
}

function unit(from: ScreenPoint, to: ScreenPoint): Vector {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  return { x: dx / length, y: dy / length };
}

/**
 * Where one edge of the band ends, once that end has had its say.
 *
 * `atStart` picks which end: at the start of the element the edge runs
 * *backwards* past the corner, at the end *forwards*.
 *
 * Reaching nothing moves nothing, which is every element on every board where
 * nobody has treated a corner.
 */
function reach(
  from: ScreenPoint,
  to: ScreenPoint,
  cut: CutWord,
  reachPx: number,
  atStart: boolean,
): ScreenPoint {
  const point = atStart ? from : to;
  if (reachPx === 0) return point;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  const along: Vector = { x: dx / length, y: dy / length };
  // Forwards at the far end, backwards at the near one. An inner mitre is the
  // one case that goes the other way: there the square is claimed twice and
  // the cut takes some off rather than adding it.
  const inward = cut === "gehrung_innen";
  const sign = (atStart ? -1 : 1) * (inward ? -1 : 1);
  return shift(point, along, reachPx * sign);
}


function shift(point: ScreenPoint, direction: Vector, by: number): ScreenPoint {
  return { x: point.x + direction.x * by, y: point.y + direction.y * by };
}

/** The four corners of the band a wall would carry — the hit area the cutout
 * tool clicks into, since at 1 px/mm the band's middle is a whole grating
 * width away from the wall line the grip listens on (design §7.1). */
function bandHitPoints(wall: WallView, band: BandGeometry): string {
  const near = band.innerPx;
  const far = band.innerPx + band.drawnWidth;
  const at = (p: ScreenPoint, d: number) => shift(p, wall.outward, d);
  return [at(wall.from, near), at(wall.to, near), at(wall.to, far), at(wall.from, far)]
    .map((p) => `${p.x},${p.y}`)
    .join(" ");
}

/**
 * The server's validity floor on a hole's middle, **not a clamp into the
 * band**: `document.py` refuses a middle before the wall's start or before
 * the near edge outright, and a refused op takes the whole gesture with it.
 * Past the outer rail or the wall's end a hole keeps its true position and
 * a finding says what does not fit (design §8).
 */
function floorCutout(alongMm: number, acrossMm: number): { offsetMm: number; acrossMm: number } {
  return { offsetMm: Math.max(alongMm, 0), acrossMm: Math.max(acrossMm, 0) };
}

/** The same grid every offset on this board lands on — see {@link GRID_MM}. */
function snapToGrid(mm: number): number {
  return Math.round(mm / GRID_MM) * GRID_MM;
}

/**
 * The surface grating is laid on.
 *
 * Holds placements and shows them. Every statement about where a rost may lie
 * — which wall may take one, which side of it is outside, where along it the
 * element comes to rest — is made in `lib/placement.ts`, and every statement
 * about what the result *is* — how many runs, how many consoles, which rule is
 * broken — comes back from `planning` in the answer (§2, §3.5). This file
 * draws, listens and reports; it decides nothing that could be wrong on a
 * building site.
 *
 * Two figures live here and neither is a domain one: the ten-millimetre grid
 * the arrow keys move by (imported, not retyped) and the twelve-pixel catching
 * radius. **The radius is capped in millimetres against `minGapMm` from the
 * answer**, and that cap is the point of it: twelve pixels are 440 mm of real
 * wall on a 17,4 m facade while a downpipe measures 120, so an uncapped radius
 * would offer to close a real interruption — two runs quietly become one, one
 * console too few, and it shows up on the building site (§5.3).
 *
 * **No green.** `--st-confirmed` means „a person has confirmed this" and
 * `--st-review` means „this needs looking at"; on this board nothing has been
 * confirmed and nothing is unclear, so neither appears — selection and focus
 * are ink (§5.5.1, and the mistake the drawing editor already made once).
 */
export function PlanningBoard({
  outline,
  placements,
  features,
  specs,
  jobGratingType,
  newLengthMm,
  minGapMm,
  tool,
  featureTemplate,
  selected,
  selectedWall,
  selectedFeature,
  issueIds,
  consoles,
  corners,
  treatableWalls,
  cuts,
  onToggleCorner,
  onCornerMenu,
  onConsoleMenu,
  onPlace,
  onMove,
  onDelete,
  onSelect,
  onSelectWall,
  onClearPick,
  onHoverWall,
  setFeature,
  onSelectFeature,
  onDeleteFeature,
  onFeatureRefused,
  cutouts,
  selectedCutout,
  onSelectCutout,
  onMoveCutout,
  onPlaceCutout,
  onDeleteCutout,
  view = FIT_VIEW,
  onView,
  maximized = false,
  onMaximize,
  maximizeRef,
  sheet,
}: {
  /** The computed outline, as the schema step hands it over. */
  outline: DemoOutline;
  placements: Placement[];
  /** The doors, sills and pipes standing on a wall — not a stretch of it, so
   * `freeStretches`/`snapOffset` do not read this list (E-47 follow-up). */
  features: FacadeFeature[];
  /** The catalogue out of the answer (§3.2) — never a constant in the browser. */
  specs: GratingSpec[];
  /** The type of the job. `null` means nobody has chosen one yet, and then no
   * width can be drawn — a width is a manufacturing figure (A-014). */
  jobGratingType: GratingType | null;
  /**
   * How long a new placement is, in mm.
   *
   * The screen decides and hands it in: for a rost it is `standardLengthMm`
   * out of the delivered catalogue, for an interruption the width somebody
   * typed. **Not a figure to type here** — „how long is a new element" is a
   * domain question, and the answer already travels with the catalogue.
   */
  newLengthMm: number;
  /** The narrowest interruption the model must be able to express, out of the
   * answer (§4.4). The catching radius is capped at half of it. */
  minGapMm: number;
  tool: Tool;
  /**
   * What a new object is built with — the screen's current „Art" choice, plus
   * whatever somebody adjusted beside it before drawing. Read only while
   * `tool === "feature"`; every field a new {@link FacadeFeature} needs beyond
   * its id and its drawn extent, so building one is `{ ...featureTemplate,
   * id, wallIndex, offsetMm, lengthMm }` and never a wholesale spread of
   * `FEATURE_DEFAULTS`, which carries `name`/`defaultLengthMm` too.
   */
  featureTemplate: {
    featureType: FeatureType;
    covered: boolean;
    widthOffsetMm: number;
    splitsRun: boolean;
    label: string | null;
  };
  selected: string | null;
  selectedWall: number | null;
  /** The facade object the side panel is working on, or `null`. Its own slot
   * and not `selected`: an object is not a placement, and the two panels ask
   * different questions. */
  selectedFeature: string | null;
  /** Placements the answer has a finding about (§3.5.1). */
  issueIds: string[];
  consoles: ConsoleMark[];
  /** The corners somebody put on mitre, by the wall whose **end** each is. */
  corners: CornerMark[];
  /**
   * The wall ends a run actually crosses — where a corner handle appears.
   *
   * Read off the answer's own `corners`, never worked out here: which joints
   * sit on a wall boundary is a domain statement (§4.1), and a board that
   * decided it for itself would be a second opinion about the topology.
   */
  treatableWalls: number[];
  /**
   * How each element is cut at either end, by placement id, out of the answer.
   *
   * Two mitre values and not a boolean, because the drawing has to know which
   * way the cut leans — outwards past the corner, or back from it — and
   * deriving that here would put a rule of the trade in the display layer.
   */
  cuts: ReadonlyMap<string, BandCut>;
  onToggleCorner: (wallIndex: number) => void;
  /**
   * Somebody asked what can be done with one wall — a right-click anywhere
   * on it (its band, an interruption, or the bare grip), or Shift+F10 on the
   * focused wall, which real browsers turn into the same `contextmenu`
   * event but jsdom does not, so `wallKeyDown` answers the key itself.
   *
   * `null` closes the menu. The board decides *where* it opens and *which*
   * wall and corner it is about; what may be done there is the screen's
   * business (a filling has a length rule and the catalogue behind it, an
   * inserted object has to land clear of whatever else is already there).
   */
  onCornerMenu: (request: CornerMenuRequest | null) => void;
  /**
   * Somebody asked what one bracket should build. `undefined` where the
   * screen offers no such menu — `PlanningBoard.test.tsx` renders every
   * console kind without one, and a mark with nothing to open stays a plain
   * dot: no tab stop, no listener, exactly as before this task.
   */
  onConsoleMenu?: (request: ConsoleMenuRequest) => void;
  onPlace: (placement: Placement) => void;
  /** `live` folds a drag's many frames into one step on the undo stack. */
  onMove: (id: string, offsetMm: number, live: boolean) => void;
  onDelete: (id: string) => void;
  onSelect: (id: string | null) => void;
  onSelectWall: (index: number | null) => void;
  /**
   * A press on the bare board, with nothing in hand — let go of the pick.
   * Optional: a board without a side column has nothing to give back.
   */
  onClearPick?: () => void;
  onHoverWall: (index: number | null, reason: WallRefusal | null) => void;
  /**
   * One drag, one object — the board hands over the finished
   * {@link FacadeFeature}, id and all, the same contract `onPlace` keeps for a
   * placement.
   *
   * **Upsert, keyed on `id`** — the op `SetFeature` of the design (§2) does
   * „anlegen und ändern" in one, and so does this: a new object and a moved
   * one are the same statement about the same wall. `live` folds a drag's
   * many frames into one step on the undo stack, exactly as `onMove` does.
   */
  setFeature: (feature: FacadeFeature, live?: boolean) => void;
  onSelectFeature: (id: string | null) => void;
  onDeleteFeature: (id: string) => void;
  /**
   * A gesture about an object that was turned down, or `null` where one
   * succeeded.
   *
   * Silently snapping the object clear, or silently doing nothing, are both
   * worse than the refusal itself: the person cannot tell which of the two
   * happened, or whether anything happened at all.
   */
  onFeatureRefused: (refusal: { wallIndex: number; reason: FeatureRefusal } | null) => void;
  /**
   * The holes, as the draft holds them (design 2026-09-10 §4). Anchored to a
   * wall, never to an element: which sheet each is drilled into is read here
   * through `carrierAt`, the same rule `cutoutsOn` and the finding keep.
   */
  cutouts: Cutout[];
  /** The hole the side panel is working on, or `null` — its own slot, as
   * `selectedFeature` is: a hole is neither a placement nor an object. */
  selectedCutout: string | null;
  onSelectCutout: (id: string | null) => void;
  /**
   * A hole carried to a new place on both axes. `live` folds a drag's many
   * frames into one step on the undo stack, exactly as `onMove` does; the
   * release repeats the last frame with `live=false` so the screen can
   * announce the op for the settled position.
   */
  onMoveCutout: (id: string, offsetMm: number, acrossMm: number, live: boolean) => void;
  /** A new free hole where the cutout tool clicked on a band, or where
   * Enter set one on a focused wall. */
  onPlaceCutout: (cutout: Cutout) => void;
  onDeleteCutout: (id: string) => void;
  /** What part of the drawing is on screen. `FIT_VIEW` shows the whole shape. */
  view?: Viewport;
  /** Where a gesture on the board asks for a different view. */
  onView?: (view: Viewport) => void;
  /**
   * Whether the screen has made this board fill the window (A3, Entscheid
   * Denis 23.09.2026). The board then draws at its container's measured
   * height instead of the fixed 520 px, and Strg/⌘ + Plus/Minus/0 zoom it
   * from anywhere on the page — the browser's own page zoom would only
   * enlarge the tools around a drawing that already fills the window.
   */
  maximized?: boolean;
  /** „Zeichenfläche maximieren" — the button sits in the zoom bar, and is
   * only there where a screen listens. */
  onMaximize?: () => void;
  /** Where the screen wants that button, to hand the focus back to it. */
  maximizeRef?: Ref<HTMLButtonElement>;
  /** Drawn for paper instead of for a pointer — see {@link BoardSheet}. */
  sheet?: BoardSheet;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [measuredWidth, setBoardWidth] = useState(BOARD_FALLBACK_WIDTH_PX);
  // A sheet has the paper's size, whatever the box measures on screen.
  const boardWidth = sheet?.width ?? measuredWidth;
  /** The height the element measures at — only read while maximized, where
   * the stylesheet lets the board grow to its container. In place the board
   * is 520 px by rule, and the viewBox says so whatever the box measures. */
  const [measuredHeight, setMeasuredHeight] = useState(0);
  const boardHeight = sheet
    ? sheet.height
    : maximized && measuredHeight > 0
      ? measuredHeight
      : BOARD_HEIGHT_PX;
  const [ghost, setGhost] = useState<Ghost | null>(null);
  const [refusal, setRefusal] = useState<{ wallIndex: number; reason: WallRefusal } | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  /**
   * How the wall under the gesture laid out its figures when the gesture
   * began — held while it runs (`holdWallReadings`, final review M-3).
   */
  const [heldReadings, setHeldReadings] = useState<HeldReadings | null>(null);
  /** Whether the gesture in flight has already produced a step to overwrite. */
  const emitted = useRef(false);
  /**
   * The last frame a hole was carried to, so the release can settle it.
   *
   * A placement needs nothing of the sort — its `pointerUp` only clears the
   * drag — because a placement carries no op: it travels in the planning
   * state as a whole. A hole carries one, and that op belongs to the final
   * position, not to the frame that opened the gesture.
   */
  const lastCutoutFrame = useRef<{ id: string; offsetMm: number; acrossMm: number } | null>(null);
  /** What was last reported upwards, so a moving pointer does not re-render the
   * screen sixty times a second with the same two values. */
  const reported = useRef<string>("");
  /**
   * Whether the space bar is down — a mouse-only pan that needs no button of
   * its own free. A `useRef` and not a `useState`: nothing on the board
   * draws differently while it is held, and re-rendering on every keystroke
   * would be felt on a board this size (correction to this task's own brief,
   * 10.09.2026).
   */
  const spaceHeld = useRef(false);
  /**
   * Where a pan (middle button, or left button with space held) began — the
   * pointer's own screen position and the view's centre at that moment, so
   * every later frame is computed from that one start rather than from the
   * frame before, the same rule {@link Drag} keeps for every other gesture.
   * `null` outside a pan.
   *
   * A `useRef`, not a `useState` (fix round 1, review of 10.09.2026, same
   * reasoning as {@link spaceHeld}): the capture-phase pointer listener below
   * reads and writes this on every native pointer event, and a `setState`
   * there would race the render/effect cycle that would otherwise refresh
   * the closures reading it — a fast drag delivers several `pointermove`s
   * before React has necessarily re-rendered. Nothing here is drawn either.
   */
  const panFrom = useRef<{
    x: number;
    y: number;
    centre: { xMm: number; yMm: number };
  } | null>(null);
  const patternPrefix = useId().replace(/[^a-zA-Z0-9_-]/g, "");

  /**
   * The house is fitted to the box the board has — and only to a box it can
   * draw in (UX review 26.10.2026, B-5). A box with no room inside the
   * padding is no box to fit to, as one of no width is not: a full-page
   * capture lays the page out once at a 1 × 1 window, the board measured
   * 136 px, and the house came out as a dot under a `50.000 mm` scale bar.
   * The board keeps the last box it could draw in, and fits again with the
   * next one — from nothing to a width included. The box is read once before
   * the first paint (a layout effect): the observer answers after it, and
   * the house stood fitted to the fallback width for a frame, then jumped.
   */
  useLayoutEffect(() => {
    const element = svgRef.current;
    if (!element) return;
    const roomToDraw = (width: number) => width > 2 * VIEW_PADDING;
    // The content box, as the observer reports it: without the board's edge.
    if (roomToDraw(element.clientWidth)) setBoardWidth(element.clientWidth);
    // jsdom has no ResizeObserver and no layout; the fallback width is what
    // the component tests measure against, and it is a real width.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      const height = entry?.contentRect.height ?? 0;
      if (roomToDraw(width)) setBoardWidth(width);
      if (height > 0) setMeasuredHeight(height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const nodes: MmPoint[] = outline.nodes.map((node, index) => ({
    id: `n${index}`,
    xMm: node.xMm,
    yMm: node.yMm,
  }));
  // The consoles ride along in the fit so a mark that sits outside the corner
  // list cannot be drawn off the board — they are points on the shape, so in
  // practice they change nothing about it.
  const fitNodes = [
    ...nodes,
    ...consoles.map((mark, index) => ({ id: `c${index}`, xMm: mark.xMm, yMm: mark.yMm })),
  ];
  const projectionOptions = {
    width: boardWidth,
    height: boardHeight,
    padding: sheet ? SHEET_PADDING : VIEW_PADDING,
  };
  const projection = projectRun(fitNodes, {
    ...projectionOptions,
    ...(view.scale !== null ? { scale: view.scale } : {}),
    ...(view.centreMm !== null ? { centreMm: view.centreMm } : {}),
  });

  const walls: WallView[] = outline.segments.flatMap((segment, index) => {
    const from = projection.points[`n${index}`];
    const to = projection.points[`n${index + 1}`];
    if (!from || !to) return [];
    const along = unit(from, to);
    const sign = outwardSign(outline, index);
    // The left-hand normal `(-dy, dx)`, the one `outwardSign` multiplies and
    // the one `dimensionLine` builds — one formula for both layers.
    const free = freeStretches(outline, placements, index);
    return [
      {
        index,
        from,
        to,
        accepts: wallAcceptsGrating(outline, index),
        lengthMm: wallLengthMm(outline, index),
        source: outline.segments[index]?.lengthSource,
        fase: isFase(segment),
        shortened: shortenedWallLabel(segment),
        disputedMm: segment.lengthMm === null ? (segment.implausibleLengthMm ?? null) : null,
        along,
        outward: { x: -along.y * sign, y: along.x * sign },
        free,
        freeMm: free.reduce((sum, stretch) => sum + stretch.lengthMm, 0),
      },
    ];
  });

  /**
   * The catching radius in millimetres — pixels at the **fitted** scale,
   * capped at half the narrowest interruption the domain knows.
   *
   * The fitted scale and not the drawn one: this decides whether a click
   * counts as „on that corner", and a radius tied to the zoom would shrink
   * from some 375 mm to 12 mm at full magnification — the corner menu would
   * go out of reach precisely after somebody zoomed in to reach it (design
   * §7.1). What the zoom does buy is a steadier hand in pixels, and that is
   * already true without moving this number.
   *
   * The cap is the whole reason this line exists; see the component docstring.
   */
  const toleranceMm = Math.min(CATCH_RADIUS_PX / projection.fitScale, minGapMm / 2);

  /** Whether the board is magnified past its fitting scale — see
   * {@link bandHalfWidth} (D-7, design 2026-09-10). */
  const zoomed = projection.scale > projection.fitScale;

  // Any tool up, the cutout tool included: the grips beneath go transparent
  // and the wall takes the gesture. What the tool then lays — an element, an
  // object or a hole — is decided in the handlers, not here.
  const placing = tool !== "select";
  const jobSpec = specs.find((spec) => spec.type === jobGratingType) ?? null;

  function wallAt(index: number): WallView | null {
    return walls.find((wall) => wall.index === index) ?? null;
  }

  /**
   * How far an oblique end (`schraeg`, at a chamfer's corner) moves the
   * band's outer edge along its wall, as a share of the drawn band width —
   * `tan(φ/2)` for a turn of φ between the two walls, plus at a corner that
   * stands out of the house and minus at one that steps into it.
   *
   * That is where the outer edges of two bands of the same width meet on the
   * bisector, so the two ends close up without a gap and without overlap —
   * the same arithmetic that makes a mitre at a right angle reach exactly one
   * band width (tan 45° = 1). Read off the drawn walls: which way a corner
   * turns is geometry the drawing already holds, and the answer says only
   * *that* the end is oblique.
   */
  function bevelAt(wallIndex: number, atEnd: boolean): number {
    const count = walls.length;
    const first = atEnd ? wallAt(wallIndex) : wallAt((wallIndex - 1 + count) % count);
    const second = atEnd ? wallAt((wallIndex + 1) % count) : wallAt(wallIndex);
    if (!first || !second) return 0;
    const cos = first.along.x * second.along.x + first.along.y * second.along.y;
    const half = Math.acos(Math.min(Math.max(cos, -1), 1)) / 2;
    // The second wall bends away from the first one's outside: the corner
    // stands out, and the outer edge reaches past it.
    const convex = second.along.x * first.outward.x + second.along.y * first.outward.y < 0;
    return (convex ? 1 : -1) * Math.tan(half);
  }

  /** A point `mm` along a wall, in board pixels. The asserted measure is the
   * ruler — an element that runs past its wall has to be *seen* running past
   * it (E-13), and it does when the same ruler draws both. */
  function pointOn(wall: WallView, mm: number): ScreenPoint {
    const fraction = wall.lengthMm ? mm / wall.lengthMm : 0;
    return {
      x: wall.from.x + (wall.to.x - wall.from.x) * fraction,
      y: wall.from.y + (wall.to.y - wall.from.y) * fraction,
    };
  }

  /** The schema sheet's summaries, laid out across walls (P-7); the screen
   * keeps its fixed offset. */
  const summaryOffsets =
    sheet?.detail === "schema" ? sheetSummaryOffsets(walls, placements, pointOn, summaryPushPxOf) : null;

  /**
   * Where the pointer is along this wall, in the wall's own millimetres.
   *
   * Beyond either end on purpose: `snapOffset` treats an overshoot as a clamp
   * rather than as a near miss.
   *
   * The wall's length comes in as an argument rather than off the wall,
   * because a wall without one has no millimetres to point at — and there must
   * be exactly one place that says so (see {@link previewOn}). A board that
   * refuses in two places refuses for two reasons, and one of them then goes
   * untested.
   *
   * Takes anything with a `clientX`/`clientY` rather than a
   * `ReactPointerEvent` specifically — a right-click is a `MouseEvent`, and
   * Shift+F10 has no event with screen coordinates in it at all, only a
   * bounding box to read one off (task 14).
   */
  function pointerMmOn(
    wall: WallView,
    lengthMm: number,
    point: { clientX: number; clientY: number },
  ): number {
    // Before layout there is no box; the origin is then the right answer,
    // because the viewBox is written in the board's own pixels.
    const box = svgRef.current?.getBoundingClientRect();
    const x = point.clientX - (box?.left ?? 0);
    const y = point.clientY - (box?.top ?? 0);
    const dx = wall.to.x - wall.from.x;
    const dy = wall.to.y - wall.from.y;
    const squared = dx * dx + dy * dy || 1;
    return (((x - wall.from.x) * dx + (y - wall.from.y) * dy) / squared) * lengthMm;
  }

  /**
   * How far the pointer is **across** the band, in millimetres from its near
   * edge — positive outward, negative toward the house (design §7.1).
   *
   * The perpendicular of the pointer onto the wall line, read along
   * `wall.outward` (the winding's answer, so the sign is right on all four
   * wall directions), minus where the band's inner rail sits, divided by the
   * band's *own* pixels per millimetre. Not `projection.scale`: at the fit
   * scale the band is clamped to `BAND_MIN_PX` and a true-scale millimetre
   * would move the hole off the drawn band under the pointer's very finger;
   * zoomed in (D-7) the two scales are the same number.
   */
  function pointerAcrossOn(
    wall: WallView,
    point: { clientX: number; clientY: number },
    band: BandGeometry,
  ): number {
    const box = svgRef.current?.getBoundingClientRect();
    const x = point.clientX - (box?.left ?? 0);
    const y = point.clientY - (box?.top ?? 0);
    const outwardPx = (x - wall.from.x) * wall.outward.x + (y - wall.from.y) * wall.outward.y;
    return (outwardPx - band.innerPx) / (band.drawnWidth / band.widthMm);
  }

  /**
   * The band an element draws, for the hole in it and for the pointer across
   * it. Without an element — the bare hit area the cutout tool clicks into —
   * it is the band the job's type would draw on this wall.
   */
  function bandOf(placement: Placement | null): BandGeometry {
    const widthMm = bandWidthMm(placement, specs, jobGratingType);
    const typeWidthMm = placement
      ? bandWidthMm({ ...placement, widthMm: null }, specs, jobGratingType)
      : widthMm;
    return bandGeometry(widthMm, typeWidthMm, projection.scale, zoomed, placement?.widensOutward ?? false);
  }

  /** How far an element's band lies past its type's outer line, in pixels —
   * 0 for every band but one widened outward (`bandHalves`). */
  function growthPxOf(placement: Placement): number {
    if (!placement.widensOutward || placement.kind !== "grating") return 0;
    const widthPx = bandWidthMm(placement, specs, jobGratingType) * projection.scale;
    const typeWidthPx = bandWidthMm({ ...placement, widthMm: null }, specs, jobGratingType) * projection.scale;
    return bandHalves(widthPx, typeWidthPx, zoomed, true).growthPx;
  }

  /** How much further out a wall's summary stands than `SUMMARY_OFFSET`:
   * past the furthest band widened outward on it. */
  function summaryPushPxOf(wallIndex: number): number {
    return Math.max(0, ...placements.filter((each) => each.wallIndex === wallIndex).map(growthPxOf));
  }

  /**
   * The cutout tool's hit area on a wall: the job's band, stretched over
   * every element band on it. A band widened outward reaches past the job's
   * (31.10.2026), and a width zone reaches inside it — a click on either
   * strip is a click on a band, and the carrier under it measures the hole.
   */
  function hitBandOf(wall: WallView): BandGeometry {
    const bands = [
      bandOf(null),
      ...placements.filter((each) => each.wallIndex === wall.index && each.kind === "grating").map(bandOf),
    ];
    const near = Math.min(...bands.map((band) => band.innerPx));
    const far = Math.max(...bands.map((band) => band.innerPx + band.drawnWidth));
    return { innerPx: near, drawnWidth: far - near, widthMm: bandOf(null).widthMm };
  }

  /**
   * A pointer position as a millimetre point on the drawing.
   *
   * The inverse of the projection, and the only place it is inverted: a wheel
   * has to know what it is zooming *at*, and every wall-bound helper
   * (`pointerMmOn`) answers a different question — how far along one wall.
   *
   * Falls back to `{ xMm: 0, yMm: 0 }` where there is nothing to invert
   * against — an outline with no nodes has no first node to read the
   * projection's offset off, and dividing by an undefined point would be a
   * `NaN` in the view instead of a wrong-but-finite one.
   */
  function pointerMm(point: { clientX: number; clientY: number }): {
    xMm: number;
    yMm: number;
  } {
    const box = svgRef.current?.getBoundingClientRect();
    const x = point.clientX - (box?.left ?? 0);
    const y = point.clientY - (box?.top ?? 0);
    // The projection puts millimetre `m` at `offset + m * scale`; the first
    // node gives the offset back.
    const anyNode = nodes[0];
    const anyPoint = anyNode ? projection.points[anyNode.id] : undefined;
    if (!anyNode || !anyPoint) return { xMm: 0, yMm: 0 };
    return {
      xMm: anyNode.xMm + (x - anyPoint.x) / projection.scale,
      yMm: anyNode.yMm + (y - anyPoint.y) / projection.scale,
    };
  }

  /**
   * The board's own centre, in millimetres — the anchor a gesture with no
   * pointer position of its own zooms around.
   *
   * `view.centreMm` once something is already off screen; otherwise the
   * board's own middle, inverted through the projection exactly as a real
   * pointer position would be. Never `{ xMm: 0, yMm: 0 }` — a coordinate the
   * drawing may not even use — which is what the zoom buttons anchored on
   * before this fix while the keyboard already computed this same figure:
   * two answers to "what is this gesture zooming at" for the same gesture
   * (review finding 3, schlussreview 10.09.2026). One function, one anchor,
   * read by both.
   */
  function boardCentreMm(): { xMm: number; yMm: number } {
    return (
      view.centreMm ??
      pointerMm({
        clientX: (svgRef.current?.getBoundingClientRect().left ?? 0) + boardWidth / 2,
        clientY: (svgRef.current?.getBoundingClientRect().top ?? 0) + boardHeight / 2,
      })
    );
  }

  /** Ask for a new scale, keeping `at` where it is on screen. A no-op with no
   * `onView` to tell — a board nobody is listening to cannot zoom itself. */
  function zoom(factor: number, at: { xMm: number; yMm: number }): void {
    if (!onView) return;
    onView(zoomBy(view, factor, at, projection.fitScale, projection.centreMm));
  }

  /** Move the view by a share of the board's own size — the keyboard's
   * answer to a drag pan, and the same two axes it moves an element by
   * (§5.3). Only meaningful once something is off screen (`view.centreMm`),
   * which `zoom`/`boardKeyDown` guard before calling this. */
  function pan(dx: number, dy: number): void {
    if (!onView || view.centreMm === null) return;
    onView({
      ...view,
      centreMm: {
        xMm: view.centreMm.xMm + (boardWidth * PAN_STEP * dx) / projection.scale,
        yMm: view.centreMm.yMm + (boardHeight * PAN_STEP * dy) / projection.scale,
      },
    });
  }

  /**
   * The whole-wall menu request for a click at `point` on `wall` — the one
   * computation both the band's `onContextMenu` and the bare wall-grip's
   * `onContextMenu`/Shift+F10 build (task 14).
   *
   * `offsetMm` is clamped to the wall rather than left as `pointerMmOn`'s raw
   * overshoot: a corner-catch test run against an unclamped offset would
   * fail on exactly the clicks the existing „Ecke auffüllen" tests use — a
   * pointer thousands of pixels off the board, on purpose, to say
   * unambiguously „this end, not that one" (§ „Ecke auffüllen" tests,
   * `PlanningBoard.test.tsx`). Clamping first keeps that idiom working and
   * gives `offsetMm` a value an inserted object could actually stand at.
   *
   * `placementSpan` restores a reach the first version of this function lost
   * (review round 1, important 2). Before task 14, the corner menu measured
   * `atStart` against the *clicked element's own* drawn ends, so a
   * right-click anywhere on a band offered the treatment for whichever of
   * *its own* two ends was nearer — the whole band was live, not only a
   * pixel radius around the wall's real end. Reproduced here: given a
   * placement's own `offsetMm`/`lengthMm`, `atStart` is decided the same
   * way, and `nearCorner` is unconditionally true for any click that landed
   * on a placement at all — deferring whether that band's nearer end is a
   * *real* corner to `crossed`/`gratingAtCorner` in `PlanningScreen`
   * (`fact`/`cornerFacts`), exactly as it already did before this task
   * existed. `null` for a bare wall-grip click, which has no element of its
   * own to ask and falls back to the wall's own two ends — `nearCorner`
   * there is the pixel-converted catch radius, since a bare stretch has
   * nothing else to measure against.
   */
  function wallMenuRequestAt(
    wall: WallView,
    placementId: string | null,
    point: { clientX: number; clientY: number },
    placementSpan: { offsetMm: number; lengthMm: number } | null = null,
    cutoutId: string | null = null,
    featureId: string | null = null,
  ): CornerMenuRequest {
    const lengthMm = wall.lengthMm ?? 0;
    const rawMm = pointerMmOn(wall, lengthMm, point);
    const offsetMm = Math.min(Math.max(rawMm, 0), lengthMm);
    const nearEndDistMm = placementSpan ? Math.abs(rawMm - placementSpan.offsetMm) : offsetMm;
    const farEndDistMm = placementSpan
      ? Math.abs(rawMm - (placementSpan.offsetMm + placementSpan.lengthMm))
      : lengthMm - offsetMm;
    const atStart = nearEndDistMm <= farEndDistMm;
    const cornerWallIndex = atStart ? (wall.index - 1 + walls.length) % walls.length : wall.index;
    // The one joint a non-closed outline does not have: between the last
    // wall's end and the first wall's start. Naming it here — whether by
    // wrapping from wall 0's start or by landing on the last wall's own end
    // directly — would offer a mark `_set_corner` on the server refuses
    // outright („Wand … endet offen", `document.py:1227`; review round 1,
    // important 5).
    const openEnd = !outline.closed && cornerWallIndex === walls.length - 1;
    // In millimetres, but converted from a pixel radius against this board's
    // own **fitted** scale rather than a fixed figure — the mistake
    // `CATCH_RADIUS_PX`'s own doc comment warns against, and the one this
    // constant had until review round 1: at A-10's scale a fixed millimetre
    // catch was one pixel wide (important 1). Fitted and not drawn for the
    // same reason as `toleranceMm` above: pinned to the zoom, this would
    // shrink to 12 mm at full magnification, right when zooming in to reach
    // a corner would put it out of reach. Only asked for a bare wall-grip
    // click; a placement's own extent is its reach regardless of scale.
    const catchMm = CATCH_RADIUS_PX / projection.fitScale;
    // Asked of the click itself, whether or not it landed on a placement —
    // that is what makes it usable as the heading's statement of fact, where
    // `nearCorner` below carries a band's whole-length reach as well.
    const geometryNear = offsetMm <= catchMm || lengthMm - offsetMm <= catchMm;
    return {
      placementId,
      cutoutId,
      featureId,
      wallIndex: cornerWallIndex,
      atStart,
      objectWallIndex: wall.index,
      offsetMm,
      cornerInReach: geometryNear && !openEnd,
      nearCorner: (placementSpan !== null || geometryNear) && !openEnd,
      x: point.clientX,
      y: point.clientY,
      boardWidth,
      boardHeight,
    };
  }

  function report(index: number | null, reason: WallRefusal | null): void {
    const key = `${index}:${reason}`;
    if (reported.current === key) return;
    reported.current = key;
    onHoverWall(index, reason);
  }

  /**
   * Where a new element would come to rest with the pointer here — or why it
   * would not.
   *
   * The pointer carries the element by its middle, so what lands under the
   * finger is the element and not its left edge.
   *
   * **The one place §3.4 is applied on this board.** Both refusals are decided
   * here and nowhere else: no asserted measure, and no room left. Deciding
   * either of them a second time somewhere upstream would make this line
   * unreachable, and an unreachable rule is one no test can defend.
   */
  function previewOn(wall: WallView, event: ReactPointerEvent<SVGElement>): Ghost | WallRefusal {
    if (!wall.accepts || wall.lengthMm === null) return "wall_without_measure";
    const kind = PLACEMENT_OF_TOOL[tool];
    // Not reachable — the callers check `tool` first — but a line the
    // compiler holds, where the cast it replaces held nothing.
    if (kind === null) return "wall_without_measure";
    const pointerMm = pointerMmOn(wall, wall.lengthMm, event);
    const wanted = pointerMm - newLengthMm / 2;

    /**
     * A console and a hatch do not take up wall, so they do not need free wall.
     *
     * `OCCUPYING_KINDS` in `placement.ts` already says that a `bracket` and an
     * `area` occupy nothing (§3.5): a console sits *under* the run it carries
     * and a hatch lies in front of the facade. Sending them through
     * `snapOffset` anyway undid that one layer up — an extra console could not
     * be put where grating lies, which is the only place anybody wants one,
     * and the refusal even said the wrong sentence („Das kleinste Element
     * misst 400 mm", about a console). Found in review, 16.08.2026.
     *
     * They still snap: to the grid, and clamped to the wall. What they skip is
     * the test for *room*.
     */
    if (kind === "bracket" || kind === "area") {
      const maxOffsetMm = Math.max(wall.lengthMm - newLengthMm, 0);
      const onGrid = Math.min(
        Math.max(Math.round(wanted / GRID_MM) * GRID_MM, 0),
        maxOffsetMm,
      );
      return { wallIndex: wall.index, offsetMm: onGrid, lengthMm: newLengthMm, snappedTo: "grid", kind };
    }

    const snap = snapOffset(outline, placements, wall.index, wanted, newLengthMm, toleranceMm);
    if (!snap) return "wall_has_no_room";
    return {
      wallIndex: wall.index,
      offsetMm: snap.offsetMm,
      lengthMm: newLengthMm,
      snappedTo: snap.snappedTo,
      kind,
    };
  }

  function showPreview(wall: WallView, event: ReactPointerEvent<SVGElement>): void {
    const outcome = previewOn(wall, event);
    if (typeof outcome === "string") {
      setGhost(null);
      setRefusal({ wallIndex: wall.index, reason: outcome });
      report(wall.index, outcome);
      return;
    }
    setGhost(outcome);
    setRefusal(null);
    report(wall.index, null);
  }

  /**
   * Lay one placement of `kind` — **the kind the gesture began with**.
   *
   * Taken as an argument rather than read off `tool` at the moment of release.
   * A gesture is a gesture: if the tool changes while the pointer is down —
   * Escape, a keyboard shortcut, anything — the release used to produce a
   * placement of whatever tool was current, `kind: "select"` included. That is
   * not a kind; it went into the draft, into `localStorage`, and came back as
   * a refusal from the endpoint (found in review, 16.08.2026). `Drag` and the
   * ghost carry the kind from the beginning for exactly this reason.
   */
  function place(kind: PlacementKind, wallIndex: number, offsetMm: number): void {
    const placement: Placement = {
      id: newPlacementId(),
      kind,
      wallIndex,
      offsetMm,
      lengthMm: newLengthMm,
      // Nothing is mitred at the moment a thing is placed, and no blank is
      // typed — both `null` mean „whatever it comes to" (§3.2, E-47).
      blankLengthMm: null,
      blankSnapshotMm: null,
      // „the type of the job", never „none" and never a guess (§3.2).
      gratingType: null,
      widthMm: null,
      // Nothing a tool can place ends a run: `Tool` has not contained „gap"
      // since the facade object replaced it (`:135`), so this was `false` in
      // every case it was ever asked. Whether a run ends at a door is a
      // statement the object itself makes (`FacadeFeature.splitsRun`).
      splitsRun: false,
      // Only grating is numbered — a numbered interruption would make the
      // element count skip (§3.6).
      label: kind === "grating" ? nextLabel(placements, wallIndex) : null,
      depthMm: null,
    };
    onPlace(placement);
    onSelect(placement.id);
    setGhost(null);
  }

  function beginGesture(event: ReactPointerEvent<SVGElement>): void {
    // Best effort: a pointer id the browser does not consider active — a
    // synthetic event from a test harness, a stylus that lifted between the
    // two calls — throws here, and losing capture is not a reason to lose the
    // gesture.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* no capture; the gesture runs without it */
    }
    emitted.current = false;
  }

  function endGesture(event: ReactPointerEvent<SVGElement>): void {
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      /* never captured; nothing to release */
    }
  }

  /**
   * Lay one object of the current template, from `startMm` to `endMm` — the
   * two ends of the gesture that drew it, in either order.
   *
   * Snapped to the grid like every other offset on this board, and clamped to
   * the wall: a drag that overshoots the end still leaves the object on the
   * wall it was drawn on, the way `snapOffset` clamps a placement (§5.3).
   *
   * **A gesture with no extent is a click, and a click takes the type's own
   * catalogue length** (`FEATURE_DEFAULTS`, §6: „Klicken statt Ziehen:
   * Standardlänge des Typs") — the same figure `newLengthMm` is for a
   * grating. Decided here and in no caller, because there are two of them and
   * only the keyboard one had it: a click on the wall produced a bare
   * `GRID_MM`, which is a 10 mm Fallrohr, 0,4 px wide at facade scale and
   * hardly selectable — the very defect the keyboard branch names in its own
   * comment, on the branch almost nobody uses (walk of 30.08.2026, finding 2).
   */
  function placeFeature(wall: WallView, startMm: number, endMm: number): void {
    if (wall.lengthMm === null) return;
    const wallLength = wall.lengthMm;
    // The start stays where the gesture began; only the far end is clamped to
    // the wall. Keeping the *length* and sliding the start backwards to make
    // it fit — what this did until 30.08.2026 — moved the one figure the
    // gesture states unambiguously, and moved it without a word (walk finding
    // 10). An object that reaches past its wall is a finding
    // (`feature_beyond_wall`), and it is a finding about a wall whose length
    // changed *after* the drawing; it is not something a drag should be able
    // to produce.
    const offsetMm = Math.min(
      Math.max(snapToGrid(Math.min(startMm, endMm)), 0),
      Math.max(wallLength - GRID_MM, 0),
    );
    const drawnMm = snapToGrid(Math.max(startMm, endMm)) - snapToGrid(Math.min(startMm, endMm));
    const wantedMm = drawnMm > 0 ? drawnMm : templateLengthMm(wallLength);
    const endMmOnWall = Math.min(
      Math.max(offsetMm + wantedMm, offsetMm + GRID_MM),
      wallLength,
    );
    const lengthMm = Math.max(endMmOnWall - offsetMm, GRID_MM);
    // Refused, not shifted clear. The menu's „Objekt einsetzen" *does* shift
    // clear, and rightly: a menu click is an approximate gesture that landed
    // wherever the pointer happened to be. A drag is not approximate — it
    // says „from here to there" — so moving it somewhere else would be
    // answering a different question than the one asked.
    if (featureOverlaps(features, wall.index, offsetMm, lengthMm)) {
      onFeatureRefused({ wallIndex: wall.index, reason: "feature_overlaps" });
      return;
    }
    onFeatureRefused(null);
    setFeature({
      id: nextFeatureId(features),
      wallIndex: wall.index,
      offsetMm,
      lengthMm,
      ...featureTemplate,
    });
  }

  /**
   * The active template's own catalogue length, clamped to fit `wallLengthMm`
   * — the number a click or Enter reaches for when nothing else says how
   * long the object is (§6, „Klicken statt Ziehen: Standardlänge des Typs").
   * One shared lookup for `placeDoorCentred`, `doorHint` and `placeFeature`'s
   * click case (review round 1, task 9, minor 2) — each still does its own
   * placement math with the result; `placeFeature` clamps again around the
   * offset it was clicked at, which this cannot know.
   */
  function templateLengthMm(wallLengthMm: number): number {
    return Math.min(FEATURE_DEFAULTS[featureTemplate.featureType].defaultLengthMm, wallLengthMm);
  }

  /** A door by keyboard: centred on its wall (Entscheid Denis, 24.09.2026),
   * never at the corner where its leaf would reach the next wall. Click,
   * drag and the menu keep the position the person gave. */
  function placeDoorCentred(wall: WallView): void {
    if (wall.lengthMm === null) return;
    const lengthMm = templateLengthMm(wall.lengthMm);
    const offsetMm = centredFeatureOffsetMm(features, wall.index, wall.lengthMm, lengthMm);
    if (offsetMm === null) {
      onFeatureRefused({ wallIndex: wall.index, reason: "feature_overlaps" });
      return;
    }
    onFeatureRefused(null);
    setFeature({ id: nextFeatureId(features), wallIndex: wall.index, offsetMm, lengthMm, ...featureTemplate });
  }

  /**
   * The wall grip's a11y title where Enter would centre a door
   * (`placeDoorCentred`), so the announced outcome matches what Enter
   * actually does — not the wall's beginning, which `wall.free[0]` would
   * otherwise say for every type alike, and not silence where the door
   * cannot stand at all (review round 1, task 9, important 1: the generic
   * fallback used to describe a placement Enter would in fact refuse).
   * Always a string in the door case, never `null`; `null` only outside it,
   * so the caller knows to fall back to the grip's generic wording.
   */
  function doorHint(wall: WallView): string | null {
    if (tool !== "feature" || !SWING_TYPES.has(featureTemplate.featureType) || wall.lengthMm === null) {
      return null;
    }
    const lengthMm = templateLengthMm(wall.lengthMm);
    const offsetMm = centredFeatureOffsetMm(features, wall.index, wall.lengthMm, lengthMm);
    return offsetMm === null
      ? "Die Tür passt nicht auf diese Wand"
      : `Eingabetaste setzt die Tür mittig auf die Wand, bei ${formatMm(offsetMm)}`;
  }

  /**
   * Put one object at `offsetMm` over `lengthMm`, if it may lie there.
   *
   * The arithmetic is `settleFeature` in `lib/placement.ts` — **one** place
   * for the rule, shared with the typed „Anfang"/„Länge" fields of the side
   * panel, which went straight past it until round 2 of the walk
   * (30.08.2026). This function is what the board does with the answer.
   *
   * An `"unchanged"` answer still clears a standing refusal: a gesture that
   * comes back to where the object already sits is not a refusal, and a
   * message that outlives its own gesture describes nothing.
   */
  function changeFeatureTo(
    feature: FacadeFeature,
    wantedOffsetMm: number,
    wantedLengthMm: number,
    live: boolean,
  ): void {
    const wall = wallAt(feature.wallIndex);
    if (!wall || wall.lengthMm === null) return;
    const settled = settleFeature(
      features,
      feature,
      wantedOffsetMm,
      wantedLengthMm,
      wall.lengthMm,
    );
    if (settled.outcome === "overlaps") {
      onFeatureRefused({ wallIndex: feature.wallIndex, reason: "feature_overlaps" });
      return;
    }
    onFeatureRefused(null);
    if (settled.outcome === "unchanged") return;
    setFeature({ ...feature, offsetMm: settled.offsetMm, lengthMm: settled.lengthMm }, live);
    emitted.current = true;
  }

  function featurePointerDown(
    event: ReactPointerEvent<SVGElement>,
    feature: FacadeFeature,
    end: "whole" | "start" | "end",
  ): void {
    // With a tool up an object is transparent to the pointer and the wall
    // beneath takes the gesture — the same contract a placement keeps, so a
    // second object can be drawn beside the first. The guard repeats it for
    // environments that do not apply CSS.
    if (placing) return;
    onSelectFeature(feature.id);
    const wall = wallAt(feature.wallIndex);
    if (!wall || wall.lengthMm === null) return;
    beginGesture(event);
    setDrag({
      kind: end === "whole" ? "feature-move" : end === "start" ? "feature-start" : "feature-end",
      wallIndex: feature.wallIndex,
      pointerMm: pointerMmOn(wall, wall.lengthMm, event),
      id: feature.id,
      baseOffsetMm: feature.offsetMm,
      baseLengthMm: feature.lengthMm,
    });
  }

  function featurePointerMove(
    event: ReactPointerEvent<SVGElement>,
    feature: FacadeFeature,
  ): void {
    if (!drag || drag.id !== feature.id) return;
    const wall = wallAt(feature.wallIndex);
    if (!wall || wall.lengthMm === null) return;
    const pointerMm = pointerMmOn(wall, wall.lengthMm, event);
    const baseOffsetMm = drag.baseOffsetMm ?? feature.offsetMm;
    const baseLengthMm = drag.baseLengthMm ?? feature.lengthMm;
    const deltaMm = pointerMm - (drag.pointerMm ?? pointerMm);
    if (drag.kind === "feature-move") {
      changeFeatureTo(feature, baseOffsetMm + deltaMm, baseLengthMm, emitted.current);
      return;
    }
    if (drag.kind === "feature-start") {
      // The far end stands still; the near one follows the pointer.
      const endMm = baseOffsetMm + baseLengthMm;
      const startMm = Math.min(baseOffsetMm + deltaMm, endMm - GRID_MM);
      changeFeatureTo(feature, startMm, endMm - startMm, emitted.current);
      return;
    }
    if (drag.kind === "feature-end") {
      const endMm = Math.max(baseOffsetMm + baseLengthMm + deltaMm, baseOffsetMm + GRID_MM);
      changeFeatureTo(feature, baseOffsetMm, endMm - baseOffsetMm, emitted.current);
    }
  }

  function featureKeyDown(event: KeyboardEvent<SVGElement>, feature: FacadeFeature): void {
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      onDeleteFeature(feature.id);
      return;
    }
    const wall = wallAt(feature.wallIndex);
    const step = event.shiftKey ? COARSE_STEP_MM : STEP_MM;
    // The screen's axes, not the walk's — the same answer `placementKeyDown`
    // gives to the same key, and for the same reason: half of every closed
    // outline runs right-to-left.
    const forward = wall
      ? Math.abs(wall.along.x) > Math.abs(wall.along.y)
        ? wall.along.x > 0
        : wall.along.y > 0
      : true;
    const towardsEnd =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? forward
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? !forward
          : null;
    if (towardsEnd !== null) {
      // Zoomed, the same key also means "pan" to `boardKeyDown` further up
      // the bubble — `preventDefault` alone does not stop it from seeing the
      // event too, and a nudge plus a pan from one press moved two things at
      // once (review finding 1, schlussreview 10.09.2026). The board owns
      // the arrow keys once something might be off screen; an object keeps
      // them back only in the fit, where there is nothing to pan to anyway.
      if (zoomed) return;
      event.preventDefault();
      changeFeatureTo(
        feature,
        feature.offsetMm + (towardsEnd ? step : -step),
        feature.lengthMm,
        false,
      );
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      changeFeatureTo(feature, 0, feature.lengthMm, false);
      return;
    }
    if (event.key === "End" && wall?.lengthMm != null) {
      event.preventDefault();
      changeFeatureTo(feature, wall.lengthMm - feature.lengthMm, feature.lengthMm, false);
    }
  }

  /**
   * Whether the tool in hand puts something on a wall that a chamfer's slant
   * does not take — an object or a hole (design 2026-10-18 §6.3) — and if so,
   * says so. A grating it takes like any measured wall.
   */
  function refusedOnFase(wall: WallView): boolean {
    if (!wall.fase || (tool !== "feature" && tool !== "cutout")) return false;
    onFeatureRefused({ wallIndex: wall.index, reason: "fase_wall" });
    return true;
  }

  function wallPointerDown(event: ReactPointerEvent<SVGElement>, wall: WallView): void {
    if (!placing) {
      onSelectWall(wall.index);
      return;
    }
    if (refusedOnFase(wall)) return;
    if (tool === "cutout") {
      // Reached only where no band-hit lies under the pointer — a bare wall
      // the tool still lands on, at the wall line.
      if (!wall.accepts || wall.lengthMm === null) {
        report(wall.index, "wall_without_measure");
        return;
      }
      placeCutout(wall, pointerMmOn(wall, wall.lengthMm, event));
      return;
    }
    if (tool === "feature") {
      if (!wall.accepts || wall.lengthMm === null) {
        report(wall.index, "wall_without_measure");
        return;
      }
      beginGesture(event);
      setDrag({ kind: "feature", wallIndex: wall.index, pointerMm: pointerMmOn(wall, wall.lengthMm, event) });
      return;
    }
    beginGesture(event);
    setDrag({ kind: "place", wallIndex: wall.index });
    showPreview(wall, event);
  }

  function wallPointerMove(event: ReactPointerEvent<SVGElement>, wall: WallView): void {
    if (!placing) return;
    // A feature is sized by the drag itself, not by a preview at `newLengthMm`
    // — there is nothing to ghost before the gesture has an extent; a hole
    // is set on the click, and nothing about it is previewed either.
    if (tool === "feature" || tool === "cutout") return;
    if (drag && drag.kind === "move") return;
    showPreview(wall, event);
  }

  function wallPointerUp(event: ReactPointerEvent<SVGElement>, wall: WallView): void {
    endGesture(event);
    if (drag && drag.kind === "feature" && drag.wallIndex === wall.index) {
      setDrag(null);
      if (wall.lengthMm === null) return;
      const endMm = pointerMmOn(wall, wall.lengthMm, event);
      placeFeature(wall, drag.pointerMm ?? endMm, endMm);
      return;
    }
    if (!drag || drag.kind !== "place" || drag.wallIndex !== wall.index) {
      setDrag(null);
      return;
    }
    setDrag(null);
    if (ghost && ghost.wallIndex === wall.index) place(ghost.kind, wall.index, ghost.offsetMm);
  }

  function leaveWall(): void {
    if (drag) return;
    setGhost(null);
    setRefusal(null);
    report(null, null);
  }

  /**
   * The wall's own right-click — reachable everywhere the band is not, which
   * is only a bare stretch, but that is exactly the pixels the band's own
   * handler cannot reach (task 14).
   */
  function wallContextMenu(event: ReactMouseEvent<SVGElement>, wall: WallView): void {
    if (!wall.accepts || wall.lengthMm === null) return;
    event.preventDefault();
    onCornerMenu(wallMenuRequestAt(wall, null, event));
  }

  function wallKeyDown(event: KeyboardEvent<SVGElement>, wall: WallView): void {
    // Shift+F10 is the keyboard's own way to ask for a context menu — real
    // browsers turn it into a `contextmenu` event on their own, but that
    // conversion is a browser behaviour, not a DOM one, and jsdom does not
    // perform it: the board still has to answer the key itself, using the
    // focused wall's own box for where the menu should sit (task 14).
    if (event.key === "F10" && event.shiftKey) {
      event.preventDefault();
      if (!wall.accepts || wall.lengthMm === null) return;
      const box = event.currentTarget.getBoundingClientRect();
      onCornerMenu(
        wallMenuRequestAt(wall, null, {
          clientX: box.left + box.width / 2,
          clientY: box.top + box.height / 2,
        }),
      );
      return;
    }
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    if (!placing) {
      onSelectWall(wall.index);
      return;
    }
    if (!wall.accepts || wall.lengthMm === null) {
      report(wall.index, "wall_without_measure");
      return;
    }
    if (refusedOnFase(wall)) return;
    if (tool === "cutout") {
      // No drag on a keyboard, and no click either: the hole goes to the
      // wall's own beginning and the person walks it from there (§5.5.5).
      placeCutout(wall, 0);
      return;
    }
    if (tool === "feature") {
      // No drag on a keyboard, so there is nothing to size a facade object
      // by — a gesture with no extent, at the wall's own beginning, and
      // `placeFeature` answers that with the type's own catalogue length
      // (review round 1, 30.08.2026, important 2: a bare `GRID_MM` produced a
      // 10 mm Haustür, invisible and unselectable, while `defaultLengthMm`
      // sat unused). The figure is read there and not here, because the click
      // on the wall needs the same answer and had a different one until the
      // walk found it.
      //
      // A door is the one exception (Entscheid Denis, 24.09.2026): at the
      // wall's beginning its leaf swings onto the neighbouring wall, so
      // Enter centres it instead — `placeDoorCentred` names both the type
      // check and the reason in one place.
      if (SWING_TYPES.has(featureTemplate.featureType)) {
        placeDoorCentred(wall);
        return;
      }
      placeFeature(wall, 0, 0);
      return;
    }
    // The first stretch that can hold it, walking the wall from its beginning
    // — the keyboard's answer to „the nearest allowed spot" (§5.5.5).
    const kind = PLACEMENT_OF_TOOL[tool];
    if (kind === null) return;
    const room = wall.free.find((stretch) => stretch.lengthMm >= newLengthMm);
    if (!room) {
      report(wall.index, "wall_has_no_room");
      return;
    }
    place(kind, wall.index, room.offsetMm);
  }

  function placementPointerDown(event: ReactPointerEvent<SVGElement>, placement: Placement): void {
    // With a tool up, a placed element is transparent to the pointer and the
    // wall beneath takes the gesture — that is how a second element is butted
    // against the first. The guard repeats it for environments that do not
    // apply CSS.
    if (placing) return;
    onSelect(placement.id);
    const wall = wallAt(placement.wallIndex);
    // No asserted measure, no ruler to drag along — and no element drawn there
    // either, so this is unreachable from the drawing rather than merely
    // guarded.
    if (!wall || wall.lengthMm === null) return;
    beginGesture(event);
    setDrag({
      kind: "move",
      wallIndex: wall.index,
      pointerMm: pointerMmOn(wall, wall.lengthMm, event),
      id: placement.id,
      baseOffsetMm: placement.offsetMm,
    });
  }

  function placementPointerMove(event: ReactPointerEvent<SVGElement>, placement: Placement): void {
    if (!drag || drag.kind !== "move" || drag.id !== placement.id) return;
    const wall = wallAt(placement.wallIndex);
    if (!wall || wall.lengthMm === null) return;
    const pointerMm = pointerMmOn(wall, wall.lengthMm, event);
    moveTo(placement, (drag.baseOffsetMm ?? 0) + (pointerMm - (drag.pointerMm ?? 0)), emitted.current);
  }

  /**
   * Put one element at `wantedMm`, if it may lie there.
   *
   * Goes through the same snap as the pointer, with no radius: a keyboard step
   * is ten millimetres and has to stay ten millimetres, but it still may not
   * walk into a neighbour or off the end — and `snapOffset` answers `null`
   * where it would.
   */
  function moveTo(placement: Placement, wantedMm: number, live: boolean): void {
    const others = placements.filter((each) => each.id !== placement.id);
    const snap = snapOffset(
      outline,
      others,
      placement.wallIndex,
      wantedMm,
      placement.lengthMm,
      0,
    );
    if (!snap) return;
    onMove(placement.id, snap.offsetMm, live);
    emitted.current = true;
  }

  function placementKeyDown(event: KeyboardEvent<SVGElement>, placement: Placement): void {
    const wall = wallAt(placement.wallIndex);
    const step = event.shiftKey ? COARSE_STEP_MM : STEP_MM;

    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      onDelete(placement.id);
      return;
    }
    /**
     * Arrow keys move the element the way the **screen** points, not the way
     * the walk runs.
     *
     * A rectangle is walked clockwise, so its third wall runs right-to-left
     * and its fourth bottom-to-top. Adding to `offsetMm` on those sends
     * ArrowRight leftwards and ArrowDown upwards — on half of every closed
     * outline the keyboard fought the picture, while `aria-orientation` said
     * „horizontal" all the same. `OutlineDraft.nudge` has always reckoned in
     * screen axes; this is the same answer to the same key (found in review,
     * 16.08.2026).
     */
    const forward = wall ? (Math.abs(wall.along.x) > Math.abs(wall.along.y)
      ? wall.along.x > 0
      : wall.along.y > 0) : true;
    const towardsEnd =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? forward
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? !forward
          : null;
    if (towardsEnd !== null) {
      // Same conflict `featureKeyDown` guards against, same fix: zoomed, the
      // board pans on this key too (`boardKeyDown`, bubbling up), and
      // `preventDefault` here would not stop it from also seeing the event
      // (review finding 1, schlussreview 10.09.2026).
      if (zoomed) return;
      event.preventDefault();
      moveTo(placement, placement.offsetMm + (towardsEnd ? step : -step), false);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      moveTo(placement, 0, false);
      return;
    }
    if (event.key === "End" && wall?.lengthMm != null) {
      event.preventDefault();
      moveTo(placement, wall.lengthMm - placement.lengthMm, false);
    }
  }

  /**
   * One free hole on `wall`, its middle `alongMm` from the wall's start.
   *
   * Along the click, snapped to the grid; across, the **middle of the band**
   * (ruling 9, design §8) — the across position is what a person then drags
   * (D-3: you grab the middle). The band is the element's under the click,
   * or the job's where none lies: a hole is anchored to the wall (§4), and a
   * click on a bare stretch is still a click on this wall.
   */
  function placeCutout(wall: WallView, alongMm: number): Cutout | null {
    if (wall.lengthMm === null) return null;
    const carrier = carrierAt(placements, wall.index, alongMm);
    const { offsetMm } = floorCutout(snapCutout(alongMm, 0).offsetMm, 0);
    const cutout = freeCutoutAt(
      wall.index,
      offsetMm,
      bandWidthMm(carrier, specs, jobGratingType),
      nextCutoutId(cutouts),
    );
    onPlaceCutout(cutout);
    return cutout;
  }

  /**
   * The cutout tool on the band: **set and take hold in one gesture** (walk
   * B-1). The hole is set at the click and hangs on the pointer from the
   * same down — the tool's own hint promises „klicken, dann ziehen", and
   * before the walk the second half set a second hole exactly on the first.
   *
   * The set is the undo entry. `emitted` is raised before the first frame so
   * the drag folds into it (`live=true` from the start) rather than opening
   * a second step; a click that never moves settles nothing and stays one
   * step, one op.
   */
  function bandPointerDown(event: ReactPointerEvent<SVGElement>, wall: WallView): void {
    if (tool !== "cutout" || wall.lengthMm === null) return;
    event.stopPropagation();
    const alongMm = pointerMmOn(wall, wall.lengthMm, event);
    const cutout = placeCutout(wall, alongMm);
    if (!cutout) return;
    beginGesture(event);
    emitted.current = true;
    holdCutout(event, wall, cutout, carrierAt(placements, wall.index, alongMm));
  }

  /** A frame of a drag that began on the band, not on the mark: the pointer
   * is captured by the band hit area, so the frames arrive here and are
   * handed to the hole the drag names. */
  function bandPointerMove(event: ReactPointerEvent<SVGElement>): void {
    if (drag?.kind !== "cutout-move") return;
    const cutout = cutouts.find((each) => each.id === drag.id);
    if (!cutout) return;
    cutoutPointerMove(
      event,
      cutout,
      carrierAt(placements, cutout.wallIndex, cutoutAlongMm(cutout, features)),
    );
  }

  /**
   * Somebody took hold of a hole: select it and open the gesture.
   *
   * With a placing tool up the hole is transparent to the pointer, like
   * every other mark on a band, so a second element can be laid beside it.
   * **Not under the cutout tool** (walk B-1, correcting ruling 11): there a
   * mark is the thing a person drags, and a down on it must never set a
   * second hole underneath. The guard repeats the CSS rule for environments
   * that do not apply it.
   */
  function cutoutPointerDown(
    event: ReactPointerEvent<SVGElement>,
    cutout: Cutout,
    carrier: Placement | null,
  ): void {
    if (placing && tool !== "cutout") return;
    onSelectCutout(cutout.id);
    const wall = wallAt(cutout.wallIndex);
    if (!wall || wall.lengthMm === null) return;
    beginGesture(event);
    holdCutout(event, wall, cutout, carrier);
  }

  /**
   * Open the drag on a hole.
   *
   * The grab records where the pointer took the hole on **both** axes —
   * along the wall (`pointerMmOn`) and across the band (`pointerAcrossOn`),
   * the latter at the band's own scale — so every later frame can be
   * measured from that one start (the rule {@link Drag} states).
   */
  function holdCutout(
    event: ReactPointerEvent<SVGElement>,
    wall: WallView,
    cutout: Cutout,
    carrier: Placement | null,
  ): void {
    if (wall.lengthMm === null) return;
    lastCutoutFrame.current = null;
    setDrag({
      kind: "cutout-move",
      wallIndex: wall.index,
      id: cutout.id,
      pointerMm: pointerMmOn(wall, wall.lengthMm, event),
      pointerAcrossMm: pointerAcrossOn(wall, event, bandOf(carrier)),
      baseOffsetMm: cutoutAlongMm(cutout, features),
      baseAcrossMm: cutout.acrossMm,
    });
  }

  /**
   * One frame of the drag: the hole under the pointer, on both axes.
   *
   * Snapped to the grid and **not clamped into the band** — a hole keeps its
   * true place, and a finding says what does not fit (design §8); the one
   * floor is {@link floorCutout}'s.
   *
   * A frame that moves the hole nowhere is not reported. The first reported
   * frame opens the undo entry (`live=false`) and every later one folds into
   * it — reporting an unchanged first frame would open no entry on the
   * screen (see `moveCutout` there) and the next frame would fold into
   * whatever stood before the drag.
   */
  function cutoutPointerMove(
    event: ReactPointerEvent<SVGElement>,
    cutout: Cutout,
    carrier: Placement | null,
  ): void {
    if (!drag || drag.kind !== "cutout-move" || drag.id !== cutout.id) return;
    const wall = wallAt(cutout.wallIndex);
    if (!wall || wall.lengthMm === null) return;
    const alongMm = pointerMmOn(wall, wall.lengthMm, event);
    const acrossMm = pointerAcrossOn(wall, event, bandOf(carrier));
    const wantedAlong = (drag.baseOffsetMm ?? 0) + (alongMm - (drag.pointerMm ?? 0));
    const wantedAcross = (drag.baseAcrossMm ?? 0) + (acrossMm - (drag.pointerAcrossMm ?? 0));
    const snapped = snapCutout(wantedAlong, wantedAcross);
    const floored = floorCutout(snapped.offsetMm, snapped.acrossMm);
    // A linked hole has no position of its own along the wall (§4): only
    // the across figure is its to give.
    const frame = {
      id: cutout.id,
      offsetMm: cutout.featureId === null ? floored.offsetMm : cutout.offsetMm,
      acrossMm: floored.acrossMm,
    };
    const last = lastCutoutFrame.current ?? {
      id: cutout.id,
      offsetMm: cutout.offsetMm,
      acrossMm: cutout.acrossMm,
    };
    if (frame.offsetMm === last.offsetMm && frame.acrossMm === last.acrossMm) return;
    lastCutoutFrame.current = frame;
    onMoveCutout(frame.id, frame.offsetMm, frame.acrossMm, emitted.current);
    emitted.current = true;
  }

  /**
   * The grab ends: settle the hole where the last frame left it.
   *
   * The drag's frames already stand on the stack as one folded entry; the
   * settling call carries the same figures with `live=false` so the screen
   * announces the op for the final position (it pushes nothing for an
   * unchanged position — see `moveCutout` there). A grab that never moved
   * settles nothing: a click on a hole is a selection, not a move.
   */
  function cutoutPointerUp(event: ReactPointerEvent<SVGElement>): void {
    endGesture(event);
    if (drag?.kind !== "cutout-move") return;
    setDrag(null);
    const frame = lastCutoutFrame.current;
    lastCutoutFrame.current = null;
    if (frame) onMoveCutout(frame.id, frame.offsetMm, frame.acrossMm, false);
  }

  /**
   * The keyboard on a hole: `←`/`→` along the wall, `↑`/`↓` across the band,
   * `STEP_MM` a press and `COARSE_STEP_MM` with Shift; Delete takes it away.
   *
   * **The band's axes, not the screen's.** An element and an object read the
   * arrows by the screen (`placementKeyDown`), because along the wall is the
   * only axis they have. A hole has two, and `↑`/`↓` are the second: `↑` is
   * „von der Fassade weg" on every wall, whichever way that wall points —
   * the figure the label reads „ab Rostkante". A linked hole moves across
   * only; along, it is where its object is (§4).
   *
   * Zoomed, the arrows belong to the pan — the same rule an element and an
   * object keep, for the same reason: `boardKeyDown` sees the event too, and
   * a nudge plus a pan from one press moves two things at once.
   */
  function cutoutKeyDown(event: KeyboardEvent<SVGElement>, cutout: Cutout): void {
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      // The mark goes with the hole, and focus went with it — to `body`,
      // and whoever works by keyboard had to tab back in (walk B-9). The
      // carrier's grip is the natural next stop: the sheet the hole was
      // drilled into, still there; the board itself where none is found.
      const carrier = carrierAt(placements, cutout.wallIndex, cutoutAlongMm(cutout, features));
      const grip = carrier
        ? Array.from(
            svgRef.current?.querySelectorAll<SVGElement>('[data-role="placement-grip"]') ?? [],
          ).find((each) => each.getAttribute("data-placement") === carrier.id)
        : undefined;
      (grip ?? svgRef.current)?.focus();
      onDeleteCutout(cutout.id);
      return;
    }
    if (!event.key.startsWith("Arrow")) return;
    if (zoomed) return;
    const step = event.shiftKey ? COARSE_STEP_MM : STEP_MM;
    const along = cutoutAlongMm(cutout, features);
    const move = (dAlong: number, dAcross: number) => {
      event.preventDefault();
      if (cutout.featureId !== null && dAlong !== 0) return;
      const floored = floorCutout(along + dAlong, cutout.acrossMm + dAcross);
      const offsetMm = cutout.featureId === null ? floored.offsetMm : cutout.offsetMm;
      // A press that moves the hole nowhere — `↓` at the near edge — reports
      // nothing, as the pointer path does: the screen would announce a
      // `set_cutout` for a hole that did not move.
      if (offsetMm === cutout.offsetMm && floored.acrossMm === cutout.acrossMm) return;
      onMoveCutout(cutout.id, offsetMm, floored.acrossMm, false);
    };
    if (event.key === "ArrowRight") move(step, 0);
    else if (event.key === "ArrowLeft") move(-step, 0);
    else if (event.key === "ArrowUp") move(0, step);
    else if (event.key === "ArrowDown") move(0, -step);
  }

  /**
   * Escape belongs to the gesture while one runs, and to the toolbar
   * otherwise (§5.3) — so it is only swallowed while there is something to
   * abandon.
   *
   * Beyond Escape this is the keyboard side of the zoom bar: every mouse
   * gesture on this board has a keyboard equivalent, by house rule, and
   * `+`/`=`, `-`, `0` and the arrow keys are answered here — the same way
   * {@link wallKeyDown}/{@link placementKeyDown} answer for a single wall or
   * element. With Strg/⌘ held the same three keys do the same thing, and
   * the browser's page zoom they would otherwise trigger is kept out
   * (`preventDefault`, A3 23.09.2026).
   */
  function boardKeyDown(event: KeyboardEvent<SVGElement>): void {
    if (event.key === "Escape" && drag) {
      event.stopPropagation();
      setDrag(null);
      setGhost(null);
      setRefusal(null);
      return;
    }
    if (!onView) return;
    const centre = boardCentreMm();
    if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      zoom(ZOOM_STEP, centre);
    } else if (event.key === "-") {
      event.preventDefault();
      zoom(1 / ZOOM_STEP, centre);
    } else if (event.key === "0") {
      event.preventDefault();
      onView(FIT_VIEW);
    } else if (view.centreMm !== null && event.key.startsWith("Arrow")) {
      // Only once something is off screen — in the fit there is nothing to
      // pan to, and the arrow keys stay free for whatever else wants them.
      event.preventDefault();
      if (event.key === "ArrowRight") pan(1, 0);
      else if (event.key === "ArrowLeft") pan(-1, 0);
      else if (event.key === "ArrowDown") pan(0, 1);
      else if (event.key === "ArrowUp") pan(0, -1);
    }
  }

  /**
   * Strg/⌘ + Plus/Minus/0 while the board fills the window — from anywhere on
   * the page, not only with the board focused (A3, Entscheid Denis
   * 23.09.2026). Maximized, the page *is* the drawing, and the browser's own
   * zoom would only blow up the tools around it.
   *
   * On `document`, bubbling: a press on the focused board has already been
   * answered by {@link boardKeyDown}, which prevents the default, and is
   * skipped here on exactly that — one key, one zoom step. The wheel is not
   * listened to at all any more; it scrolls the page, and a pinch is the
   * browser's.
   */
  useEffect(() => {
    if (!maximized) return;
    function onKey(event: globalThis.KeyboardEvent): void {
      if (event.defaultPrevented || !(event.ctrlKey || event.metaKey)) return;
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        zoom(ZOOM_STEP, boardCentreMm());
      } else if (event.key === "-") {
        event.preventDefault();
        zoom(1 / ZOOM_STEP, boardCentreMm());
      } else if (event.key === "0") {
        event.preventDefault();
        onView?.(FIT_VIEW);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  /**
   * Pan the view — middle button, or left button with space held — ahead of
   * every other pointer gesture on the board.
   *
   * Attached natively in the **capture** phase, on `svgRef.current` itself,
   * rather than as a check repeated in `wallPointerDown`/`wallPointerMove`
   * and copied again into `placementPointerDown`/`featurePointerDown` (fix
   * round 1, review of 10.09.2026: the first cut missed exactly those two,
   * and the room's open interior — anywhere past a wall's own grab band —
   * had no pointer handler to carry a copy in at all). Capture runs before
   * any React handler on a descendant, wherever that descendant sits in the
   * tree; a recognised pan calls `stopPropagation` so none of them see the
   * gesture, and `preventDefault` on the down so a real middle click does
   * not also trigger the browser's own autoscroll. That is also why the old
   * per-wall check is gone rather than left beside this one — two paths for
   * the same gesture is exactly the drift this fix removes.
   *
   * `panFrom` — not `view.centreMm !== null` alone — is what `handleMove`/
   * `handleEnd` test, because a middle click before anything is zoomed in
   * is swallowed (no tool fires) without becoming a pan (nothing to pan
   * from yet); the down handler still leaves `panFrom.current` `null` then,
   * and the other two are no-ops until a later gesture actually sets it.
   *
   * `setPointerCapture` on the down, mirroring {@link beginGesture} for every
   * other gesture on this board (fix round 2, walk-through 10.09.2026,
   * Befund 4). Without it a release over the zoom bar — which overlaps the
   * board visually but sits beside it in the DOM, not inside it — never
   * reaches this element at all: `handleEnd` is bound here in the capture
   * phase, which only ever runs for events whose target is `element` itself
   * or a descendant of it. `panFrom` was then left set, and every later plain
   * pointer move — no button held, nowhere near a drag — kept reading that
   * stale start and panned the board out from under it until the next click
   * anywhere on the board happened to call `handleEnd`. Capture makes this
   * element the target of the release regardless of what is visually under
   * the pointer, the same guarantee the per-element drags already rely on.
   * `pointercancel` already cleared `panFrom` before this fix; `lostpointercapture`
   * is the one other way a capture can end without this element ever seeing
   * a release of its own (another element capturing the same pointer, the
   * browser reclaiming it), and needs the same reset.
   */
  useEffect(() => {
    const element = svgRef.current;
    if (!element) return;

    // `const` arrow functions below, not `function` declarations: TypeScript keeps the `element` guard's narrowing
    // (`SVGSVGElement`, not `SVGSVGElement | null`) in a closure only for a
    // `const` never reassigned — a hoisted function declaration loses it,
    // because its body is reachable from the top of this scope, before the
    // guard has necessarily run. `handleMove` does not read `element` and
    // would type-check as a declaration too, but one shape for all three
    // beats a reader wondering why only two of them differ.
    const handleDown = (event: globalThis.PointerEvent): void => {
      if (event.button !== 1 && !spaceHeld.current) return;
      event.preventDefault();
      event.stopPropagation();
      if (view.centreMm === null) return;
      panFrom.current = { x: event.clientX, y: event.clientY, centre: view.centreMm };
      // Best effort, same fallback as `beginGesture`: a pointer id the
      // browser does not consider active loses nothing but the capture.
      try {
        element.setPointerCapture(event.pointerId);
      } catch {
        /* no capture; the gesture runs without it */
      }
    };

    const handleMove = (event: globalThis.PointerEvent): void => {
      const from = panFrom.current;
      if (!from) return;
      event.stopPropagation();
      if (!onView) return;
      onView({
        ...view,
        centreMm: {
          xMm: from.centre.xMm - (event.clientX - from.x) / projection.scale,
          yMm: from.centre.yMm - (event.clientY - from.y) / projection.scale,
        },
      });
    };

    const handleEnd = (event: globalThis.PointerEvent): void => {
      if (!panFrom.current) return;
      event.stopPropagation();
      panFrom.current = null;
      try {
        if (element.hasPointerCapture(event.pointerId)) {
          element.releasePointerCapture(event.pointerId);
        }
      } catch {
        /* never captured; nothing to release */
      }
    };

    element.addEventListener("pointerdown", handleDown, { capture: true });
    element.addEventListener("pointermove", handleMove, { capture: true });
    element.addEventListener("pointerup", handleEnd, { capture: true });
    // A cancelled gesture (lost capture, an interrupting browser gesture)
    // has to clear `panFrom` exactly like a normal release — otherwise every
    // later pointer move on the board keeps reading a stale start and keeps
    // stealing the gesture from whatever tool is actually in hand.
    element.addEventListener("pointercancel", handleEnd, { capture: true });
    // The other way a capture can end without a release of its own ever
    // reaching this element — see the docstring above.
    element.addEventListener("lostpointercapture", handleEnd, { capture: true });
    return () => {
      element.removeEventListener("pointerdown", handleDown, { capture: true });
      element.removeEventListener("pointermove", handleMove, { capture: true });
      element.removeEventListener("pointerup", handleEnd, { capture: true });
      element.removeEventListener("pointercancel", handleEnd, { capture: true });
      element.removeEventListener("lostpointercapture", handleEnd, { capture: true });
    };
  }, [view, onView, projection.scale]);

  /**
   * Tracks {@link spaceHeld}. `keydown`/`keyup` on the SVG element itself and
   * not `window` — the board is not the only screen that might be listening
   * for a space bar (correction to this task's own brief, 10.09.2026).
   *
   * `blur` on `window`, not on the element: Alt-Tab (or any other way of
   * taking the window's own focus) fires no DOM `blur`/`focusout` on the
   * element at all — `document.activeElement` does not change, only the
   * window stops being the focused one — so a listener placed on the SVG
   * would never see it. Only `keyup` cleared {@link spaceHeld} before this
   * fix, and a `keyup` for a key that is still physically down never
   * arrives once the window has lost focus: the flag stayed `true`, and
   * every later left click became a pan (review finding 2, schlussreview
   * 10.09.2026 — the same failure shape as walk-through Befund 4, a gesture
   * outliving the input that started it, on the keyboard's axis instead of
   * the pointer's). `visibilitychange` was considered and left out: window
   * `blur` already covers the named failure (Alt-Tab), and a second listener
   * for a case this one already catches would be an unexplained
   * belt-and-braces.
   */
  useEffect(() => {
    const element = svgRef.current;
    if (!element) return;
    function handleKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === " ") spaceHeld.current = true;
    }
    function handleKeyUp(event: globalThis.KeyboardEvent): void {
      if (event.key === " ") spaceHeld.current = false;
    }
    function handleWindowBlur(): void {
      spaceHeld.current = false;
    }
    element.addEventListener("keydown", handleKeyDown);
    element.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      element.removeEventListener("keydown", handleKeyDown);
      element.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  const issues = new Set(issueIds);
  /** The drawn width of an element's band, in pixels — what `PlacedThing`
   * draws it with and what its figures stand off from. */
  const widthPxOf = (placement: Placement): number =>
    (effectiveWidthMm(placement, specs, jobGratingType) ?? (jobSpec?.widthMm ?? 0)) * projection.scale;
  // The type's own width, ignoring any special width this one element
  // carries — the outer rail hangs off this figure so it stays one straight
  // line across every element built to this wall's type, whatever a single
  // element is narrowed or widened to (design §3, 30.08.2026).
  const typeWidthPxOf = (placement: Placement): number => widthPxOf({ ...placement, widthMm: null });
  // Where the screen's figures stand (UX-Gutachten 2, B-2). A sheet packs its
  // own (`SheetLabels`, the schema's summaries).
  const laidOut: ReadingLayout = sheet
    ? { summarised: new Set(), lengths: new Map(), blanks: new Map() }
    : layOutReadings({
        walls,
        placements,
        issues,
        zoomed,
        blankOf: (placement) => cuts.get(placement.id)?.blankMm ?? placement.lengthMm,
        // Half the growth on top of the band's own half width: the figures
        // then stand where a type band's would, moved out with the outer edge.
        halfWidthOf: (placement) =>
          bandHalves(widthPxOf(placement), typeWidthPxOf(placement), zoomed, placement.widensOutward ?? false)
            .halfWidth +
          growthPxOf(placement) / 2,
        summaryOffsetOf: (wallIndex) => SUMMARY_OFFSET + summaryPushPxOf(wallIndex),
        pointOn,
      });
  // Nothing moves under the pointer (design 2026-10-24 §7; final review,
  // M-3): while a gesture runs, the wall under it keeps the figures it had
  // when the gesture began — an element pushed against its neighbour must
  // not flip the wall from its elements' figures to its summary, or send a
  // figure a lane further out, under the hand that pushes it. The layout is
  // taken at the gesture's first render, before anything moved, and given
  // back on release, where the wall lays itself out again.
  const draggedWall = drag?.wallIndex ?? null;
  if (draggedWall === null ? heldReadings !== null : heldReadings?.wallIndex !== draggedWall) {
    setHeldReadings(draggedWall === null ? null : wallReadings(laidOut, draggedWall, placements));
  }
  const readings =
    heldReadings !== null && heldReadings.wallIndex === draggedWall
      ? holdWallReadings(laidOut, heldReadings, placements)
      : laidOut;
  // Read off the drawn scale so the bar states the truth about what is on
  // screen — but measured against the same 120 px it always was, which is why
  // `scaleBarMm` grew steps below half a metre (design §7.1).
  const scaleMm = scaleBarMm(projection.scale);

  return (
    <div className={styles.frame} data-maximized={maximized} data-sheet={sheet?.detail}>
      <SheetCanvas on={sheet !== undefined}>
      <svg
      ref={svgRef}
      className={styles.board}
      viewBox={`0 0 ${boardWidth} ${boardHeight}`}
      role="group"
      aria-label={`Roste planen, ${walls.length} Wände`}
      data-role="plan-board"
      // The drawing is a sheet, paper in the dark too (design 2026-10-24
      // §5.3); the zoom bar over it and the legend under it follow the mode
      // (§8.3), so the sheet is the SVG and not the frame. A drawing of
      // ours on warm paper (B-20) — on the print sheet the sheet's own white.
      data-blatt={sheet ? "" : "zeichnung"}
      data-tool={tool}
      data-scale={projection.scale}
      data-fit-scale={projection.fitScale}
      data-tolerance-mm={toleranceMm}
      // What a new placement of the current tool would be — the screen decides
      // it, and it is on the element so a test can see the difference between
      // „a rost" and „an interruption" without simulating a pointer.
      data-new-length-mm={newLengthMm}
      data-walls={walls.length}
      // Focusable in its own right: the zoom keys and the arrow-key pan are
      // answered here, not only on the walls and the elements standing on
      // them, which already carried their own tab stop.
      tabIndex={0}
      onKeyDown={boardKeyDown}
      // The bare board itself, not anything drawn on it: every wall, band and
      // element has a handler of its own, and a pan (middle button, or space
      // held) is stopped before it gets here.
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget || event.button !== 0 || placing) return;
        onClearPick?.();
      }}
    >
      <defs>
        {/* The mesh of the product itself, at 22° — the same motif the brand
            uses, in grey: a wall that cannot take an element breaks no rule. */}
        <pattern
          id={`${patternPrefix}-mesh`}
          width="7"
          height="7"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(22)"
        >
          <path className={styles.meshLine} d="M0,0 L0,7 M0,0 L7,0" />
        </pattern>
        <pattern
          id={`${patternPrefix}-area`}
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(22)"
        >
          <path className={styles.areaLine} d="M0,0 L0,6" />
        </pattern>
      </defs>

      {/* Context hatching lies under everything and goes into no list. */}
      {placements
        .filter((placement) => placement.kind === "area")
        .map((placement) => {
          const wall = wallAt(placement.wallIndex);
          if (!wall || wall.lengthMm === null || !placement.depthMm) return null;
          const depth = placement.depthMm * projection.scale;
          const start = pointOn(wall, placement.offsetMm);
          const end = pointOn(wall, placement.offsetMm + placement.lengthMm);
          const outerStart = shift(start, wall.outward, depth);
          const outerEnd = shift(end, wall.outward, depth);
          return (
            <polygon
              key={`area-${placement.id}`}
              className={styles.area}
              data-role="area-hatch"
              data-placement={placement.id}
              fill={`url(#${patternPrefix}-area)`}
              points={`${start.x},${start.y} ${end.x},${end.y} ${outerEnd.x},${outerEnd.y} ${outerStart.x},${outerStart.y}`}
            />
          );
        })}

      {walls.map((wall) => {
        const accepts = wall.accepts;
        const gratings = placements.filter(
          (placement) => placement.wallIndex === wall.index && placement.kind === "grating",
        );
        const refusing = refusal?.wallIndex === wall.index ? refusal.reason : null;
        // A wall without a measure has no free stretch to speak of — the mesh
        // then covers the whole of it, which is exactly the statement.
        const meshed: FreeStretch[] = accepts
          ? wall.free
          : [{ offsetMm: 0, lengthMm: 1 }];
        const doorTitle = doorHint(wall);

        return (
          <g key={`wall-${wall.index}`} data-role="plan-wall" data-wall={wall.index}>
            {selectedWall === wall.index ? (
              <line
                className={styles.wallSelected}
                x1={wall.from.x}
                y1={wall.from.y}
                x2={wall.to.x}
                y2={wall.to.y}
              />
            ) : null}
            <line
              className={accepts ? styles.wall : styles.wallWithoutMeasure}
              data-role="wall-line"
              data-wall={wall.index}
              data-accepts={accepts}
              x1={wall.from.x}
              y1={wall.from.y}
              x2={wall.to.x}
              y2={wall.to.y}
            />

            {refusing
              ? meshed.map((stretch, stretchIndex) => {
                  const start = accepts
                    ? pointOn(wall, stretch.offsetMm)
                    : wall.from;
                  const end = accepts
                    ? pointOn(wall, stretch.offsetMm + stretch.lengthMm)
                    : wall.to;
                  const outerStart = shift(start, wall.outward, REFUSAL_DEPTH_PX);
                  const outerEnd = shift(end, wall.outward, REFUSAL_DEPTH_PX);
                  return (
                    <polygon
                      key={`refusal-${stretchIndex}`}
                      className={styles.refusal}
                      data-role="wall-refusal"
                      data-wall={wall.index}
                      data-reason={refusing}
                      fill={`url(#${patternPrefix}-mesh)`}
                      points={`${start.x},${start.y} ${end.x},${end.y} ${outerEnd.x},${outerEnd.y} ${outerStart.x},${outerStart.y}`}
                    />
                  );
                })
              : null}

            {/* Fitted only: `SUMMARY_OFFSET` sits the summary at a fixed
                pixel distance from the wall, and a magnified band can reach
                past that distance on its own (`bandHalfWidth` sheds its
                ceiling once zoomed, D-7) — the summary would then sit *inside*
                the band, crossed by its own hatching, in exactly the six-
                element case it exists to read (task 8, design 2026-09-10).
                Same gate as `summarised` below, so the two never disagree
                about which of them is showing a figure for this wall. */}
            {/* On a sheet the rule is the detail's, not the element count's:
                the schema sheet states every wall's division in one line,
                the plan sheet names every element on its own instead. */}
            {(sheet ? sheet.detail === "schema" && gratings.length > 0 : readings.summarised.has(wall.index)) ? (
              <WallSummary
                wall={wall}
                gratings={gratings}
                pointOn={pointOn}
                offset={summaryOffsets?.get(wall.index) ?? SUMMARY_OFFSET + summaryPushPxOf(wall.index)}
              />
            ) : null}

            {sheet ? (
              <SheetWallDimension
                wall={wall}
                markSet={sheet.markSetMeasures === true}
                swings={features
                  .filter((each) => each.wallIndex === wall.index && SWING_TYPES.has(each.featureType))
                  .map((each) => [each.offsetMm, each.offsetMm + each.lengthMm])}
              />
            ) : (
              <WallDimension wall={wall} />
            )}

            <line
              className={styles.wallGrip}
              data-role="wall-grip"
              data-wall={wall.index}
              data-testid={`wall-${wall.index}`}
              data-accepts={accepts}
              // Two different refusals, one cursor: a wall without a measure
              // can never take one, a full wall cannot take one *here*. Both
              // have to say so before the click rather than after it.
              data-refusing={refusing !== null}
              x1={wall.from.x}
              y1={wall.from.y}
              x2={wall.to.x}
              y2={wall.to.y}
              strokeWidth={GRAB_HALF_WIDTH * 2}
              role="button"
              tabIndex={0}
              aria-disabled={!accepts}
              aria-label={
                accepts
                  ? `Wand ${wall.index + 1}${wall.fase ? " (Fase)" : ""}, ${
                      wall.shortened ?? formatMm(wall.lengthMm)
                    }, ${formatMm(wall.freeMm)} frei`
                  : `Wand ${wall.index + 1}, kein Maß`
              }
              onFocus={() => onSelectWall(wall.index)}
              onPointerDown={(event) => wallPointerDown(event, wall)}
              onPointerMove={(event) => wallPointerMove(event, wall)}
              onPointerUp={(event) => wallPointerUp(event, wall)}
              onPointerCancel={(event) => wallPointerUp(event, wall)}
              onPointerLeave={leaveWall}
              onKeyDown={(event) => wallKeyDown(event, wall)}
              onContextMenu={(event) => wallContextMenu(event, wall)}
            >
              {/* The door case names its own outcome — centred, or refused —
                  whether or not `wall.free` (grating's own free stretches,
                  unrelated to a facade object) happens to be empty; every
                  other type keeps the older, `wall.free`-gated wording. */}
              {accepts
                ? doorTitle !== null
                  ? <title>{doorTitle}</title>
                  : wall.free.length > 0
                    ? <title>{`Eingabetaste setzt ein Element bei ${formatMm(wall.free[0]!.offsetMm)}`}</title>
                    : null
                : null}
            </line>

            {/* Only with the cutout tool up: the one tool that has to land
                on the band and not on the wall line the grip listens on
                (design §7.1). Drawn on the job's band, not an element's — a
                hole is anchored to the wall (§4), and where no element lies
                the click is still a click on this wall. */}
            {tool === "cutout" && accepts && wall.lengthMm !== null && !wall.fase ? (
              <polygon
                data-role="band-hit"
                data-wall={wall.index}
                className={styles.bandHit}
                points={bandHitPoints(wall, hitBandOf(wall))}
                onPointerDown={(event) => bandPointerDown(event, wall)}
                onPointerMove={bandPointerMove}
                onPointerUp={cutoutPointerUp}
                onPointerCancel={cutoutPointerUp}
              />
            ) : null}
          </g>
        );
      })}

      {placements.map((placement) => {
        const wall = wallAt(placement.wallIndex);
        // A placement on a wall nobody measured is not drawn, because there is
        // no ruler to draw it against — its offset would have to be read off a
        // sketched distance, which is the one thing A-014 forbids. It is also
        // a state the endpoint refuses outright (§3.5.1, left column), so the
        // screen is showing an error rather than a plan when it happens; a
        // *wrong* plan, by contrast, is drawn in full and on purpose (E-13).
        if (!wall || wall.lengthMm === null) return null;
        return (
          <PlacedThing
            key={placement.id}
            placement={placement}
            wall={wall}
            from={pointOn(wall, placement.offsetMm)}
            to={pointOn(wall, placement.offsetMm + placement.lengthMm)}
            // An interruption has no width of its own — `effectiveWidthMm`
            // answers `null` for it, and 0 made a downpipe six pixels tall
            // beside grating that stands fifteen out from the wall: it read as
            // a thickening of the line, not as an opening. It gets the width
            // of the grating it interrupts, so the opening convention is drawn
            // exactly as tall as the thing it interrupts, at any scale (found
            // in review, 16.08.2026).
            widthPx={widthPxOf(placement)}
            typeWidthPx={typeWidthPxOf(placement)}
            typeName={typeNameOf(placement, jobGratingType)}
            selected={selected === placement.id}
            issue={issues.has(placement.id)}
            cutStart={cuts.get(placement.id)?.start ?? "gerade"}
            cutEnd={cuts.get(placement.id)?.end ?? "gerade"}
            bevelStart={cuts.get(placement.id)?.start === "schraeg" ? bevelAt(placement.wallIndex, false) : 0}
            bevelEnd={cuts.get(placement.id)?.end === "schraeg" ? bevelAt(placement.wallIndex, true) : 0}
            growsStartPx={(cuts.get(placement.id)?.growsStartPx ?? 0) * projection.scale}
            growsEndPx={(cuts.get(placement.id)?.growsEndPx ?? 0) * projection.scale}
            blankMm={cuts.get(placement.id)?.blankMm ?? placement.lengthMm}
            // Where its figures stand, if it prints any (`layOutReadings`,
            // UX-Gutachten 2, B-2): the wall's summary prints one figure for
            // all of them where theirs do not fit fitted — at six elements,
            // or where two neighbours' would run into each other —, and then
            // the elements print theirs no more, or the two label layers
            // land on top of each other (measured in the browser,
            // 16.08.2026). Zoomed, the figures step out a lane instead:
            // fitted shows the summary, zoomed the element figures, never
            // both and never neither (design 2026-09-10). On a sheet the
            // figures are written by `SheetLabels` (plan) or by the wall's
            // summary (schema) — never by the element.
            lengthLane={readings.lengths.get(placement.id)}
            blankLane={readings.blanks.get(placement.id)}
            sheet={sheet !== undefined}
            wallMaxOffsetMm={Math.max(wall.lengthMm - placement.lengthMm, 0)}
            onPointerDown={(event) => placementPointerDown(event, placement)}
            onPointerMove={(event) => placementPointerMove(event, placement)}
            onPointerUp={(event) => {
              endGesture(event);
              setDrag(null);
            }}
            onKeyDown={(event) => placementKeyDown(event, placement)}
            // Every kind, not only grating: the wall menu is about the wall
            // under the click, and an interruption or a bracket sits on a
            // wall exactly as much as a rost does (task 14; used to read
            // `if (!isBand) return` inside `PlacedThing`, which made the
            // menu unreachable from anywhere but a grating band).
            onCornerMenu={(event) =>
              onCornerMenu(
                wallMenuRequestAt(wall, placement.id, event, {
                  offsetMm: placement.offsetMm,
                  lengthMm: placement.lengthMm,
                }),
              )
            }
            zoomed={zoomed}
          />
        );
      })}

      {/* The holes — after every element, so they lie over the bands and not
          under their grips, and in **one** list keyed by the hole: the mark
          is the element that captured the pointer, and a mark remounted
          under its new carrier at every seam would take the capture with
          the old node and never settle the drag. Whose sheet each is drilled
          into is read here through `carrierAt`, the same rule `cutoutsOn`
          and the finding keep. A hole no sheet lies under is drawn all the
          same — dashed, on the band the job's type would draw here, naming
          no carrier (Schlussreview I-4): the picture is the error message
          (E-13), and a mark that vanished left a thing nobody could see,
          select or delete while the finding still said it was there. */}
      {cutouts.map((cutout) => {
        const wall = wallAt(cutout.wallIndex);
        if (!wall || wall.lengthMm === null) return null;
        const alongMm = cutoutAlongMm(cutout, features);
        const carrier = carrierAt(placements, cutout.wallIndex, alongMm);
        return (
          <CutoutMark
            key={cutout.id}
            cutout={cutout}
            carrierId={carrier?.id ?? null}
            alongMm={alongMm}
            band={bandOf(carrier)}
            wall={wall}
            seat={pointOn(wall, alongMm)}
            selected={selectedCutout === cutout.id}
            linked={cutout.featureId !== null}
            onPointerDown={(event) => cutoutPointerDown(event, cutout, carrier)}
            onPointerMove={(event) => cutoutPointerMove(event, cutout, carrier)}
            onPointerUp={cutoutPointerUp}
            onKeyDown={(event) => cutoutKeyDown(event, cutout)}
            onContextMenu={(event) => {
              event.preventDefault();
              onCornerMenu(
                wallMenuRequestAt(
                  wall,
                  carrier?.id ?? null,
                  event,
                  carrier ? { offsetMm: carrier.offsetMm, lengthMm: carrier.lengthMm } : null,
                  cutout.id,
                ),
              );
            }}
          />
        );
      })}

      {/* A door, a sill, a downpipe — standing on the wall, not a stretch of
          it. A covered zone that narrows the band is drawn by `PlacedThing`
          above, off the element's own `widthMm`; this only draws the object
          itself. An uncovered zone (`covered === false`) draws nothing extra
          either — `fill_wall` already left a gap in the grating there, and a
          second mark over an empty stretch would say the same thing twice. */}
      {features.map((feature) => {
        const wall = wallAt(feature.wallIndex);
        if (!wall || wall.lengthMm === null) return null;
        return (
          <FacadeObject
            key={feature.id}
            feature={feature}
            wall={wall}
            from={pointOn(wall, feature.offsetMm)}
            to={pointOn(wall, feature.offsetMm + feature.lengthMm)}
            selected={selectedFeature === feature.id}
            onSelect={() => onSelectFeature(feature.id)}
            onPointerDown={(event, end) => featurePointerDown(event, feature, end)}
            onPointerMove={(event) => featurePointerMove(event, feature)}
            onPointerUp={(event) => {
              endGesture(event);
              setDrag(null);
            }}
            onKeyDown={(event) => featureKeyDown(event, feature)}
            // The wall menu, reachable through the object too. An object
            // standing on a covered wall is the last thing drawn there, so
            // its grip is what a right-click lands on — and without this the
            // one menu per wall became unreachable exactly where „Wand neu
            // aufteilen" is most needed (found in the browser, 30.08.2026).
            // A bare wall click: the object has no corner of its own to
            // measure against.
            onCornerMenu={(event) =>
              onCornerMenu(wallMenuRequestAt(wall, null, event, null, null, feature.id))
            }
          />
        );
      })}

      {treatableWalls.map((wallIndex) => {
        const wall = walls[wallIndex];
        const following = walls[(wallIndex + 1) % walls.length];
        if (!wall || !following) return null;
        // A chamfer's two corners are only ever butted (design 2026-10-18,
        // call 7): no mitre and no filling to offer there, so no handle that
        // would offer one. The wall menu says why.
        if (isFaseCorner(outline, wallIndex)) return null;
        const mark = corners.find((each) => each.wallIndex === wallIndex) ?? null;
        // On paper an untreated corner carries nothing: the hollow handle is
        // an offer to a pointer, and a sheet has none.
        if (sheet && mark === null) return null;
        // Off the corner, along the bisector of the two walls' outward
        // directions — never *on* it. The corner already carries the shared
        // bracket (§4.1), and a second mark on the same point covers the
        // figure the whole screen exists to make believable (seen in the
        // browser, 27.08.2026).
        const bisector = normalised({
          x: wall.outward.x + following.outward.x,
          y: wall.outward.y + following.outward.y,
        });
        const seat = shift(wall.to, bisector, MITER_HANDLE_OFFSET);
        const next = ((wallIndex + 1) % walls.length) + 1;
        const kind =
          mark?.kindOverride === "innen"
            ? "Innenecke"
            : mark?.kindOverride === "aussen"
              ? "Außenecke"
              : null;
        return (
          <g
            key={`miter-${wallIndex}`}
            data-role="miter-handle"
            data-wall={wallIndex}
            data-on={mark !== null}
            data-treatment={mark?.treatment ?? "none"}
          >
            {/* A hairline back to the corner, so the handle is legibly about
                *that* corner and not floating beside the drawing. */}
            <line
              className={styles.miterLeader}
              x1={wall.to.x}
              y1={wall.to.y}
              x2={seat.x}
              y2={seat.y}
            />
            <circle
              className={mark ? styles.miterOn : styles.miterOff}
              cx={seat.x}
              cy={seat.y}
              r={MITER_HANDLE_R}
              role="button"
              tabIndex={0}
              aria-pressed={mark !== null}
              aria-label={
                `Ecke Wand ${wallIndex + 1} zu Wand ${next}` +
                (kind ? `, ${kind}` : "") +
                // The handle clears whatever the corner carries and sets a
                // mitre where it carries nothing. Switching a filling to a
                // mitre is therefore two presses — unambiguous, where one
                // press doing something different depending on the state
                // would not be.
                (mark === null
                  ? " — Gehrung setzen"
                  : mark.treatment === "fuellung"
                    ? " — Auffüllung entfernen"
                    : " — Gehrung entfernen")
              }
              onClick={() => onToggleCorner(wallIndex)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                onToggleCorner(wallIndex);
              }}
            />
            {mark?.treatment === "gehrung" ? (
              // The blade, drawn through the corner at 45 degrees. Not
              // decoration: a mitre on the wrong corner is visible as a slash
              // pointing at nothing, which is the whole of E-13.
              <line
                className={styles.miterBlade}
                data-role="miter-blade"
                x1={seat.x - MITER_HANDLE_R}
                y1={seat.y + MITER_HANDLE_R}
                x2={seat.x + MITER_HANDLE_R}
                y2={seat.y - MITER_HANDLE_R}
              />
            ) : null}
            {mark?.treatment === "fuellung" ? (
              // A filled square, because that is what happens there: the
              // corner square is covered whole rather than cut across. The
              // two states have to be told apart by **form**, not by tone
              // (design §5.5.1).
              <rect
                className={styles.cornerFilled}
                data-role="corner-filled"
                x={seat.x - MITER_HANDLE_R / 2}
                y={seat.y - MITER_HANDLE_R / 2}
                width={MITER_HANDLE_R}
                height={MITER_HANDLE_R}
              />
            ) : null}
          </g>
        );
      })}

      {consoles.map((mark, index) => {
        const point = projection.points[`c${index}`];
        if (!point) return null;
        return (
          <ConsoleDot
            key={`console-${index}`}
            index={index}
            mark={mark}
            point={point}
            walls={walls}
            scale={projection.scale}
            onConsoleMenu={onConsoleMenu}
          />
        );
      })}

      {sheet?.detail === "plan" ? (
        <SheetLabels
          placements={placements}
          cutouts={cutouts}
          features={features}
          issues={issues}
          cuts={cuts}
          wallAt={wallAt}
          pointOn={pointOn}
          bandOf={bandOf}
        />
      ) : null}

      {ghost ? (
        <GhostBand
          ghost={ghost}
          wall={wallAt(ghost.wallIndex)}
          pointOn={pointOn}
          placements={placements}
          widthPx={
            (tool === "grating" && jobSpec ? jobSpec.widthMm : 0) * projection.scale
          }
          minLengthMm={tool === "grating" ? (jobSpec?.minLengthMm ?? null) : null}
          zoomed={zoomed}
        />
      ) : null}

      {sheet ? (
        <SheetScaleBar scale={projection.scale} x={16} y={boardHeight - 14} />
      ) : (
        <g data-role="scale-bar" className={styles.scaleBar}>
          <line
            x1={VIEW_PADDING / 2}
            y1={boardHeight - 18}
            x2={VIEW_PADDING / 2 + scaleMm * projection.scale}
            y2={boardHeight - 18}
          />
          <text x={VIEW_PADDING / 2} y={boardHeight - 24} className={styles.scaleText}>
            {formatMm(scaleMm)}
          </text>
        </g>
      )}

      {/* Only once zoomed past the fit — fitted, the whole house already
          fills the board and a smaller copy of it would say nothing new
          (design §7.3). `view.scale !== null` and `view.centreMm !== null`
          go together on every `Viewport` this board is ever handed
          (`FIT_VIEW`, `zoomBy`, `viewForBox`); checked here instead of
          asserted, so `OverviewFrame` never needs a non-null assertion. */}
      {!sheet && view.scale !== null && view.centreMm !== null ? (
        <OverviewFrame
          fitNodes={fitNodes}
          segments={outline.segments}
          boardWidth={boardWidth}
          boardHeight={boardHeight}
          scale={projection.scale}
          centreMm={view.centreMm}
        />
      ) : null}
      </svg>
      </SheetCanvas>

      {/* The view controls, over the drawing rather than beside it: the
          board is as wide as the frame, and a column next to it would take
          width from the very thing being magnified. Plus and minus as signs
          with their names on them — since 23.09.2026 the only pointer route
          to a zoom, the wheel scrolls the page again (Entscheid Denis). */}
      {sheet ? null : (
      <div className={styles.zoomBar} role="group" aria-label="Ansicht">
        <div className={styles.zoomSteps}>
          <button
            type="button"
            className={`${styles.zoomButton} ${styles.zoomSign}`}
            data-role="zoom-out"
            aria-label="Verkleinern"
            title={`Verkleinern (${shortcutWords("zoomOut", 1)})`}
            onClick={() => zoom(1 / ZOOM_STEP, boardCentreMm())}
            disabled={view.scale === null}
          >
            −
          </button>
          {/* Against the whole house, not against the screen: „100 %" is
              „everything in view", which is the one figure somebody can
              recognise. No true-scale claim — a screen has no known
              millimetres per pixel. */}
          <output className={`num ${styles.zoomLevel}`} data-role="zoom-level" aria-label="Zoomstufe">
            {`${Math.round((projection.scale / projection.fitScale) * 100)} %`}
          </output>
          <button
            type="button"
            className={`${styles.zoomButton} ${styles.zoomSign}`}
            data-role="zoom-in"
            aria-label="Vergrößern"
            title={`Vergrößern (${shortcutWords("zoomIn", 1)})`}
            onClick={() => zoom(ZOOM_STEP, boardCentreMm())}
          >
            +
          </button>
        </div>
        <button
          type="button"
          className={styles.zoomButton}
          data-role="zoom-fit"
          title={`Alles zeigen (${shortcutWords("zoomFit", 1)})`}
          onClick={() => onView?.(FIT_VIEW)}
          disabled={view.scale === null}
        >
          Alles zeigen
        </button>
        {onMaximize ? (
          <MaximizeButton ref={maximizeRef} pressed={maximized} onClick={onMaximize} />
        ) : null}
      </div>
      )}

      <PlanningLegend
        placements={placements}
        features={features}
        consoles={consoles}
        cuts={cuts}
        sheet={
          sheet
            ? {
                specialWidth: sheet.detail === "plan",
                withoutMeasure: walls.some((wall) => !wall.accepts),
                setMeasures:
                  sheet.markSetMeasures === true &&
                  walls.some((wall) => wall.source === "manual"),
                mitre: corners.some(
                  (mark) => mark.treatment === "gehrung" && treatableWalls.includes(mark.wallIndex),
                ),
                filled: corners.some(
                  (mark) => mark.treatment === "fuellung" && treatableWalls.includes(mark.wallIndex),
                ),
                cutouts: cutouts.length > 0,
                issues: issues.size > 0,
              }
            : undefined
        }
      />
    </div>
  );
}

/**
 * What the eight signs on this board mean, drawn the way they are drawn.
 *
 * Miniatures, never colour chips: `OutlineReview` learned that on 10.08.2026,
 * where „Rost" against „Rost unklar" measured 1,12:1 and two states came out
 * as the identical grey. And this is the screen that most needs one — it
 * carries a band, a seam, an opening tick, four different console marks, a
 * bridge and two hatches, and it was the only drawing in the house without a
 * legend (found in review, 16.08.2026).
 *
 * Only what is actually on the board: a legend listing signs nobody has used
 * is a legend people stop reading.
 */
function PlanningLegend({
  placements,
  features,
  consoles,
  cuts,
  sheet,
}: {
  placements: Placement[];
  features: FacadeFeature[];
  consoles: ConsoleMark[];
  cuts: ReadonlyMap<string, BandCut>;
  /**
   * What a sheet draws that the screen explains by hover or by its side
   * panel — a dashed wall, a set measure, a treated corner, a hole, a
   * finding. On paper the legend is the only place to say it. `undefined`
   * on the screen, which keeps its legend exactly as it was.
   */
  sheet?: {
    /** The ∗ stands only in the plan sheet's numbers. */
    specialWidth: boolean;
    withoutMeasure: boolean;
    setMeasures: boolean;
    mitre: boolean;
    filled: boolean;
    cutouts: boolean;
    issues: boolean;
  };
}) {
  const has = (kind: PlacementKind) => placements.some((each) => each.kind === kind);
  const hasConsole = (kind: ConsoleMark["kind"]) => consoles.some((each) => each.kind === kind);

  const rows: { key: string; swatch: ReactNode; text: string }[] = [];
  if (has("grating")) {
    rows.push({
      key: "grating",
      text: "Rostelement, Fuge am Stoß",
      swatch: (
        <>
          <rect x="2" y="4" width="30" height="8" className={styles.legendBand} />
          <line x1="17" y1="4" x2="17" y2="12" className={styles.legendSeam} />
        </>
      ),
    });
  }
  if (features.length > 0) {
    rows.push({
      key: "feature",
      // The heavier mark drawn directly over the wall line, which is what a
      // facade object is: a thing standing on the wall rather than a stretch
      // of it. Named here because the board draws one on every wall that
      // carries one and explained none of them (review, 30.08.2026).
      text: "Fassadenobjekt — Tür, Fensterbank, Fallrohr, Stufe",
      swatch: (
        <>
          <line x1="2" y1="8" x2="32" y2="8" className={styles.legendWall} />
          <line x1="10" y1="8" x2="24" y2="8" className={styles.legendFeature} />
        </>
      ),
    });
  }
  if (has("gap")) {
    rows.push({
      key: "gap",
      // Not „Tür, Fallrohr, Schacht" any more: those are a `FacadeFeature`
      // since 30.08.2026, and the row above names them. A `gap` stays a valid
      // kind and an old draft can still carry one — which is the whole of
      // what this row now says.
      text: "Unterbrechung aus einem älteren Entwurf",
      swatch: (
        <>
          <line x1="2" y1="8" x2="32" y2="8" className={styles.legendWall} />
          <line x1="12" y1="3" x2="12" y2="13" className={styles.legendSeam} />
          <line x1="22" y1="3" x2="22" y2="13" className={styles.legendSeam} />
        </>
      ),
    });
  }
  if (hasConsole("joint")) {
    rows.push({
      key: "joint",
      text: "Konsole, gerechnet",
      swatch: (
        <>
          <line x1="2" y1="8" x2="32" y2="8" className={styles.legendWall} />
          <circle cx="17" cy="8" r={CONSOLE_RADIUS} className={styles.console} />
        </>
      ),
    });
  }
  if (hasConsole("corner")) {
    rows.push({
      key: "corner",
      text: "geteilte Eckkonsole — trägt beide Schenkel",
      swatch: (
        <>
          <line x1="2" y1="8" x2="32" y2="8" className={styles.legendWall} />
          <circle cx="17" cy="8" r={CORNER_CONSOLE_RADIUS} className={styles.console} />
        </>
      ),
    });
  }
  if (hasConsole("bridged")) {
    rows.push({
      key: "bridged",
      text: "ein Lauf, der über die Unterbrechung geht",
      swatch: (
        <>
          <line x1="2" y1="10" x2="32" y2="10" className={styles.legendWall} />
          <path d="M11,10 Q17,1 23,10" className={styles.consoleBridge} />
          <circle cx="17" cy="10" r={CONSOLE_RADIUS} className={styles.console} />
        </>
      ),
    });
  }
  if (hasConsole("middle")) {
    rows.push({
      key: "middle",
      text: "Mittelkonsole — trägt ein Element, keine zwei",
      swatch: (
        <>
          <line x1="2" y1="8" x2="32" y2="8" className={styles.legendWall} />
          <rect
            x={17 - MIDDLE_MARK_HALF_LENGTH}
            y={8 - MIDDLE_MARK_HALF_WIDTH}
            width={MIDDLE_MARK_HALF_LENGTH * 2}
            height={MIDDLE_MARK_HALF_WIDTH * 2}
            className={styles.consoleMiddle}
          />
        </>
      ),
    });
  }
  if (consoles.some(isConsoleOverridden)) {
    rows.push({
      key: "hand-set",
      // What the contour means, not what it looks like (design §8.1): a
      // person reading this row already sees the outline on the board and
      // needs to know it is a choice, not a rendering accident.
      text: "von Hand gewählt — weicht vom Vorschlag ab",
      swatch: (
        <>
          <line x1="2" y1="8" x2="32" y2="8" className={styles.legendWall} />
          <circle
            cx="17"
            cy="8"
            r={CONSOLE_RADIUS}
            className={`${styles.console} ${styles.consoleHandSet}`}
          />
        </>
      ),
    });
  }
  if (has("bracket")) {
    rows.push({
      key: "bracket",
      text: "Konsole extra — zusätzlich zu den gerechneten",
      swatch: (
        <>
          <line x1="2" y1="8" x2="32" y2="8" className={styles.legendWall} />
          <polygon points={diamond(17, 8, BRACKET_HALF - 1)} className={styles.bracket} />
        </>
      ),
    });
  }
  if (
    (sheet === undefined || sheet.specialWidth) &&
    placements.some((each) => each.kind === "grating" && each.widthMm !== null)
  ) {
    rows.push({
      key: "special",
      text: "Sonderbreite, weicht vom Rosttyp ab",
      swatch: (
        <>
          <rect x="2" y="5" width="30" height="6" className={styles.legendBand} />
          <text x="17" y="8" textAnchor="middle" dominantBaseline="middle" className={styles.specialWidth}>
            ∗
          </text>
        </>
      ),
    });
  }
  if (
    placements.some(
      (each) =>
        each.kind === "grating" && (cuts.get(each.id)?.blankMm ?? each.lengthMm) <= 0,
    )
  ) {
    rows.push({
      key: "no-blank",
      // Told apart by **form** and not by tone (§5.5.1): the frame with the
      // cross and no bars, which is exactly what the board draws there. Named
      // because a red frame without an explanation is a puzzle, and this one
      // means something precise (design §12.1).
      text: "kein Rohling — die Auffüllung nimmt die ganze Wandstrecke",
      swatch: (
        <>
          <rect x="2" y="4" width="30" height="8" className={styles.railIssue} />
          <line x1="2" y1="4" x2="32" y2="12" className={styles.issueCross} />
          <line x1="2" y1="12" x2="32" y2="4" className={styles.issueCross} />
        </>
      ),
    });
  }
  if (sheet?.withoutMeasure) {
    rows.push({
      key: "without-measure",
      text: "Wand ohne Maß — gezeichnet, keine Zahl, kein Rost",
      swatch: <line x1="2" y1="8" x2="32" y2="8" className={styles.wallWithoutMeasure} />,
    });
  }
  if (sheet?.setMeasures) {
    rows.push({
      key: "set-measure",
      text: "Maß gesetzt, nicht aus der Vorlage gelesen",
      swatch: (
        <>
          <line x1="2" y1="11" x2="32" y2="11" className={styles.setMeasureStrip} />
          <line x1="2" y1="6" x2="32" y2="6" className={styles.wall} />
        </>
      ),
    });
  }
  if (sheet?.mitre) {
    rows.push({
      key: "mitre",
      text: "Gehrung an der Ecke",
      swatch: (
        <>
          <circle cx="17" cy="8" r={MITER_HANDLE_R - 1} className={styles.miterOn} />
          <line x1="12" y1="13" x2="22" y2="3" className={styles.miterBlade} />
        </>
      ),
    });
  }
  if (sheet?.filled) {
    rows.push({
      key: "filled",
      text: "Ecke aufgefüllt — ein Rost deckt das Eckquadrat",
      swatch: (
        <>
          <circle cx="17" cy="8" r={MITER_HANDLE_R - 1} className={styles.miterOn} />
          <rect x="14" y="5" width="6" height="6" className={styles.cornerFilled} />
        </>
      ),
    });
  }
  if (sheet?.cutouts) {
    rows.push({
      key: "cutout",
      text: "Ausschnitt im Rost — Lage in der Stückliste",
      swatch: (
        <>
          <rect x="2" y="4" width="30" height="8" className={styles.legendBand} />
          <rect x="14" y="5.5" width="5" height="5" className={styles.cutout} />
        </>
      ),
    });
  }
  if (sheet?.issues) {
    rows.push({
      key: "issue",
      text: "! Befund — so nicht baubar, siehe Stückliste",
      swatch: (
        <>
          <rect x="2" y="4" width="30" height="8" className={styles.railIssue} />
          <line x1="2" y1="4" x2="32" y2="12" className={styles.issueCross} />
          <line x1="2" y1="12" x2="32" y2="4" className={styles.issueCross} />
        </>
      ),
    });
  }
  if (has("area")) {
    rows.push({
      key: "area",
      text: "Außenfläche — geht in keine Mengenliste",
      swatch: <rect x="2" y="3" width="30" height="10" className={styles.legendArea} />,
    });
  }

  if (rows.length === 0) return null;

  return (
    <ul className={styles.legend} aria-label="Zeichenerklärung">
      {rows.map((row) => (
        <li key={row.key} className={styles.legendItem} data-legend={row.key}>
          <svg className={styles.legendSwatch} viewBox="0 0 34 16" aria-hidden="true">
            {row.swatch}
          </svg>
          {row.text}
        </li>
      ))}
    </ul>
  );
}

function typeNameOf(placement: Placement, jobGratingType: GratingType | null): string {
  if (placement.kind !== "grating") return KIND_NAMES[placement.kind];
  const type = placement.gratingType ?? jobGratingType;
  if (type === null) return KIND_NAMES.grating;
  return GRATING_TYPE_NAMES[type] ?? type;
}

/**
 * One wall's own reading, once the elements on it are too narrow to carry
 * theirs: `12×1.000 · 480`, the house convention (§5.5.1).
 *
 * Run-length grouping in walking order, so the reading says what lies there in
 * the order it lies there — not a sorted tally somebody has to map back.
 */
/**
 * The wall's own measure, written **inside** the house.
 *
 * Inside and not beside the band, and that is the whole point of it. Every
 * other figure on this board sits *outside*: the element readings, the wall
 * summary, the console marks — `readingMark` pushes all of them along
 * `wall.outward`. A wall measure put there is either hidden under a band or
 * read as belonging to one, which is worse. Denis, 03.09.2026: „Bitte füge
 * auch in der Ansicht anbei immer die Maße der Wände ein. Diese platzierst du
 * innenliegend im Haus, damit die Maße nicht von den Gitterrosten
 * überschrieben werden."
 *
 * The flip is `dimensionLine`'s own: it pushes away from the point it is
 * handed, so handing it a point *outside* the wall turns its „away" into
 * „inwards". `readingMark` plays the same trick the other way round, and
 * neither of them re-derives the normal.
 *
 * **Only where a measure was asserted.** A `sketch` or `closure` wall carries
 * no figure at all, and the drawn distance is not one — A-014, §3.4. Such a
 * wall gets nothing rather than a number nobody stated — unless somebody did
 * state one that the sketch contradicts (E-64): that figure stands as a
 * question, „17,50 m?“, and still spans nothing.
 */
function WallDimension({ wall }: { wall: WallView }) {
  if (wall.lengthMm === null) {
    return wall.disputedMm !== null ? <DisputedWallFigure wall={wall} disputedMm={wall.disputedMm} /> : null;
  }

  // Two short ticks reaching back towards the wall, so the measure reads as
  // spanning that wall rather than floating parallel to it. The line lies
  // inside, so „towards the wall" is `wall.outward` — nothing here rebuilds a
  // normal of its own (`wallDimensionAt`, shared with the sheet).
  const { mark, tickFrom, tickTo } = wallDimensionAt(wall);

  return (
    <g data-role="wall-dimension" data-wall={wall.index}>
      <line
        className={styles.wallDimensionLine}
        x1={mark.x1}
        y1={mark.y1}
        x2={mark.x2}
        y2={mark.y2}
      />
      <line
        className={styles.wallDimensionLine}
        x1={mark.x1}
        y1={mark.y1}
        x2={tickFrom.x}
        y2={tickFrom.y}
      />
      <line
        className={styles.wallDimensionLine}
        x1={mark.x2}
        y1={mark.y2}
        x2={tickTo.x}
        y2={tickTo.y}
      />
      <text
        className={styles.wallDimension}
        data-role="wall-dimension-text"
        data-wall={wall.index}
        x={mark.labelX}
        y={mark.labelY}
        textAnchor="middle"
        dominantBaseline="middle"
        transform={mark.rotation ? `rotate(${mark.rotation} ${mark.labelX} ${mark.labelY})` : undefined}
      >
        {wall.shortened !== null ? <ShortenedFigure wall={wall} mark={mark} /> : plainMm(wall.lengthMm)}
      </text>
    </g>
  );
}

/** „17,50 m?“ — a stated figure as the question it is (E-64). */
function disputedFigure(mm: number): string {
  return `${formatMetres(mm)}?`;
}

/** What a reader hears for {@link disputedFigure}: the wall, the figure, why it is asked. */
function disputedLabel(wall: WallView, mm: number): string {
  return `Wand ${wall.index + 1}: ${formatMetres(mm)}, passt nicht zur Skizze`;
}

/**
 * A wall whose stated figure the sketch contradicts (E-64, design 2026-10-28
 * §3.2): drawn dashed at the sketch's length, and the figure written where a
 * measure would stand — in metres with a question mark and in the conflict
 * tone, because it is one: the person's figure against the sketch's line.
 * Text only, no dimension line with ticks — the figure does not span the
 * wall it is drawn beside, and a dimension line would say it did.
 */
function DisputedWallFigure({ wall, disputedMm }: { wall: WallView; disputedMm: number }) {
  const { mark } = wallDimensionAt(wall);
  return (
    <g data-role="wall-dimension" data-wall={wall.index} data-measure="disputed">
      <text
        className={`${styles.wallDimension} ${styles.wallDimensionDisputed}`}
        data-role="wall-dimension-text"
        data-wall={wall.index}
        aria-label={disputedLabel(wall, disputedMm)}
        x={mark.labelX}
        y={mark.labelY}
        textAnchor="middle"
        dominantBaseline="middle"
        transform={mark.rotation ? `rotate(${mark.rotation} ${mark.labelX} ${mark.labelY})` : undefined}
      >
        {disputedFigure(disputedMm)}
      </text>
    </g>
  );
}

/** How far the second line of a shortened wall's figure stands from the first. */
const SHORTENED_LINE_PX = 12;

/**
 * A wall a chamfer shortened says both its lengths (design 2026-10-18 §5.1):
 * „2.500 mm belegbar“, and under it „(3.000 bis zum Eckpunkt)“ — on two
 * lines, because on one the pair outgrew a short wall and ran into its
 * neighbour's figure (the editor found that first, 18.10.2026). Read
 * together the text is the design's one phrase.
 *
 * Both lines stay inside the house and read top to bottom: the text is
 * turned with its wall, so „down“ in its own frame is the screen direction
 * (−sin r, cos r). Where that points into the house, the second line goes
 * under the first; where it points back at the wall, the first line moves up
 * by one line and the second takes the place a single figure would have —
 * never back over the wall.
 */
function ShortenedFigure({ wall, mark }: { wall: WallView; mark: DimensionLine }) {
  const label = wall.shortened ?? "";
  const split = label.indexOf(" (");
  const turn = (mark.rotation * Math.PI) / 180;
  const downIsOutward = -Math.sin(turn) * wall.outward.x + Math.cos(turn) * wall.outward.y > 0;
  return (
    <>
      <tspan dy={downIsOutward ? -SHORTENED_LINE_PX : 0}>{label.slice(0, split)}</tspan>
      <tspan x={mark.labelX} dy={SHORTENED_LINE_PX}>
        {label.slice(split)}
      </tspan>
    </>
  );
}

// ── The board on paper (WP-E2-4) ──────────────────────────────────────────

/**
 * The drawing of a sheet, taken out of the tab order and out of the
 * accessibility tree at once (`inert`): every grip below is still a grip,
 * and a printed page has nothing to operate. The sheet around it names the
 * drawing and carries its text equivalent — the parts list.
 */
function SheetCanvas({ on, children }: { on: boolean; children: ReactNode }) {
  if (!on) return <>{children}</>;
  return (
    <div className={styles.sheetCanvas} inert>
      {children}
    </div>
  );
}

/**
 * Where a wall's own measure stands: inside the house, parallel to the wall,
 * with its two ticks — one place for the screen's `WallDimension` and the
 * sheet's, so the two cannot put the figure in different spots.
 */
function wallDimensionAt(wall: WallView) {
  const middle = { x: (wall.from.x + wall.to.x) / 2, y: (wall.from.y + wall.to.y) / 2 };
  const outside = shift(middle, wall.outward, 100);
  const mark = dimensionLine(wall.from, wall.to, outside, WALL_DIMENSION_OFFSET);
  return {
    mark,
    tickFrom: shift({ x: mark.x1, y: mark.y1 }, wall.outward, WALL_DIMENSION_TICK),
    tickTo: shift({ x: mark.x2, y: mark.y2 }, wall.outward, WALL_DIMENSION_TICK),
  };
}

/**
 * The wall's measure on a sheet — the screen's `WallDimension`, plus the
 * three things paper has to say without a pointer:
 *
 * - **which wall** it is (`W1`), because every element number starts with it;
 * - **„ohne Maß"** where nobody stated one, written where the figure would
 *   stand — dashed alone is a convention, the words are the statement
 *   (design §3, A-014). Still no number: the drawn distance is not one;
 * - **„17,50 m?"** where a stated figure contradicts the sketch (E-64) — the
 *   customer compares this sheet with their own sketch, and the figure in
 *   question is exactly what they have to see;
 * - on the schema sheet of a read Vorgang, a quiet strip along the wall where
 *   a person **set** the figure instead of the reading (design §3), so the
 *   customer sees which of their figures we changed.
 */
function SheetWallDimension({
  wall,
  markSet,
  swings,
}: {
  wall: WallView;
  markSet: boolean;
  /** Where a door on this wall sweeps into the house, in mm along the wall —
   * the figure steps aside rather than being crossed by the arc. */
  swings: [number, number][];
}) {
  const { mark, tickFrom, tickTo } = wallDimensionAt(wall);
  const name = `W${wall.index + 1}`;
  // A wall a chamfer shortened says both its lengths on paper too (design
  // 2026-10-18 §5.1) — the workshop lays on the first, the customer stated
  // the second.
  const disputed = wall.lengthMm === null ? wall.disputedMm : null;
  const figure =
    wall.lengthMm === null
      ? disputed !== null
        ? disputedFigure(disputed)
        : "ohne Maß"
      : (wall.shortened ?? plainMm(wall.lengthMm));
  const along = labelAlong(wall, `${name}  ${figure}`, swings);
  const labelX = mark.labelX + (mark.x2 - mark.x1) * (along - 0.5);
  const labelY = mark.labelY + (mark.y2 - mark.y1) * (along - 0.5);
  const rotate = mark.rotation ? `rotate(${mark.rotation} ${labelX} ${labelY})` : undefined;

  if (wall.lengthMm === null) {
    return (
      <g data-role="wall-dimension" data-wall={wall.index} data-measure={disputed !== null ? "disputed" : "none"}>
        <text
          className={
            disputed !== null ? `${styles.wallDimension} ${styles.wallDimensionDisputed}` : styles.wallDimension
          }
          data-role="wall-dimension-text"
          x={labelX}
          y={labelY}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={rotate}
        >
          <tspan className={styles.sheetWallName}>{name}</tspan>
          <tspan dx="4">{figure}</tspan>
        </text>
      </g>
    );
  }

  const set = markSet && wall.source === "manual";
  const stripFrom = shift(wall.from, wall.outward, -SET_STRIP_OFFSET);
  const stripTo = shift(wall.to, wall.outward, -SET_STRIP_OFFSET);
  return (
    <g
      data-role="wall-dimension"
      data-wall={wall.index}
      data-measure={wall.source === "manual" ? "set" : "read"}
    >
      {set ? (
        <line
          className={styles.setMeasureStrip}
          data-role="set-measure"
          x1={stripFrom.x}
          y1={stripFrom.y}
          x2={stripTo.x}
          y2={stripTo.y}
        />
      ) : null}
      <line className={styles.wallDimensionLine} x1={mark.x1} y1={mark.y1} x2={mark.x2} y2={mark.y2} />
      <line className={styles.wallDimensionLine} x1={mark.x1} y1={mark.y1} x2={tickFrom.x} y2={tickFrom.y} />
      <line className={styles.wallDimensionLine} x1={mark.x2} y1={mark.y2} x2={tickTo.x} y2={tickTo.y} />
      <text
        className={styles.wallDimension}
        data-role="wall-dimension-text"
        data-wall={wall.index}
        x={labelX}
        y={labelY}
        textAnchor="middle"
        dominantBaseline="middle"
        transform={rotate}
      >
        <tspan className={styles.sheetWallName}>{name}</tspan>
        <tspan dx="4">{figure}</tspan>
      </text>
    </g>
  );
}

/**
 * Where along its wall (0…1) a wall's own figure stands: the middle, unless a
 * door's swing covers the middle — then the middle of the longest stretch no
 * swing covers. Where nothing is free, the middle all the same.
 */
function labelAlong(wall: WallView, text: string, swings: [number, number][]): number {
  if (wall.lengthMm === null || swings.length === 0) return 0.5;
  const lengthMm = wall.lengthMm;
  const pxPerMm = Math.hypot(wall.to.x - wall.from.x, wall.to.y - wall.from.y) / lengthMm;
  const halfMm = (text.length * 10 * MONO_ADVANCE) / 2 / pxPerMm;
  const middle = lengthMm / 2;
  const blocked = ([start, end]: [number, number]) => start < middle + halfMm && end > middle - halfMm;
  if (!swings.some(blocked)) return 0.5;
  const sorted = [...swings].sort((a, b) => a[0] - b[0]);
  let best: [number, number] = [0, 0];
  let cursor = 0;
  for (const [start, end] of [...sorted, [lengthMm, lengthMm] as [number, number]]) {
    if (start - cursor > best[1] - best[0]) best = [cursor, start];
    cursor = Math.max(cursor, end);
  }
  if (best[1] - best[0] < halfMm * 2) return 0.5;
  return (best[0] + best[1]) / 2 / lengthMm;
}

/** How far inside the house the „gesetzt" strip runs, in board units —
 * clear of the wall line, inside the wall's own dimension line. */
const SET_STRIP_OFFSET = 5;

/** An angle that keeps text upright along a wall in any direction. */
function uprightAngle(from: ScreenPoint, to: ScreenPoint): number {
  const angle = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
  if (angle > 90) return angle - 180;
  if (angle <= -90) return angle + 180;
  return angle;
}

/**
 * Every element on a sheet, with its number and its length (design §4:
 * „Planungszeichnung zeigt jedes Element mit Nummer und Länge").
 *
 * **The number stands in the band**, on a white ground cut out of the
 * hatching — the way a layout plan carries its position numbers, and the
 * hatching never runs behind a figure (CI §3). Where it does not fit — a
 * short element on a long facade, or a hole where the number would sit —
 * it moves outside, in front of the length.
 *
 * **The length stands outside the band**, as on the screen; where two
 * neighbours' figures would overlap, the later one steps one row further out.
 * Rows are packed per wall in walking order, so the figure of an element
 * stays beside it — nothing is dropped and nothing is summarised.
 *
 * A finding puts a „!" in front of the length (status red, data area) — the
 * rails and the cross on the band already say it in form.
 */
function SheetLabels({
  placements,
  cutouts,
  features,
  issues,
  cuts,
  wallAt,
  pointOn,
  bandOf,
}: {
  placements: Placement[];
  cutouts: Cutout[];
  features: FacadeFeature[];
  issues: ReadonlySet<string>;
  cuts: ReadonlyMap<string, BandCut>;
  wallAt: (index: number) => WallView | null;
  pointOn: (wall: WallView, mm: number) => ScreenPoint;
  bandOf: (placement: Placement | null) => BandGeometry;
}) {
  const gratings = placements
    .filter((each) => each.kind === "grating")
    .sort((a, b) => a.wallIndex - b.wallIndex || a.offsetMm - b.offsetMm);
  /** Per wall, where each row of figures is taken up to, along the wall. */
  const rowsByWall = new Map<number, number[]>();
  /** Every figure already placed, on any wall — the rows above only see
   * their own wall, and at a short Rücksprung the neighbour's figures stand
   * in the same corner (P-7). */
  const standing: LabelBox[] = [];
  const numberWidth = (text: string) => text.length * SHEET_NUMBER_FONT * MONO_ADVANCE + 4;
  const readingWidth = (text: string) => text.length * SHEET_READING_FONT * MONO_ADVANCE;

  /** The first row (or pair of rows) on this wall that is free over
   * `[left, right]` — packed in walking order, so a figure stays beside its
   * element and steps out only where its neighbour's figure is in the way. */
  function claim(rows: number[], left: number, right: number, height: 1 | 2): number {
    const free = (row: number) => rows[row] === undefined || left >= rows[row]! + SHEET_FIGURE_GAP;
    let row = 0;
    while (!(free(row) && (height === 1 || free(row + 1)))) row += 1;
    rows[row] = right;
    if (height === 2) rows[row + 1] = right;
    return row;
  }

  const figureOf = (placement: Placement) => {
        const wall = wallAt(placement.wallIndex);
        if (!wall || wall.lengthMm === null) return null;
        const pxPerMm = Math.hypot(wall.to.x - wall.from.x, wall.to.y - wall.from.y) / wall.lengthMm;
        const from = pointOn(wall, placement.offsetMm);
        const to = pointOn(wall, placement.offsetMm + placement.lengthMm);
        const middle = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
        const lengthPx = placement.lengthMm * pxPerMm;
        const centrePx = (placement.offsetMm + placement.lengthMm / 2) * pxPerMm;
        const band = bandOf(placement);
        const blankMm = cuts.get(placement.id)?.blankMm ?? placement.lengthMm;
        const label = `${placement.label ?? "—"}${placement.widthMm !== null ? "∗" : ""}`;
        const issue = issues.has(placement.id);

        // A hole lies over the band's middle more often than not (a downpipe
        // sits where it sits); a number cut out of the band there would hide
        // the very thing the workshop has to drill.
        const half = numberWidth(label) / 2;
        const underHole = cutouts
          .filter((cutout) => cutout.wallIndex === placement.wallIndex)
          .some((cutout) => {
            const along = cutoutAlongMm(cutout, features) * pxPerMm;
            const reachPx = (cutoutExtentMm(cutout).alongMm * pxPerMm) / 2 + CUTOUT_MIN_R;
            return along - reachPx < centrePx + half && along + reachPx > centrePx - half;
          });
        const inside =
          blankMm > 0 &&
          !underHole &&
          numberWidth(label) <= lengthPx - 2 &&
          SHEET_NUMBER_FONT + 2 <= band.drawnWidth;

        const length =
          blankMm <= 0
            ? `${plainMm(placement.lengthMm)} → kein Rohling`
            : blankMm === placement.lengthMm
              ? plainMm(placement.lengthMm)
              : `${plainMm(placement.lengthMm)} → ${plainMm(blankMm)}`;
        const mark = issue ? "! " : "";
        // Outside, the number stands over the length — two short lines, not
        // one long one that would reach over the neighbours.
        const width = inside
          ? readingWidth(`${mark}${length}`)
          : Math.max(readingWidth(`${mark}${label}`), readingWidth(length));
        const rows = rowsByWall.get(placement.wallIndex) ?? [];
        rowsByWall.set(placement.wallIndex, rows);
        let row = claim(rows, centrePx - width / 2, centrePx + width / 2, inside ? 1 : 2);

        const angle = uprightAngle(from, to);
        const bandCentre = shift(middle, wall.outward, band.innerPx + band.drawnWidth / 2);
        /** Outside the band (`1`), or — where a neighbouring wall's figures
         * leave no room there — inside the house along the wall (`-1`). */
        type Side = 1 | -1;
        const rowAt = (index: number, side: Side = 1) =>
          side === 1
            ? shift(
                middle,
                wall.outward,
                band.innerPx + band.drawnWidth + SHEET_READING_GAP + SHEET_READING_FONT / 2 + index * SHEET_ROW_PX,
              )
            : shift(middle, wall.outward, -(SHEET_READING_GAP + SHEET_READING_FONT / 2 + index * SHEET_ROW_PX));
        // What this element prints outside its band, at a given row: the
        // number over the length where the number had to leave the band.
        const boxesAt = (index: number, side: Side = 1): LabelBox[] => [
          ...(inside
            ? []
            : [labelBox(rowAt(index, side), angle, readingWidth(`${mark}${label}`), SHEET_NUMBER_FONT)]),
          labelBox(rowAt(inside ? index : index + 1, side), angle, readingWidth(`${mark}${length}`), SHEET_READING_FONT),
        ];
        const blocked = (index: number, side: Side = 1) =>
          boxesAt(index, side).some((box) => collides(box, standing, SHEET_CROSS_GAP));
        // A neighbouring wall's figure in the way (P-7): step further out,
        // the way a figure steps past its own neighbour on the wall. Where
        // that runs along a long wall's packed figures and finds no room —
        // a short element in a Rücksprung — the figure crosses to the inside
        // of its own wall, still beside its element. Nothing is shortened or
        // dropped; where neither clears, it stays where the wall put it.
        const firstRow = row;
        let side: Side = 1;
        if (blocked(row)) {
          let stepped = row;
          while (stepped < firstRow + SHEET_MAX_EXTRA_ROWS && blocked(stepped)) stepped += 1;
          if (!blocked(stepped)) row = stepped;
          else if (!blocked(0, -1)) {
            row = 0;
            side = -1;
          }
        }
        if (side === 1 && row !== firstRow) {
          rows[row] = Math.max(rows[row] ?? -Infinity, centrePx + width / 2);
          if (!inside) rows[row + 1] = Math.max(rows[row + 1] ?? -Infinity, centrePx + width / 2);
        }
        standing.push(...boxesAt(row, side));
        const placeText = (at: ScreenPoint) => `translate(${at.x} ${at.y}) rotate(${angle})`;
        const numberBox = numberWidth(label);

        return (
          <g
            key={placement.id}
            data-role="sheet-element"
            data-placement={placement.id}
            data-label={placement.label ?? ""}
            data-length={placement.lengthMm}
            data-blank={blankMm}
            data-inside={inside}
            data-row={row}
            data-side={side === 1 ? "outside" : "inside"}
          >
            {inside ? (
              <g transform={placeText(bandCentre)}>
                <rect
                  className={styles.sheetKnockout}
                  x={-numberBox / 2}
                  y={-(SHEET_NUMBER_FONT / 2 + 1)}
                  width={numberBox}
                  height={SHEET_NUMBER_FONT + 2}
                />
                <text
                  className={styles.sheetNumber}
                  data-role="sheet-number"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {label}
                </text>
              </g>
            ) : (
              <text
                className={`${styles.sheetNumber} ${styles.sheetHalo}`}
                data-role="sheet-number"
                textAnchor="middle"
                dominantBaseline="central"
                transform={placeText(rowAt(row, side))}
              >
                {issue ? <tspan className={styles.sheetIssue}>{mark}</tspan> : null}
                {label}
              </text>
            )}
            <text
              className={`${styles.sheetReading} ${styles.sheetHalo}`}
              data-role="sheet-reading"
              textAnchor="middle"
              dominantBaseline="central"
              transform={placeText(rowAt(inside ? row : row + 1, side))}
            >
              {issue && inside ? <tspan className={styles.sheetIssue}>{mark}</tspan> : null}
              {length}
            </text>
          </g>
        );
  };

  // Laid out longest wall first, drawn in walking order: where two walls'
  // figures meet in a corner, the short wall's step out of the long wall's
  // way — at a Rücksprung the long wall's first element lies right under
  // the short wall's figures, and only those can move clear (P-7). The
  // drawing order stays the parts list's.
  const lengthOf = (placement: Placement) => wallAt(placement.wallIndex)?.lengthMm ?? 0;
  const laid = new Map<string, ReactNode>();
  for (const placement of [...gratings].sort(
    (a, b) => lengthOf(b) - lengthOf(a) || a.wallIndex - b.wallIndex || a.offsetMm - b.offsetMm,
  )) {
    laid.set(placement.id, figureOf(placement));
  }

  return <g data-role="sheet-labels">{gratings.map((placement) => laid.get(placement.id) ?? null)}</g>;
}

/** The bar lengths a sheet may use, and how each is divided — a bar a reader
 * can lay a ruler against, not a single stroke (design §3: „1 m / 5 m"). */
const SHEET_BAR_STEPS: readonly { mm: number; parts: number }[] = [
  { mm: 1000, parts: 2 },
  { mm: 2000, parts: 2 },
  { mm: 5000, parts: 5 },
  { mm: 10_000, parts: 5 },
  { mm: 20_000, parts: 4 },
];
const SHEET_BAR_MAX_PX = 190;

/**
 * The scale of a sheet, as a bar and never as „1:100" (design §3): the
 * printed size depends on the browser and the paper, a bar scales with the
 * drawing whatever happens to it. Alternating fields, „0" and the length in
 * metres, in the mono face like every figure.
 */
function SheetScaleBar({ scale, x, y }: { scale: number; x: number; y: number }) {
  const step =
    [...SHEET_BAR_STEPS].reverse().find((each) => each.mm * scale <= SHEET_BAR_MAX_PX) ??
    SHEET_BAR_STEPS[0]!;
  const partPx = (step.mm * scale) / step.parts;
  return (
    <g data-role="scale-bar" data-scale-mm={step.mm} className={styles.sheetScale}>
      {Array.from({ length: step.parts }, (_, index) => (
        <rect
          key={index}
          className={index % 2 === 0 ? styles.sheetScaleFilled : undefined}
          x={x + index * partPx}
          y={y}
          width={partPx}
          height={4}
        />
      ))}
      <text className={styles.sheetScaleText} x={x} y={y - 4}>
        0
      </text>
      <text className={styles.sheetScaleText} x={x + step.mm * scale} y={y - 4} textAnchor="end">
        {`${step.mm / 1000} m`}
      </text>
    </g>
  );
}

/**
 * Which lane each figure outside the bands stands on, on the screen
 * (UX-Gutachten 2, B-2), and which walls say their division once.
 *
 * The first screen of `/planung` showed „1.000 mm 1.000 mm 1.0001mm00 →
 * 1.300 mm" at a wall of four elements: each figure was set at its
 * element's middle whatever its length, and the pair a grown element
 * prints was longer than the element. On a tool whose promise is that the
 * measures are right, a mangled figure costs more than any style.
 *
 * Laid out per wall, in walking order, before anything is drawn — the
 * boxes are estimated the way the sheet's are (`labelBox`, the mono face's
 * 0,6 em advance), each one long by {@link READING_GAP} so two figures on a
 * lane keep that much room between them:
 *
 * - **The lengths** stand beside the band; one that would run into the
 *   figure before it steps a lane further out (font + 4 px).
 * - **Fitted**, a wall where that happens says its division once instead —
 *   the wall's summary, „4×1.000", as walls of six elements always have.
 *   The screen keeps its rule: fitted shows the summary where the elements'
 *   figures do not fit, zoomed the figures, never both (design 2026-09-10).
 * - **The blank** — „→ 1.300 mm" where a corner grew the element — is its
 *   own figure, at its element, on the lane past its length (on a
 *   summarised wall, where the lengths would stand): it said which of the
 *   two gratings at a corner covers it, inside the length's own lane it ran
 *   over the neighbour's figure.
 *
 * Only where a figure stands is estimated, never what it says: nothing is
 * rounded, no figure invented — each is a length the plan holds (A-014,
 * E-14). The sheets keep their own packing (`SheetLabels`).
 */
interface ReadingLayout {
  /** The walls whose division their summary says. */
  summarised: ReadonlySet<number>;
  /** Element → the lane its length stands on, 0 beside the band. Absent: it prints none. */
  lengths: ReadonlyMap<string, number>;
  /** Element → the lane its blank stands on. Absent: it grew nothing, or prints nothing. */
  blanks: ReadonlyMap<string, number>;
}

/** What the screen prints for a blank that differs from its wall stretch. */
function blankReading(blankMm: number): string {
  return blankMm <= 0 ? "→ kein Rohling" : `→ ${formatMm(blankMm)}`;
}

function layOutReadings({
  walls,
  placements,
  issues,
  zoomed,
  blankOf,
  halfWidthOf,
  summaryOffsetOf,
  pointOn,
}: {
  walls: readonly WallView[];
  placements: readonly Placement[];
  issues: ReadonlySet<string>;
  zoomed: boolean;
  /** The element's blank in mm — its wall stretch where nothing grew it. */
  blankOf: (placement: Placement) => number;
  /** The element's drawn half width in px — what its figures stand off from. */
  halfWidthOf: (placement: Placement) => number;
  /** How far off the wall its summary stands — `SUMMARY_OFFSET`, past a band
   * widened outward further. */
  summaryOffsetOf: (wallIndex: number) => number;
  pointOn: (wall: WallView, mm: number) => ScreenPoint;
}): ReadingLayout {
  const summarised = new Set<number>();
  const lengths = new Map<string, number>();
  const blanks = new Map<string, number>();
  for (const wall of walls) {
    if (wall.lengthMm === null || wall.lengthMm <= 0) continue;
    const gratings = placements
      .filter((each) => each.wallIndex === wall.index && each.kind === "grating")
      .sort((a, b) => a.offsetMm - b.offsetMm);
    if (gratings.length === 0) continue;
    const pxPerMm = Math.hypot(wall.to.x - wall.from.x, wall.to.y - wall.from.y) / wall.lengthMm;
    const boxAt = (placement: Placement, lane: number, text: string, font = READING_FONT, room = READING_GAP) => {
      const mark = readingMark(
        wall,
        pointOn(wall, placement.offsetMm),
        pointOn(wall, placement.offsetMm + placement.lengthMm),
        READING_OFFSET + halfWidthOf(placement) + lane * READING_LANE,
      );
      return labelBox({ x: mark.labelX, y: mark.labelY }, mark.rotation, text.length * font * MONO_ADVANCE + room, font);
    };
    // A finding prints its „!" where the length would stand, and the length not.
    const marks = gratings.filter((each) => issues.has(each.id)).map((each) => boxAt(each, 0, "!", ISSUE_FONT));
    const readable = gratings.filter((each) => !issues.has(each.id) && each.lengthMm * pxPerMm >= MIN_PX_FOR_READING);
    const standing: LabelBox[] = [...marks];
    const claim = (placement: Placement, text: string, from: number): number => {
      let lane = from;
      while (lane < READING_MAX_LANES - 1 && collides(boxAt(placement, lane, text), standing)) lane += 1;
      standing.push(boxAt(placement, lane, text));
      return lane;
    };
    const own = new Map(readable.map((each) => [each.id, claim(each, formatMm(each.lengthMm), 0)] as const));
    const summary = !zoomed && (gratings.length >= SUMMARY_FROM_ELEMENTS || [...own.values()].some((lane) => lane > 0));
    if (summary) {
      summarised.add(wall.index);
      // The lengths give their lane up to the blanks; the summary stands further out.
      standing.splice(0, standing.length, ...marks, wallSummaryMark(wall, gratings, pointOn, summaryOffsetOf(wall.index)).box);
    } else {
      for (const [id, lane] of own) lengths.set(id, lane);
    }
    for (const placement of readable) {
      const blankMm = blankOf(placement);
      if (blankMm === placement.lengthMm) continue;
      blanks.set(placement.id, claim(placement, blankReading(blankMm), summary ? 0 : (own.get(placement.id) ?? 0) + 1));
    }
  }
  return { summarised, lengths, blanks };
}

/** One wall's part of a {@link ReadingLayout}, as it stood when a gesture on that wall began. */
interface HeldReadings {
  wallIndex: number;
  summarised: boolean;
  lengths: ReadonlyMap<string, number>;
  blanks: ReadonlyMap<string, number>;
}

/** The ids of the elements on `wallIndex`. */
function idsOnWall(placements: readonly Placement[], wallIndex: number): Set<string> {
  return new Set(placements.filter((each) => each.wallIndex === wallIndex).map((each) => each.id));
}

/** What `wallIndex` prints and where, out of the whole board's layout. */
function wallReadings(layout: ReadingLayout, wallIndex: number, placements: readonly Placement[]): HeldReadings {
  const on = idsOnWall(placements, wallIndex);
  const own = (lanes: ReadonlyMap<string, number>) => new Map([...lanes].filter(([id]) => on.has(id)));
  return {
    wallIndex,
    summarised: layout.summarised.has(wallIndex),
    lengths: own(layout.lengths),
    blanks: own(layout.blanks),
  };
}

/**
 * The board's layout with one wall's part as it was held (final review,
 * M-3): summary or element figures, and each figure's lane, from the
 * gesture's start; every other wall as it lays itself out now. An element
 * the held layout does not know prints nothing until the release — a held
 * summary already says it, and a figure would come and go under the hand.
 */
function holdWallReadings(layout: ReadingLayout, held: HeldReadings, placements: readonly Placement[]): ReadingLayout {
  const on = idsOnWall(placements, held.wallIndex);
  for (const id of held.lengths.keys()) on.add(id);
  for (const id of held.blanks.keys()) on.add(id);
  const others = (lanes: ReadonlyMap<string, number>) => [...lanes].filter(([id]) => !on.has(id));
  const summarised = new Set([...layout.summarised].filter((index) => index !== held.wallIndex));
  if (held.summarised) summarised.add(held.wallIndex);
  return {
    summarised,
    lengths: new Map([...others(layout.lengths), ...held.lengths]),
    blanks: new Map([...others(layout.blanks), ...held.blanks]),
  };
}

/** The summary's font size in board units (`.summary`). */
const SUMMARY_FONT = 11;
/** One step further out for a summary in a neighbour's way (P-7). */
const SUMMARY_STEP = 13;
/** How many steps a summary may take before it stays where it was. */
const SUMMARY_MAX_STEPS = 4;

/** A wall's division in one line — „6×1.000 · 600" — and where it sits. */
function wallSummaryMark(
  wall: WallView,
  gratings: Placement[],
  pointOn: (wall: WallView, mm: number) => ScreenPoint,
  offset: number,
) {
  const ordered = [...gratings].sort((a, b) => a.offsetMm - b.offsetMm);
  const groups: { lengthMm: number; count: number }[] = [];
  for (const placement of ordered) {
    const last = groups.at(-1);
    if (last && last.lengthMm === placement.lengthMm) last.count += 1;
    else groups.push({ lengthMm: placement.lengthMm, count: 1 });
  }
  const text = groups
    .map((group) => (group.count > 1 ? `${group.count}×${plainMm(group.lengthMm)}` : plainMm(group.lengthMm)))
    .join(" · ");

  const first = ordered[0]!;
  const last = ordered.at(-1)!;
  const mark = readingMark(
    wall,
    pointOn(wall, first.offsetMm),
    pointOn(wall, last.offsetMm + last.lengthMm),
    offset,
  );
  const box = labelBox(
    { x: mark.labelX, y: mark.labelY },
    mark.rotation,
    text.length * SUMMARY_FONT * MONO_ADVANCE,
    SUMMARY_FONT,
  );
  return { text, mark, box };
}

/**
 * How far each wall's summary sits off its wall on the schema sheet (P-7).
 *
 * At a short Rücksprung the short wall's summary stands upright in the
 * corner, right over the start of the long wall's summary — „1.000 · 750"
 * over „6×1.000 · 600". Laid out **shortest wall first**: a short wall's
 * summary is short, so the long wall's summary clears it by stepping out a
 * row or two; the other way round the short one would have to step along
 * the whole long line. Where no step clears, the summary stays put.
 */
function sheetSummaryOffsets(
  walls: readonly WallView[],
  placements: readonly Placement[],
  pointOn: (wall: WallView, mm: number) => ScreenPoint,
  pushOf: (wallIndex: number) => number,
): Map<number, number> {
  const offsets = new Map<number, number>();
  const standing: LabelBox[] = [];
  const gratingsOf = (wall: WallView) =>
    placements.filter((each) => each.wallIndex === wall.index && each.kind === "grating");
  const ordered = walls
    .filter((wall) => gratingsOf(wall).length > 0)
    .sort((a, b) => (a.lengthMm ?? Infinity) - (b.lengthMm ?? Infinity) || a.index - b.index);
  for (const wall of ordered) {
    const gratings = gratingsOf(wall);
    const base = SUMMARY_OFFSET + pushOf(wall.index);
    let offset = base;
    for (let step = 0; step <= SUMMARY_MAX_STEPS; step += 1) {
      const candidate = base + step * SUMMARY_STEP;
      if (!collides(wallSummaryMark(wall, gratings, pointOn, candidate).box, standing, SHEET_CROSS_GAP)) {
        offset = candidate;
        break;
      }
    }
    offsets.set(wall.index, offset);
    standing.push(wallSummaryMark(wall, gratings, pointOn, offset).box);
  }
  return offsets;
}

function WallSummary({
  wall,
  gratings,
  pointOn,
  offset = SUMMARY_OFFSET,
}: {
  wall: WallView;
  gratings: Placement[];
  pointOn: (wall: WallView, mm: number) => ScreenPoint;
  /** Off the wall, outwards — further than the default only on a sheet,
   * where a neighbouring wall's summary is in the way (P-7). */
  offset?: number;
}) {
  const { text, mark } = wallSummaryMark(wall, gratings, pointOn, offset);

  return (
    <text
      className={styles.summary}
      data-role="wall-summary"
      data-wall={wall.index}
      x={mark.labelX}
      y={mark.labelY}
      textAnchor="middle"
      dominantBaseline="middle"
      transform={mark.rotation ? `rotate(${mark.rotation} ${mark.labelX} ${mark.labelY})` : undefined}
    >
      {text}
    </text>
  );
}

/**
 * Where a reading sits beside a stretch of wall, and which way up.
 *
 * `dimensionLine` decides the side by pushing away from a centre point — which
 * is the outline's centroid for a schema drawing, and the wrong answer in a
 * Rücksprung. It is handed the point the winding calls inside instead, so
 * „away from the centre" and „outwards" are the same statement here (§3.3).
 */
function readingMark(wall: WallView, from: ScreenPoint, to: ScreenPoint, offset: number) {
  const middle = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  const inside = shift(middle, wall.outward, -100);
  return dimensionLine(from, to, inside, offset);
}

/** One placement, drawn as what it is, plus the invisible band it is grabbed
 * and driven by. */
function PlacedThing({
  placement,
  wall,
  from,
  to,
  widthPx,
  typeWidthPx,
  typeName,
  selected,
  issue,
  cutStart,
  cutEnd,
  bevelStart = 0,
  bevelEnd = 0,
  growsStartPx,
  growsEndPx,
  blankMm,
  lengthLane,
  blankLane,
  wallMaxOffsetMm,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onKeyDown,
  onCornerMenu,
  zoomed,
  sheet = false,
}: {
  placement: Placement;
  wall: WallView;
  from: ScreenPoint;
  to: ScreenPoint;
  widthPx: number;
  /** The type's own width — ignoring any special width this element
   * carries — so the outer rail can hang off a figure that is the same for
   * every element of the type on this wall (design §3, 30.08.2026). */
  typeWidthPx: number;
  typeName: string;
  selected: boolean;
  issue: boolean;
  cutStart: CutWord;
  cutEnd: CutWord;
  /** At a `schraeg` end, how far the outer edge moves along the wall, as a
   * share of the drawn band width — signed, `tan(φ/2)` (`bevelAt`). Unread
   * at every other end. */
  bevelStart?: number;
  bevelEnd?: number;
  /** How far the element reaches past either end, in pixels. */
  growsStartPx: number;
  growsEndPx: number;
  /** What is cut, in mm. Equal to the wall stretch wherever nothing grew it. */
  blankMm: number;
  /** The lane its length stands on, 0 beside the band — none where it prints
   * none: on a sheet, on a summarised wall, a finding, a band too short
   * (`layOutReadings`). */
  lengthLane?: number;
  /** The lane its blank „→ 1.300 mm" stands on — none where it grew nothing. */
  blankLane?: number;
  wallMaxOffsetMm: number;
  onPointerDown: (event: ReactPointerEvent<SVGElement>) => void;
  onPointerMove: (event: ReactPointerEvent<SVGElement>) => void;
  onPointerUp: (event: ReactPointerEvent<SVGElement>) => void;
  onKeyDown: (event: KeyboardEvent<SVGElement>) => void;
  onCornerMenu: (event: ReactMouseEvent<SVGElement>) => void;
  /** Whether the board is magnified past its fitting scale — see
   * {@link bandHalfWidth}. */
  zoomed: boolean;
  /** Drawn on a sheet: the „!" and the „∗" travel with the element's number
   * there (`SheetLabels`), where a reader looks for them. */
  sheet?: boolean;
}) {
  // The pixel form of `bandGeometry`'s arithmetic — the two props arrive in
  // pixels, `bandGeometry` takes millimetres and a scale. The inner rail at
  // `(typeHalfWidth − halfWidth) * 2` and the drawn width `halfWidth * 2`
  // below are its `innerPx` and `drawnWidth`; `CutoutMark` reads those from
  // there, so a hole sits on the band this draws and not beside it.
  // With the type's own half width — ignoring any special width this element
  // carries — computed here rather than after the centre line below, because
  // the centre line needs it too: see the comment there. Widening outward the
  // two are one figure and the inner rail stays on the wall (`bandHalves`).
  const { halfWidth, typeHalfWidth, growthPx } = bandHalves(
    widthPx,
    typeWidthPx,
    zoomed,
    placement.widensOutward ?? false,
  );
  // The band lies *outside* the wall, not across it: a rost sits in front of
  // the facade. In the ordinary case (no special width, `typeHalfWidth ===
  // halfWidth`) its centre line is the wall pushed out by half a width, same
  // as always. In a width zone the band itself has moved — its inner edge
  // steps to `(typeHalfWidth − halfWidth) * 2` and its outer edge sits at
  // `typeHalfWidth * 2` (see the two below) — so the real centre is the
  // midpoint of those two, `2·typeHalfWidth − halfWidth`, not the old
  // `halfWidth`. Everything that keys off "the middle of the band" — the
  // selection halo, the issue cross (via `railPair`), the grab/keyboard
  // grip — has to move with it, or it keeps pointing at where the band used
  // to be while the band itself is now visibly somewhere else (review round
  // 1, 30.08.2026).
  const centreFrom = shift(from, wall.outward, 2 * typeHalfWidth - halfWidth);
  const centreTo = shift(to, wall.outward, 2 * typeHalfWidth - halfWidth);
  const [railA, railB] = railPair(centreFrom, centreTo, halfWidth);
  // Where the band's outer edge ends, once the mitres have had their say.
  // A mitre lies across the corner square, so only the outer edge moves —
  // the inner one still stops at the wall corner, which is why `length_mm`
  // is untouched by any of this.
  //
  // Shifted by the **drawn** band width and not by the real one: at working
  // scales `bandHalfWidth` clamps the band so a 300 mm rost stays legible,
  // and shifting by the unclamped figure sent the two outer edges past each
  // other by the difference — a mitre that visibly missed its own corner
  // (measured in the browser, 27.08.2026).
  // A mitre moves only the outer edge; a filling moves both, because there the
  // element covers the corner square whole rather than being cut across it.
  // The growth is measured off the drawn band width, never the real grating
  // width — `bandHalfWidth` clamps the band so a 300 mm rost stays legible at
  // any scale, and shifting by the unclamped figure sent the two edges of a
  // corner past each other (measured in the browser, 27.08.2026).
  // The reach stays on the element's OWN drawn width, not the type's — and
  // that is deliberately unaffected by the width-zone change (review round 1,
  // 30.08.2026, minor 6). The backend grows the blank by the element's own
  // width (settled and tested there); on screen, the diagonal a mitre draws
  // is the cap line from the inner corner to the outer corner below, and its
  // outward span between those two points is `typeHalfWidth*2 −
  // (typeHalfWidth − halfWidth)*2 = halfWidth*2` — the `typeHalfWidth` terms
  // cancel. A width zone therefore only *translates* the whole diagonal
  // outward with the band it belongs to; it does not change the diagonal's
  // own shape, so the along-wall reach that keeps it at 45° is still
  // `halfWidth*2`, exactly as before this feature existed. Pinned by "keeps
  // a mitre at 45° on a narrowed element" below.
  const drawnWidth = halfWidth * 2;
  const lengthPx = Math.hypot(to.x - from.x, to.y - from.y);
  const isBand = placement.kind === "grating";
  // Gated on the CUT, not the growth. At an inner mitre nothing grows — E-47
  // is correct that the corner square is claimed twice already — so gating on
  // `grows > 0` fell into the zero branch and drew a butt joint. Because the
  // two outward normals converge at a re-entrant corner, the two full
  // rectangles then covered that square twice: a visible overlap (Denis,
  // 30.08.2026, on the first real house). `reach()` already leans the right
  // way for `gehrung_innen` — it was simply never asked to move.
  // …and a filling reaches by the SIGN of its growth. A filled inner corner
  // hands the corner square to one of the two gratings, and the other one
  // stops short of it — its growth is negative, and a band drawn to the full
  // wall stretch there would show the double coverage the filling just
  // resolved (walk of 30.08.2026, finding 1). `reach()` takes the negative
  // figure and turns the end around by itself.
  //
  // **A shrink can only take what the band has.** Its reach is the *drawn*
  // width like every other one, so that the two gratings at a corner meet on
  // the drawn corner square rather than near it — but at facade scale that
  // width is clamped up to 14 px while a 350 mm element is 13 px long, and an
  // unclamped reach then pulled the near end past the far one: the element
  // drew as a bow tie, its own edges crossed (found in review, 30.08.2026).
  // Shared out where both ends give a square up, so the two together can
  // never eat more than the band.
  const givingUp = (growsStartPx < 0 ? 1 : 0) + (growsEndPx < 0 ? 1 : 0);
  const shrink = givingUp
    ? Math.min(
        drawnWidth,
        Math.max(lengthPx - BAND_MIN_LENGTH_PX, 0) / givingUp,
      )
    : 0;
  // **And an element with nothing left is not a band at all.** Where the
  // filling takes the whole wall stretch the backend floors the blank at zero
  // (`corner_fill_leaves_nothing`, design §12.1): no grating comes out of
  // this stretch, so none is drawn — the stretch stands, without bars and
  // without a reach, and the finding beside it says why.
  const noBlank = isBand && blankMm <= 0;
  const reachOf = (cut: CutWord, growsPx: number, bevel: number): number => {
    if (noBlank) return 0;
    // An oblique end at a chamfer: the outer edge to the bisector, out past
    // the corner where it stands out of the house and back where it steps
    // in — nothing grows, the two bands only meet (design 2026-10-18).
    if (cut === "schraeg") return drawnWidth * bevel;
    if (cut !== "gerade") return drawnWidth;
    if (growsPx > 0) return drawnWidth;
    return growsPx < 0 ? -shrink : 0;
  };
  const reachStart = reachOf(cutStart, growsStartPx, bevelStart);
  const reachEnd = reachOf(cutEnd, growsEndPx, bevelEnd);
  const edgeFrom = reach(from, to, cutStart, reachStart, true);
  const edgeTo = reach(from, to, cutEnd, reachEnd, false);
  // The inner edge moves along the wall only where nothing was cut — a
  // filling.
  const innerEdgeFrom = cutStart === "gerade" ? edgeFrom : from;
  const innerEdgeTo = cutEnd === "gerade" ? edgeTo : to;
  // The outer rail sits at the *type's* half width outward — one straight
  // line across every element of this wall's default type, whatever this one
  // element is built to. The inner rail follows this element's own width and
  // steps by the difference: nothing where nobody set a special width
  // (`typeHalfWidth === halfWidth`, the ordinary case), outward where an
  // object narrows this element, inward where one widens it. This is the
  // whole visual point of a width zone — the customer sees the outer line on
  // site, and it stays one line (design §3, 30.08.2026).
  const outerFrom = shift(edgeFrom, wall.outward, typeHalfWidth * 2);
  const outerTo = shift(edgeTo, wall.outward, typeHalfWidth * 2);
  const innerFrom = shift(innerEdgeFrom, wall.outward, (typeHalfWidth - halfWidth) * 2);
  const innerTo = shift(innerEdgeTo, wall.outward, (typeHalfWidth - halfWidth) * 2);
  // Off the band's outer edge, not off the wall: at working width the
  // fixed offset landed the figure inside the band (plan Task 5). A lane
  // further out per step (UX-Gutachten 2, B-2).
  // Moved out with a band widened outward, as `layOutReadings` lays it out.
  const markAt = (lane: number) =>
    readingMark(wall, from, to, READING_OFFSET + halfWidth + growthPx / 2 + lane * READING_LANE);
  const mark = markAt(0);
  const lengthMark = lengthLane !== undefined ? markAt(lengthLane) : null;
  const blankMark = blankLane !== undefined ? markAt(blankLane) : null;
  return (
    <g
      data-role="placement"
      data-placement={placement.id}
      data-kind={placement.kind}
      data-wall={placement.wallIndex}
      data-offset={placement.offsetMm}
      data-issue={issue}
      data-selected={selected}
      // „none" where the filling leaves nothing to build. Stated on the
      // element rather than left to be read off the missing bars, so the
      // state has a name in the DOM as well as a picture.
      data-blank={noBlank ? "none" : undefined}
      data-special-width={placement.widthMm !== null}
      // The drawn half-width, in pixels — `bandHalfWidth` clamped at the
      // fitted scale, true once zoomed in (D-7, design 2026-09-10).
      data-half-width={halfWidth}
      data-testid={`element-${placement.id}`}
      onContextMenu={(event) => {
        event.preventDefault();
        onCornerMenu(event);
      }}
    >
      {selected ? (
        <line
          className={styles.placementSelected}
          x1={centreFrom.x}
          y1={centreFrom.y}
          x2={centreTo.x}
          y2={centreTo.y}
          strokeWidth={halfWidth * 2 + 8}
        />
      ) : null}

      {isBand ? (
        <>
          {/* The two long edges, stated rather than taken off `railPair`:
              which of that pair is the outer one depends on the wall's own
              outward sign, and only the outer one moves with a mitre. */}
          {[
            { outer: false, a: innerFrom, b: innerTo },
            { outer: true, a: outerFrom, b: outerTo },
          ].map(({ outer, a, b }) => (
            <line
              key={`rail-${outer ? "outer" : "inner"}`}
              className={issue ? styles.railIssue : styles.rail}
              data-role="band-rail"
              data-outer={outer}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
            />
          ))}
          {/* The seams: one at each end, so two touching elements show their
              joint as the cross bar it is. */}
          {[
            [innerFrom, outerFrom],
            [innerTo, outerTo],
          ].map(([start, end], index) => (
            <line
              key={`cap-${index}`}
              className={issue ? styles.railIssue : styles.rail}
              data-role="band-cap"
              x1={start!.x}
              y1={start!.y}
              x2={end!.x}
              y2={end!.y}
            />
          ))}
          {/* Over the **grown** extent, not the wall stretch: the bars are what
              say this is grating. Drawn to the ends of the rails, a filled
              corner square reads as the piece it is; drawn only to the wall
              corner it read as an empty box, and nothing then told the two
              gratings at a corner apart (found in the browser, 27.08.2026). */}
          {(noBlank
            ? []
            : gratingHatch(
                shift(innerFrom, wall.outward, halfWidth),
                shift(innerTo, wall.outward, halfWidth),
                HATCH_SPACING_PX,
                halfWidth,
              )
          ).map((bar, index) => (
            <line
              key={`hatch-${index}`}
              className={styles.hatch}
              data-role="band-hatch"
              x1={bar.x1}
              y1={bar.y1}
              x2={bar.x2}
              y2={bar.y2}
            />
          ))}
          {/* Where the added corner piece begins. Only on a filling: a mitre
              carries its own form, the diagonal, and a second line across it
              would say there are two pieces where there is one. Dashed, and
              unlike the solid caps at either end, for the same reason.
              `grows > 0`, so the element that *gives* a corner square up at a
              filled inner corner gets none: nothing was added there, and its
              cap already stands where the piece now ends. */}
          {[
            { at: from, grows: growsStartPx, cut: cutStart },
            { at: to, grows: growsEndPx, cut: cutEnd },
          ]
            .filter(({ grows, cut }) => grows > 0 && cut === "gerade")
            .map(({ at }, index) => (
              <line
                key={`fill-seam-${index}`}
                className={styles.fillSeam}
                data-role="fill-seam"
                // Across the band this element actually occupies — the inner
                // rail's outward offset to the outer rail's, at the raw
                // corner's along-wall position — and not `0 … halfWidth*2`
                // off the wall. That range used to be the same thing when
                // every element sat flush with the wall; in a width zone it
                // starts inside the wall and stops short of the real outer
                // rail (review round 1, 30.08.2026).
                x1={shift(at, wall.outward, (typeHalfWidth - halfWidth) * 2).x}
                y1={shift(at, wall.outward, (typeHalfWidth - halfWidth) * 2).y}
                x2={shift(at, wall.outward, typeHalfWidth * 2).x}
                y2={shift(at, wall.outward, typeHalfWidth * 2).y}
              />
            ))}

          {issue ? (
            <g data-role="issue-cross">
              <line
                className={styles.issueCross}
                x1={railA.x1}
                y1={railA.y1}
                x2={railB.x2}
                y2={railB.y2}
              />
              <line
                className={styles.issueCross}
                x1={railB.x1}
                y1={railB.y1}
                x2={railA.x2}
                y2={railA.y2}
              />
            </g>
          ) : null}
        </>
      ) : null}

      {placement.kind === "gap" ? (
        <>
          {[from, to].map((point, index) => (
            <line
              key={`gap-end-${index}`}
              className={styles.gapEnd}
              data-role="gap-end"
              x1={shift(point, wall.outward, halfWidth).x}
              y1={shift(point, wall.outward, halfWidth).y}
              x2={shift(point, wall.outward, -halfWidth).x}
              y2={shift(point, wall.outward, -halfWidth).y}
            />
          ))}
          <line
            className={styles.gapLine}
            data-role="gap-line"
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
          />
        </>
      ) : null}

      {/* A width departing from the catalogue, marked in its own right.
          §3.2.1 promises „auf dem Board ist das Band schmaler — die Abweichung
          ist damit sichtbar" — and past about 22 m of drawn span every band is
          clamped to six pixels, so 233 mm and 300 mm are drawn identically and
          the promise is dead on exactly the case it was written for (A-2, the
          one reference solution in the stock). The tick does not depend on the
          scale (found in review, 16.08.2026). */}
      {isBand && placement.widthMm !== null && lengthPx >= 10 && !sheet ? (
        <text
          className={styles.specialWidth}
          data-role="special-width"
          x={(from.x + to.x) / 2}
          y={(from.y + to.y) / 2}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          ∗
        </text>
      ) : null}

      {placement.kind === "bracket" ? (
        <polygon
          className={styles.bracket}
          data-role="bracket-mark"
          points={diamond((from.x + to.x) / 2, (from.y + to.y) / 2, BRACKET_HALF)}
        />
      ) : null}

      {issue && !sheet ? (
        <text
          className={styles.issueMark}
          data-role="issue-mark"
          x={mark.labelX}
          y={mark.labelY}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          !
        </text>
      ) : null}

      {isBand && !issue && lengthMark !== null ? (
        <text
          className={styles.reading}
          data-role="element-length"
          data-lane={lengthLane}
          x={lengthMark.labelX}
          y={lengthMark.labelY}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={
            lengthMark.rotation
              ? `rotate(${lengthMark.rotation} ${lengthMark.labelX} ${lengthMark.labelY})`
              : undefined
          }
        >
          {formatMm(placement.lengthMm)}
        </text>
      ) : null}

      {/* The blank, where a treated corner grew or shortened the element:
          the difference between the two figures is the whole statement —
          one grating covers the square, the other stops at it; the same
          pair the parts list and the CSV carry. A figure of its own, on its
          own lane (UX-Gutachten 2, B-2): in the length's lane „1.000 →
          1.300 mm" was longer than its element and ran over the next. */}
      {isBand && !issue && blankMark !== null ? (
        <text
          className={styles.reading}
          data-role="element-blank"
          data-lane={blankLane}
          x={blankMark.labelX}
          y={blankMark.labelY}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={
            blankMark.rotation ? `rotate(${blankMark.rotation} ${blankMark.labelX} ${blankMark.labelY})` : undefined
          }
        >
          {blankReading(blankMm)}
        </text>
      ) : null}

      <line
        className={styles.placementGrip}
        data-role="placement-grip"
        data-placement={placement.id}
        x1={centreFrom.x}
        y1={centreFrom.y}
        x2={centreTo.x}
        y2={centreTo.y}
        strokeWidth={Math.max(halfWidth * 2, GRAB_HALF_WIDTH * 2)}
        role="slider"
        tabIndex={0}
        // „kein Rohling" belongs in the name, not only in the picture: a
        // sighted person gets the red frame, the ✕ and the finding, and
        // somebody on a screen reader got a length and nothing else (found in
        // review, 30.08.2026).
        aria-label={[
          placement.label,
          typeName,
          formatMm(placement.lengthMm),
          `Wand ${placement.wallIndex + 1}`,
          noBlank ? "kein Rohling — die Auffüllung nimmt die ganze Wandstrecke" : null,
        ]
          .filter(Boolean)
          .join(", ")}
        aria-orientation={
          Math.abs(wall.to.x - wall.from.x) >= Math.abs(wall.to.y - wall.from.y)
            ? "horizontal"
            : "vertical"
        }
        aria-valuemin={0}
        aria-valuemax={wallMaxOffsetMm}
        aria-valuenow={placement.offsetMm}
        aria-valuetext={`${formatMm(placement.offsetMm)} vom Wandanfang`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        // A capture the element loses ends its drag too (review of d8516611,
        // I-2): a release that never reaches the grip — a page gone inert
        // under a modal, the pointer let go elsewhere — must not leave the
        // element following the pointer with no button down.
        onLostPointerCapture={onPointerUp}
        onKeyDown={onKeyDown}
      />
    </g>
  );
}

/**
 * A hole, on the band of the element it is drilled into — or, where none
 * is, dashed on the band the wall would carry (Schlussreview I-4).
 *
 * Drawn at the **band's** scale on both axes — `drawnWidth / widthMm` — so
 * that a 120 mm square is square at every zoom and sits inside a band that
 * may be clamped at the fit scale (§3.1). A round hole is a circle, a square
 * one an axis-parallel polygon (no angle, §4). Nothing is clamped into the
 * band: a hole that reaches past a rail is drawn reaching past it, with
 * `data-fits="false"` saying so, and stays the marking colour — whether a
 * notch at the edge is permissible is Chiara's question (§9, §14.1 of the
 * objects design).
 *
 * **Hittable in the select tool**, unlike every other mark on a band: since
 * 12.09.2026 the hole is a thing a person drags, and the element under it is
 * grabbed beside it. With a tool up it is transparent like the rest, so a
 * second hole can be set next to the first.
 */
function CutoutMark({
  cutout,
  carrierId,
  alongMm,
  band,
  wall,
  seat,
  selected,
  linked,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onKeyDown,
  onContextMenu,
}: {
  cutout: Cutout;
  /** The element the hole is drilled into — named on the mark, so a test and
   * a person can read whose sheet this is. `null` where no sheet lies under
   * the middle: then the mark is dashed on the job's band and fits nothing. */
  carrierId: string | null;
  /** The hole's middle along the wall, resolved (a linked hole sits at its
   * object's middle, wherever its stored offset says). */
  alongMm: number;
  band: BandGeometry;
  wall: WallView;
  /** The hole's middle along the wall, on the wall line. */
  seat: ScreenPoint;
  selected: boolean;
  linked: boolean;
  onPointerDown: (event: ReactPointerEvent<SVGElement>) => void;
  onPointerMove: (event: ReactPointerEvent<SVGElement>) => void;
  onPointerUp: (event: ReactPointerEvent<SVGElement>) => void;
  onKeyDown: (event: KeyboardEvent<SVGElement>) => void;
  onContextMenu: (event: ReactMouseEvent<SVGElement>) => void;
}) {
  const pxPerMm = band.drawnWidth / band.widthMm;
  const extent = cutoutExtentMm(cutout);
  const halfAlong = Math.max((extent.alongMm * pxPerMm) / 2, CUTOUT_MIN_R);
  const halfAcross = Math.max((extent.acrossMm * pxPerMm) / 2, CUTOUT_MIN_R);
  const centre = shift(seat, wall.outward, band.innerPx + cutout.acrossMm * pxPerMm);
  // Without a sheet nothing fits, whatever the job's band would say: there
  // is no steel to drill.
  const unsupported = carrierId === null;
  const fits =
    !unsupported &&
    cutout.acrossMm - extent.acrossMm / 2 >= 0 &&
    cutout.acrossMm + extent.acrossMm / 2 <= band.widthMm;
  const name = FEATURE_DEFAULTS[cutout.obstacleType].name;
  const title = `Ausschnitt ${cutout.id}: ${
    cutout.shape === "rund"
      ? `rund Ø ${formatMm(extent.alongMm)}`
      : `eckig ${formatMm(extent.alongMm)} × ${formatMm(extent.acrossMm)}`
  }, Mitte ${formatMm(alongMm)} ab Wandanfang, ${formatMm(cutout.acrossMm)} ab Rostkante · ${name}${
    unsupported ? " · kein Rost darunter" : ""
  }`;
  const shared = {
    className: `${styles.cutout} ${selected ? styles.cutoutSelected : ""} ${
      unsupported ? styles.cutoutUnsupported : ""
    }`,
    "data-role": "cutout",
    "data-cutout": cutout.id,
    "data-carrier": carrierId ?? undefined,
    "data-shape": cutout.shape,
    "data-linked": linked,
    "data-fits": fits,
    "data-unsupported": unsupported,
    "data-selected": selected,
    tabIndex: 0,
    role: "button",
    "aria-label": title,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    onKeyDown,
    onContextMenu,
  } as const;
  if (cutout.shape === "rund") {
    return (
      <circle {...shared} cx={centre.x} cy={centre.y} r={halfAlong}>
        <title>{title}</title>
      </circle>
    );
  }
  const a = wall.along;
  const o = wall.outward;
  const corner = (sa: number, so: number) =>
    `${centre.x + a.x * sa * halfAlong + o.x * so * halfAcross},${centre.y + a.y * sa * halfAlong + o.y * so * halfAcross}`;
  return (
    <polygon {...shared} points={[corner(-1, -1), corner(1, -1), corner(1, 1), corner(-1, 1)].join(" ")}>
      <title>{title}</title>
    </polygon>
  );
}

/**
 * Where a door's leaf and swing go, in board pixels.
 *
 * The hinge is the end on the viewer's left **seen from inside, facing the
 * door** — DIN 107 reads the side from where the door opens to, and a house
 * door opens inwards; `"right"` takes the other end. The leaf stands at a
 * right angle into the house (against `outward`), exactly as long as the door
 * is wide, and the quarter circle runs from its tip back to the latch end.
 * `sweep` is the SVG arc flag that takes the short way round.
 */
function doorSwing(
  from: ScreenPoint,
  to: ScreenPoint,
  outward: Vector,
  hinge: "left" | "right",
): { hinge: ScreenPoint; latch: ScreenPoint; leafEnd: ScreenPoint; sweep: 0 | 1 } {
  // Facing `outward` on a y-down screen, the viewer's left is that direction
  // turned a quarter anticlockwise.
  const viewersLeft = { x: outward.y, y: -outward.x };
  const toIsLeft = (to.x - from.x) * viewersLeft.x + (to.y - from.y) * viewersLeft.y > 0;
  const leftEnd = toIsLeft ? to : from;
  const rightEnd = toIsLeft ? from : to;
  const pivot = hinge === "left" ? leftEnd : rightEnd;
  const latch = hinge === "left" ? rightEnd : leftEnd;
  const widthPx = Math.hypot(to.x - from.x, to.y - from.y);
  const leafEnd = shift(pivot, outward, -widthPx);
  const cross =
    (leafEnd.x - pivot.x) * (latch.y - pivot.y) - (leafEnd.y - pivot.y) * (latch.x - pivot.x);
  return { hinge: pivot, latch, leafEnd, sweep: cross > 0 ? 1 : 0 };
}

/**
 * One object standing on a wall, drawn — and, since 30.08.2026, reachable.
 *
 * A covered zone that narrows the band is drawn by {@link PlacedThing} off
 * the element's own `widthMm`; this draws the object itself: the bar on the
 * facade line, its name, the invisible band it is grabbed and focused by, and
 * the two end handles that change its length while the far end stands still.
 *
 * It was a `<g>` with two shapes in it and nothing else until the acceptance
 * walk of 30.08.2026 — no handler, no role, no tab stop and no way to remove
 * it (findings 1 and 3).
 */
function FacadeObject({
  feature,
  wall,
  from,
  to,
  selected,
  onSelect,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onKeyDown,
  onCornerMenu,
}: {
  feature: FacadeFeature;
  wall: WallView;
  from: ScreenPoint;
  to: ScreenPoint;
  selected: boolean;
  onSelect: () => void;
  /** Which end the gesture took hold of — `"whole"` carries the object. */
  onPointerDown: (
    event: ReactPointerEvent<SVGElement>,
    end: "whole" | "start" | "end",
  ) => void;
  onPointerMove: (event: ReactPointerEvent<SVGElement>) => void;
  onPointerUp: (event: ReactPointerEvent<SVGElement>) => void;
  onKeyDown: (event: KeyboardEvent<SVGElement>) => void;
  onCornerMenu: (event: ReactMouseEvent<SVGElement>) => void;
}) {
  const name = feature.label ?? FEATURE_DEFAULTS[feature.featureType].name;
  const lengthPx = Math.hypot(to.x - from.x, to.y - from.y);
  const swing = SWING_TYPES.has(feature.featureType)
    ? doorSwing(from, to, wall.outward, hingeOf(feature))
    : null;
  // Inwards off the wall, and turned with it — the name is a landmark beside
  // the drawing, not a caption over the hatching (design §6, „Beschriftung
  // wie eine Landmarke"). On a door, off the middle towards the latch, so it
  // stands inside the swing clear of the leaf.
  const anchor = swing
    ? {
        x: swing.hinge.x + (swing.latch.x - swing.hinge.x) * DOOR_LABEL_ALONG,
        y: swing.hinge.y + (swing.latch.y - swing.hinge.y) * DOOR_LABEL_ALONG,
      }
    : { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  const seat = shift(anchor, wall.outward, -FEATURE_LABEL_OFFSET);
  const hingeWords = swing ? `, Anschlag ${HINGE_WORDS[hingeOf(feature)]}` : "";
  const angle = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
  // Upright, whichever way the walk runs: a wall walked right-to-left would
  // otherwise print its objects upside down.
  const rotation = angle > 90 ? angle - 180 : angle < -90 ? angle + 180 : angle;
  const nameFits = lengthPx >= name.length * FEATURE_LABEL_PX_PER_CHAR + 4;

  return (
    <g
      data-role="feature"
      data-feature-type={feature.featureType}
      data-covered={feature.covered}
      data-wall={feature.wallIndex}
      data-selected={selected}
      data-testid={`feature-${feature.id}`}
      onContextMenu={(event) => {
        event.preventDefault();
        onCornerMenu(event);
      }}
    >
      {selected ? (
        <line
          className={styles.featureSelected}
          x1={from.x}
          y1={from.y}
          x2={to.x}
          y2={to.y}
        />
      ) : null}
      {/* The door symbol of a floor plan: the leaf standing open at a right
          angle and the quarter circle its edge sweeps, on the house side —
          the grating lies outside, in front of it. Drawn before the bar so
          the bar stays the heaviest stroke on the wall line. */}
      {swing && lengthPx >= DOOR_SWING_MIN_PX ? (
        <g
          data-role="door-swing"
          data-hinge={hingeOf(feature)}
          data-selected={selected}
          className={selected ? styles.doorSwingSelected : undefined}
        >
          <path
            className={styles.doorSwing}
            data-role="door-arc"
            d={`M ${swing.leafEnd.x} ${swing.leafEnd.y} A ${lengthPx} ${lengthPx} 0 0 ${
              swing.sweep
            } ${swing.latch.x} ${swing.latch.y}`}
          />
          <line
            className={styles.doorLeaf}
            data-role="door-leaf"
            x1={swing.hinge.x}
            y1={swing.hinge.y}
            x2={swing.leafEnd.x}
            y2={swing.leafEnd.y}
          />
        </g>
      ) : null}
      <line
        className={styles.featureBar}
        data-role="feature-bar"
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
      />
      {nameFits ? (
        <text
          className={styles.featureLabel}
          data-role="feature-label"
          x={seat.x}
          y={seat.y}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={rotation ? `rotate(${rotation} ${seat.x} ${seat.y})` : undefined}
        >
          {name}
        </text>
      ) : null}

      {/* The invisible band the object is grabbed, focused and driven by —
          `.placementGrip`'s twin, down to going transparent to the pointer
          while a tool is up so a second object can be drawn beside this one. */}
      <line
        className={styles.featureGrip}
        data-role="feature-grip"
        data-feature={feature.id}
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        strokeWidth={GRAB_HALF_WIDTH * 2}
        role="button"
        tabIndex={0}
        aria-label={`${name}, ${formatMm(feature.lengthMm)}, Wand ${
          feature.wallIndex + 1
        }, ab ${formatMm(feature.offsetMm)}${hingeWords}`}
        onFocus={onSelect}
        onPointerDown={(event) => onPointerDown(event, "whole")}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      >
        {/* Where the name does not fit beside the object, this is where it
            still is — for a pointer as well as for a reader. */}
        <title>{`${name}, ${formatMm(feature.lengthMm)}`}</title>
      </line>

      {/* One handle per end, and only on the object somebody picked: a pair of
          dots on every object would be a board full of dots. */}
      {selected
        ? (
            [
              { end: "start" as const, at: from, word: "Anfang" },
              { end: "end" as const, at: to, word: "Ende" },
            ] as const
          ).map(({ end, at, word }) => (
            <circle
              key={`feature-handle-${end}`}
              className={styles.featureHandle}
              data-role="feature-handle"
              data-feature={feature.id}
              data-end={end}
              cx={at.x}
              cy={at.y}
              r={FEATURE_HANDLE_R}
              role="button"
              tabIndex={-1}
              aria-label={`${word} von ${name} ziehen`}
              onPointerDown={(event) => onPointerDown(event, end)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
          ))
        : null}
    </g>
  );
}

/**
 * Whether a mark's article was set by hand and has since drifted from the
 * catalogue's own proposal — the one condition that decides both the contour
 * on the board (`ConsoleDot`) and the legend row that explains it
 * (`PlanningLegend`). Pulled out so the two cannot say something different,
 * the way `.console.consoleHandSet` vs. a bare `.consoleHandSet` already did
 * once on the CSS side (whole-branch review, 05.09.2026).
 */
function isConsoleOverridden(mark: ConsoleMark): boolean {
  return (
    mark.articleNo !== undefined &&
    mark.proposedArticleNo !== undefined &&
    mark.articleNo !== mark.proposedArticleNo
  );
}

/**
 * One console the answer worked out.
 *
 * A plain joint is a dot, the shared corner console a bigger one, and a joint
 * the run walks over an interruption carries a small bridge across it — one
 * run over a downpipe has to look different from two runs meeting, because it
 * counts differently (§3.7). A middle bracket is a bar, not a dot, because it
 * is the one mark that carries nothing but a single element, and that has to
 * read even with colour switched off (K60-CI Prüfliste 4). The bar and the
 * bridge are both oriented off the wall the mark sits on; the mark itself
 * brings only its point, and finding the nearest drawn wall is a question
 * about the drawing, not about the domain.
 */
function ConsoleDot({
  mark,
  point,
  walls,
  scale,
  index,
  onConsoleMenu,
}: {
  mark: ConsoleMark;
  point: ScreenPoint;
  walls: WallView[];
  /** Board pixels per millimetre, so the bridge can span a real width. */
  scale: number;
  /** Position in `consoles` — the only stable handle a test has to name one
   * particular mark by (`console-middle-${index}`, `console-joint-${index}`). */
  index: number;
  /** `undefined` where the screen offers no chooser — the mark then draws
   * exactly as it did before task 11: no tab stop, no listener. */
  onConsoleMenu?: (request: ConsoleMenuRequest) => void;
}) {
  const wall = nearestWall(point, walls);
  const along = wall?.along ?? { x: 1, y: 0 };
  const outward = wall?.outward ?? { x: 0, y: -1 };

  // What actually applies here differs from what is proposed now only where
  // an override is in effect — the backend never lets the two disagree
  // otherwise (`ConsoleMarkOut.article_no`'s own comment). One condition
  // therefore carries both the contour and the drift note: a hand-set
  // article that still matches today's proposal has nothing left to say.
  const overridden = isConsoleOverridden(mark);
  const driftTitle = overridden
    ? `Von Hand auf ${mark.articleNo ?? "keinen Artikel"} gesetzt — passt nicht mehr zum Vorschlag (${
        mark.proposedArticleNo ?? "kein Vorschlag mehr"
      }).`
    : null;

  /**
   * The two triggers a mark answers, mirroring the wall's own
   * `wallContextMenu`/`wallKeyDown`: a right-click anywhere on the dot, or
   * Shift+F10 once it holds focus — jsdom does not turn the key into a
   * `contextmenu` event the way a real browser does, so the key is answered
   * directly, off the dot's own box.
   */
  function contextMenu(event: ReactMouseEvent<SVGElement>): void {
    if (!onConsoleMenu) return;
    event.preventDefault();
    onConsoleMenu({ key: mark.key ?? null, index, x: event.clientX, y: event.clientY });
  }
  function keyDown(event: KeyboardEvent<SVGElement>): void {
    if (event.key !== "F10" || !event.shiftKey) return;
    if (!onConsoleMenu) return;
    event.preventDefault();
    const box = event.currentTarget.getBoundingClientRect();
    onConsoleMenu({
      key: mark.key ?? null,
      index,
      x: box.left + box.width / 2,
      y: box.top + box.height / 2,
    });
  }

  if (mark.kind === "middle") {
    const angleDeg = (Math.atan2(along.y, along.x) * 180) / Math.PI;
    return (
      <g data-role="console" data-console-kind="middle">
        <rect
          className={`${styles.consoleMiddle} ${overridden ? styles.consoleHandSet : ""}`}
          x={point.x - MIDDLE_MARK_HALF_LENGTH}
          y={point.y - MIDDLE_MARK_HALF_WIDTH}
          width={MIDDLE_MARK_HALF_LENGTH * 2}
          height={MIDDLE_MARK_HALF_WIDTH * 2}
          transform={`rotate(${angleDeg} ${point.x} ${point.y})`}
        />
        {/* The hit target, padded past the bar itself so a small miss still
            lands — see `.consoleGrip`. Its own rect, not the bar's: the
            shape-comparison this board's own tests hold („a middle bracket
            draws differently from a joint") reads the *bar*, and a grip that
            replaced it with a circle would make every mark look like a joint
            to that test. */}
        <rect
          data-testid={`console-middle-${index}`}
          className={`${styles.consoleGrip} ${onConsoleMenu ? styles.consoleGripActive : ""}`}
          x={point.x - MIDDLE_MARK_HALF_LENGTH - CONSOLE_GRIP_PAD}
          y={point.y - MIDDLE_MARK_HALF_WIDTH - CONSOLE_GRIP_PAD}
          width={(MIDDLE_MARK_HALF_LENGTH + CONSOLE_GRIP_PAD) * 2}
          height={(MIDDLE_MARK_HALF_WIDTH + CONSOLE_GRIP_PAD) * 2}
          transform={`rotate(${angleDeg} ${point.x} ${point.y})`}
          tabIndex={onConsoleMenu ? 0 : -1}
          role={onConsoleMenu ? "button" : undefined}
          aria-label={onConsoleMenu ? "Mittelkonsole, Artikel wählen" : undefined}
          onContextMenu={contextMenu}
          onKeyDown={keyDown}
        >
          {driftTitle ? <title>{driftTitle}</title> : null}
        </rect>
      </g>
    );
  }

  // The arc spans the opening it walks over, with a floor so a hairline gap
  // still draws something a person can see.
  const half = Math.max((mark.spanMm ?? 0) * scale, 5) / 2;
  const start = shift(point, along, -half);
  const end = shift(point, along, half);
  const control = shift(point, outward, Math.max(half, 8) + 4);
  const radius = mark.kind === "corner" ? CORNER_CONSOLE_RADIUS : CONSOLE_RADIUS;

  return (
    <g data-role="console" data-console-kind={mark.kind}>
      {mark.kind === "bridged" ? (
        <path
          className={styles.consoleBridge}
          data-role="console-bridge"
          d={`M${start.x},${start.y} Q${control.x},${control.y} ${end.x},${end.y}`}
        />
      ) : null}
      <circle
        className={`${styles.console} ${overridden ? styles.consoleHandSet : ""}`}
        cx={point.x}
        cy={point.y}
        r={radius}
      />
      {/* The hit target — see `.consoleGrip`'s own comment. Padded past the
          dot so a small miss still lands, the same margin `wallGrip` and
          `placementGrip` give a drag. */}
      <circle
        data-testid={`console-joint-${index}`}
        className={`${styles.consoleGrip} ${onConsoleMenu ? styles.consoleGripActive : ""}`}
        cx={point.x}
        cy={point.y}
        r={radius + CONSOLE_GRIP_PAD}
        tabIndex={onConsoleMenu ? 0 : -1}
        role={onConsoleMenu ? "button" : undefined}
        aria-label={onConsoleMenu ? "Konsole, Artikel wählen" : undefined}
        onContextMenu={contextMenu}
        onKeyDown={keyDown}
      >
        {driftTitle ? <title>{driftTitle}</title> : null}
      </circle>
    </g>
  );
}

/** The drawn wall a point lies closest to — presentation, not geometry with a
 * verdict in it: it decides which way up a mark is drawn, nothing else. */
/** The four points of a diamond around a centre — see {@link BRACKET_HALF}. */
function diamond(x: number, y: number, half: number): string {
  return `${x},${y - half} ${x + half},${y} ${x},${y + half} ${x - half},${y}`;
}

function nearestWall(point: ScreenPoint, walls: WallView[]): WallView | null {
  let best: WallView | null = null;
  let bestDistance = Infinity;
  for (const wall of walls) {
    const dx = wall.to.x - wall.from.x;
    const dy = wall.to.y - wall.from.y;
    const squared = dx * dx + dy * dy || 1;
    const t = Math.min(
      Math.max(((point.x - wall.from.x) * dx + (point.y - wall.from.y) * dy) / squared, 0),
      1,
    );
    const distance = Math.hypot(
      point.x - (wall.from.x + dx * t),
      point.y - (wall.from.y + dy * t),
    );
    if (distance < bestDistance) {
      bestDistance = distance;
      best = wall;
    }
  }
  return best;
}

/**
 * The element that has not been placed yet, standing where it will land.
 *
 * Dashed outline, no hatching and no fill: it is not a product yet. Drawn at
 * the snapped offset in its end size, so nothing jumps on release — a preview
 * that promises something is worse than no preview (§5.4).
 *
 * The reading beside it carries the one rule that costs material: what the
 * wall has left after this element, and whether that remainder is under the
 * smallest element the catalogue knows. The board computes `free − length` and
 * compares against a delivered figure; the verdict itself still comes back
 * from the answer.
 */
function GhostBand({
  ghost,
  wall,
  pointOn,
  placements,
  widthPx,
  minLengthMm,
  zoomed,
}: {
  ghost: Ghost;
  wall: WallView | null;
  pointOn: (wall: WallView, mm: number) => ScreenPoint;
  placements: Placement[];
  widthPx: number;
  minLengthMm: number | null;
  /** Whether the board is magnified past its fitting scale — see
   * {@link bandHalfWidth}. */
  zoomed: boolean;
}) {
  if (!wall || wall.lengthMm === null) return null;

  const from = pointOn(wall, ghost.offsetMm);
  const to = pointOn(wall, ghost.offsetMm + ghost.lengthMm);
  const halfWidth = bandHalfWidth(widthPx, zoomed);
  const outerFrom = shift(from, wall.outward, halfWidth * 2);
  const outerTo = shift(to, wall.outward, halfWidth * 2);

  const stretch = wall.free.find(
    (each) =>
      each.offsetMm <= ghost.offsetMm &&
      ghost.offsetMm + ghost.lengthMm <= each.offsetMm + each.lengthMm,
  );
  const remainders = stretch
    ? [
        ghost.offsetMm - stretch.offsetMm,
        stretch.offsetMm + stretch.lengthMm - (ghost.offsetMm + ghost.lengthMm),
      ].filter((each) => each > 0)
    : [];
  // The smaller of the two pieces it leaves behind — that is the one that can
  // fall under the minimum first. Nothing left over is not a conflict: zero is
  // „the stretch is used up", which is what was wanted.
  const restMm = remainders.length > 0 ? Math.min(...remainders) : 0;
  const restConflict = minLengthMm !== null && restMm > 0 && restMm < minLengthMm;

  // The same set `snapOffset` snaps to, and only that set: it collects the
  // edges of the *occupying* kinds (§3.5), while this list used to take every
  // placement. An extra console then drew a snap mark at a place nothing had
  // ever snapped to — two answers to one question, and the visible one was
  // the wrong one (found in review, 16.08.2026).
  const edges = new Set<number>([0, wall.lengthMm]);
  for (const placement of placements) {
    if (placement.wallIndex !== wall.index) continue;
    if (placement.kind !== "grating" && placement.kind !== "gap") continue;
    edges.add(placement.offsetMm);
    edges.add(placement.offsetMm + placement.lengthMm);
  }
  // A mark where the ghost actually meets something. None for the grid — a
  // mark that is always there marks nothing (§5.4).
  const marks = [ghost.offsetMm, ghost.offsetMm + ghost.lengthMm].filter((edge) =>
    edges.has(edge),
  );
  const mark = readingMark(wall, from, to, READING_OFFSET + halfWidth);

  return (
    <g
      data-role="ghost"
      data-wall={ghost.wallIndex}
      data-offset={ghost.offsetMm}
      data-length={ghost.lengthMm}
      data-snapped-to={ghost.snappedTo}
      data-rest-conflict={restConflict}
    >
      <polygon
        className={styles.ghost}
        points={`${from.x},${from.y} ${to.x},${to.y} ${outerTo.x},${outerTo.y} ${outerFrom.x},${outerFrom.y}`}
      />
      {marks.map((edge, index) => {
        const at = pointOn(wall, edge);
        return (
          <line
            key={`snap-${index}`}
            className={styles.snapMark}
            data-role="snap-mark"
            x1={shift(at, wall.outward, halfWidth * 2 + 4).x}
            y1={shift(at, wall.outward, halfWidth * 2 + 4).y}
            x2={shift(at, wall.outward, -4).x}
            y2={shift(at, wall.outward, -4).y}
          />
        );
      })}
      <text
        className={restConflict ? styles.readingConflict : styles.reading}
        data-role="ghost-readout"
        x={mark.labelX}
        y={mark.labelY}
        textAnchor="middle"
        dominantBaseline="middle"
        transform={
          mark.rotation ? `rotate(${mark.rotation} ${mark.labelX} ${mark.labelY})` : undefined
        }
      >
        {minLengthMm === null
          ? formatMm(ghost.lengthMm)
          : `${formatMm(ghost.lengthMm)} · Rest ${formatMm(restMm)}${
              restConflict ? ` — unter ${formatMm(minLengthMm)}` : ""
            }`}
      </text>
    </g>
  );
}

/**
 * Where the current view sits on the whole house — drawn once the board is
 * zoomed in, tucked into the one corner neither the zoom buttons (top right)
 * nor the scale bar (bottom left) already occupy (task 9, design §7.3).
 *
 * The very same `projectRun` as the main board, fitted into a small box
 * instead of the whole board — one projection function at two sizes, never a
 * second drawing that could disagree with the first. Decoration only:
 * `aria-hidden`, no pointer events, no state of its own.
 */
function OverviewFrame({
  fitNodes,
  segments,
  boardWidth,
  boardHeight,
  scale,
  centreMm,
}: {
  fitNodes: MmPoint[];
  segments: DemoOutline["segments"];
  boardWidth: number;
  boardHeight: number;
  /** The scale the main board is actually drawn at (`projection.scale`) —
   * always the zoomed-in one, this component is only ever mounted past the
   * fit (see the caller). */
  scale: number;
  centreMm: { xMm: number; yMm: number };
}) {
  // Sanitised the same way `patternPrefix` is, two screens up: `useId`'s own
  // colons are not legal inside a bare `id` or a `url(#…)` reference, and two
  // boards on one screen must not land on the same clip id.
  const clipId = `${useId().replace(/[^a-zA-Z0-9_-]/g, "")}-overview-clip`;

  const mini = projectRun(fitNodes, {
    width: OVERVIEW_WIDTH_PX,
    height: OVERVIEW_HEIGHT_PX,
    padding: OVERVIEW_PADDING_PX,
  });

  // The same trick `pointOn` uses on the main drawing: one known node and the
  // point `mini` placed it at, plus a delta in millimetres — `projectRun`
  // reports the points it placed, not the offset it placed them with, so a
  // point it was never asked for is found through one it was.
  const anchor = fitNodes[0];
  const anchorPoint = anchor ? mini.points[anchor.id] : undefined;
  if (!anchor || !anchorPoint) return null;

  // How much smaller the fitted overview is than the board's current, zoomed
  // drawing — the ratio that turns the board's own pixel size into the
  // window's.
  //
  // **Not clamped to the frame.** `windowWidthPx`/`windowHeightPx` say how
  // much of the house is actually visible, honestly, however large that
  // gets — clamping the number would have the window claim "this much is
  // visible" while more of it is. Checked at `scale = fit`: `ratio` peaks
  // there (it only shrinks as `scale` grows past the fit), so the window is
  // already bigger than the 132×74 frame at the very first zoom step, not
  // only deep in — `<clipPath>` below truncates what is *drawn*, never these
  // two numbers (found in review, 10.09.2026).
  const ratio = mini.scale / scale;
  const windowWidthPx = boardWidth * ratio;
  const windowHeightPx = boardHeight * ratio;
  const windowCentreX = anchorPoint.x + (centreMm.xMm - anchor.xMm) * mini.scale;
  const windowCentreY = anchorPoint.y + (centreMm.yMm - anchor.yMm) * mini.scale;

  return (
    <g
      data-role="overview"
      transform={`translate(${boardWidth - OVERVIEW_WIDTH_PX - OVERVIEW_MARGIN_PX}, ${
        boardHeight - OVERVIEW_HEIGHT_PX - OVERVIEW_MARGIN_PX
      })`}
      aria-hidden="true"
      clipPath={`url(#${clipId})`}
    >
      <defs>
        <clipPath id={clipId}>
          <rect width={OVERVIEW_WIDTH_PX} height={OVERVIEW_HEIGHT_PX} />
        </clipPath>
      </defs>
      <rect width={OVERVIEW_WIDTH_PX} height={OVERVIEW_HEIGHT_PX} className={styles.overviewGround} />
      {segments.map((_segment, index) => {
        const from = mini.points[`n${index}`];
        const to = mini.points[`n${index + 1}`];
        if (!from || !to) return null;
        return (
          <line
            key={`overview-wall-${index}`}
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
            className={styles.overviewWall}
          />
        );
      })}
      <rect
        data-role="overview-window"
        x={windowCentreX - windowWidthPx / 2}
        y={windowCentreY - windowHeightPx / 2}
        width={windowWidthPx}
        height={windowHeightPx}
        className={styles.overviewWindow}
      />
    </g>
  );
}
